import React, { useState, useEffect, useCallback, useRef, Component } from 'react';

class CMOErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, background: '#1a0000', border: '1px solid #ef4444', borderRadius: 12, color: '#ef4444' }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>CMO Render Error:</div>
          <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', color: '#ff8888' }}>{this.state.error.message}{'\n'}{this.state.error.stack?.split('\n').slice(0, 5).join('\n')}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

const LOCAL_API = 'http://localhost:3001';
const PROD_API = 'https://neurorank-production.up.railway.app';

const SECTIONS = ['Overview', 'Users', 'Marketing', 'Checklist', 'Growth', 'CMO', 'Agents'];
const SECONDARY_VIEWS = ['Overview', 'Events', 'Users', 'Marketing', 'Checklist', 'Growth', 'CMO', 'Orgs', 'Agents', 'Settings'];

// --- CSV export helper ---
function exportCsv(filename, rows, columns) {
  if (!rows || !rows.length) return;
  const keys = columns || Object.keys(rows[0]);
  const esc = (v) => {
    if (v == null) return '';
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [keys.join(','), ...rows.map(r => keys.map(k => esc(r[k])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
}

// --- Week-over-week delta badge ---
function WoWDelta({ curr, prev, inline }) {
  if (curr == null || prev == null) return null;
  const pct = prev === 0 ? (curr > 0 ? 100 : 0) : Math.round(((curr - prev) / prev) * 100);
  const up = pct > 0;
  const flat = pct === 0;
  const color = flat ? '#555' : up ? '#10b981' : '#ef4444';
  const arrow = flat ? '→' : up ? '↑' : '↓';
  return (
    <span title={`Previous 7d: ${prev}`} style={{ fontSize: inline ? 10 : 9, color, fontWeight: 700, marginLeft: 6 }}>
      {arrow} {Math.abs(pct)}%
    </span>
  );
}

// --- CSV export button ---
function CsvButton({ rows, columns, filename }) {
  if (!rows?.length) return null;
  return (
    <button
      onClick={() => exportCsv(filename, rows, columns)}
      style={{ background: 'transparent', border: '1px solid #333', color: '#888', borderRadius: 6, padding: '4px 10px', fontSize: 10, cursor: 'pointer' }}
      title="Export to CSV"
    >
      📥 CSV
    </button>
  );
}
const COLORS = {
  bg: '#0a0a0a', card: '#141414', cardHover: '#1a1a1a', border: '#1f1f1f',
  borderLight: '#2a2a2a', accent: '#10b981', accentDim: '#10b98133',
  cyan: '#00d4ff', purple: '#a855f7', amber: '#f59e0b', red: '#ef4444',
  text: '#ffffff', muted: '#888888', dim: '#555555', faint: '#333333',
};
const CMO_SUBS = [
  // Our own subreddit — always monitor
  { sub: 'neurorank',            weight: 2.0 },
  // Improvement / aim training — safest for organic comments
  { sub: 'FPSAimTrainer',        weight: 1.5 },
  { sub: 'aimtraining',          weight: 1.4 },
  { sub: 'GetGoodAtGames',       weight: 1.4 },
  { sub: 'CompetitiveGaming',    weight: 1.4 },
  // Academy / learning subs — safe, exist to share resources
  { sub: 'AgentAcademy',         weight: 1.4 },
  { sub: 'summonerschool',       weight: 1.4 },
  { sub: 'CsgoAcademy',          weight: 1.3 },
  { sub: 'OverwatchUniversity',  weight: 1.3 },
  { sub: 'LearnDota2',           weight: 1.3 },
  { sub: 'RocketLeagueSchool',   weight: 1.3 },
  { sub: 'CompetitiveApex',      weight: 1.2 },
  // Reaction time / fighting games
  { sub: 'StreetFighter',        weight: 1.3 },
  { sub: 'Tekken',               weight: 1.3 },
  { sub: 'SmashBrosCompetitive', weight: 1.3 },
  { sub: 'SSBM',                 weight: 1.2 },
  // Competitive game subs — fine with genuine value comments
  { sub: 'VALORANT',             weight: 1.2 },
  { sub: 'ValorantCompetitive',  weight: 1.2 },
  { sub: 'apexlegends',          weight: 1.1 },
  { sub: 'leagueoflegends',      weight: 1.0 },
  { sub: 'DotA2',                weight: 1.0 },
  { sub: 'overwatch2',           weight: 1.0 },
  { sub: 'Competitiveoverwatch', weight: 1.1 },
  { sub: 'RocketLeague',         weight: 1.0 },
  { sub: 'Rainbow6',             weight: 1.0 },
  { sub: 'R6SiegeCompetitive',   weight: 1.1 },
  { sub: 'FortniteCompetitive',  weight: 1.0 },
  { sub: 'CS2',                  weight: 1.1 },
  { sub: 'GlobalOffensive',      weight: 1.0 },
  { sub: 'truegaming',           weight: 0.8 },
  { sub: 'pcgaming',             weight: 0.7 },
  { sub: 'gaming',               weight: 0.5 },
  // Org / coach / scout audience
  { sub: 'esports',              weight: 1.1 },
  { sub: 'CollegeEsports',       weight: 1.2 },
  { sub: 'esportsjobs',          weight: 1.1 },
  { sub: 'ProGaming',            weight: 1.0 },
  { sub: 'esportscoaching',      weight: 1.3 },
];
const TOP_SUBS_COUNT = CMO_SUBS.length;

const HIGH_SIGNAL = [
  'hardstuck','hard stuck','cant climb',"can't climb",'cant rank up',"can't rank",
  'rank up','ranking up','how to improve','not improving','stopped improving',
  'plateau','peaked','ceiling','stuck in','elo hell','skill gap',
  'why am i','why cant i',"why can't i",'why do i',
  'deranked','deranking','dropped rank','losing streak',
  'reaction time','reaction speed','slow reaction','late reaction','reflex','reflexes',
  'cognitive','mental game','mental block','mental performance',
  'decision making','game sense','game iq','spatial awareness','awareness',
  'brain','focus','concentration','tunnel vision','overthinking',
  'choking','choke','panic','clutch','ranked anxiety',
  'tilting','tilt','on tilt','burnout','flow state',
  'composure','inconsistent','inconsistency','consistency','mental health gaming',
  'stress','pressure','nervous','anxiety',
  'aim training','aim inconsistency','flicking','flick shot','tracking aim',
  'micro adjustments','crosshair placement','hand eye coordination','hand-eye',
  'muscle memory','click timing','spray control','mechanics vs game sense',
  'aim vs game sense','mechanical skill',
  'performance','improve','improvement','getting better','warm up','warmup',
  'practice routine','training routine','aim routine','kovaaks','kovaak',
  'aim lab','aimlab','how to get better','tips to improve',
  'tryout','tryouts','roster','coaching','coach','player evaluation',
  'player development','player assessment','scouting','scout',
  'recruit','recruiting','recruitment','talent','team performance',
  'team building','in-game leader','igl','bootcamp','boot camp',
  'combine','cognitive assessment','performance analysis',
];

function scorePost(post) {
  const text = `${post.title} ${post.selftext || ''}`.toLowerCase();
  const age  = (Date.now() / 1000 - post.created_utc) / 3600;
  if (age > 72) return 0; // ignore posts older than 3 days
  const recency     = age < 2 ? 1.0 : age < 8 ? 0.9 : age < 24 ? 0.7 : age < 48 ? 0.4 : 0.2;
  const engagement  = Math.log10(Math.max(1, post.score + post.num_comments * 3)) / 4;
  const highMatches = HIGH_SIGNAL.filter(k => text.includes(k)).length;
  const relevance   = Math.min(1, highMatches * 0.25);
  const isQuestion  = post.title.includes('?') || /^(how|why|what|is|does|can|should)/i.test(post.title);
  const textBonus   = post.is_self ? 0.1 : 0; // text posts are slightly better but link posts still valid
  return recency * 0.35 + engagement * 0.2 + relevance * 0.35 + (isQuestion ? 0.05 : 0) + textBonus;
}

function StatCard({ label, value, sub, color = '#00d4ff' }) {
  return (
    <div style={{
      background: '#0d1117', border: `1px solid ${color}22`,
      borderRadius: 10, padding: '18px 22px', minWidth: 140,
    }}>
      <div style={{ fontSize: 26, fontWeight: 700, color, fontFamily: 'monospace' }}>{value ?? '—'}</div>
      <div style={{ fontSize: 13, color: '#aaa', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#555', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '0.15em',
        color: '#555', textTransform: 'uppercase', marginBottom: 14,
        borderBottom: '1px solid #1a1a1a', paddingBottom: 8,
      }}>{title}</div>
      {children}
    </div>
  );
}

function ProgressRing({ value, max = 100, size = 80, color = COLORS.accent, label, sublabel }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(value / max, 1);
  const offset = circ * (1 - pct);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={COLORS.border} strokeWidth={4} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={4}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      </svg>
      <div style={{ marginTop: -size/2 - 10, textAlign: 'center', position: 'relative', zIndex: 1, height: size/2 + 10, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{ fontSize: size > 60 ? 18 : 14, fontWeight: 800, color }}>{value ?? '—'}</div>
      </div>
      {label && <div style={{ fontSize: 10, color: COLORS.muted, textAlign: 'center', marginTop: 2 }}>{label}</div>}
      {sublabel && <div style={{ fontSize: 9, color: COLORS.dim }}>{sublabel}</div>}
    </div>
  );
}

function PanelCard({ title, children, style: extraStyle, action }) {
  return (
    <div style={{
      background: COLORS.card, border: `1px solid ${COLORS.border}`,
      borderRadius: 12, padding: '14px 16px', marginBottom: 10, ...extraStyle,
    }}>
      {title && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.dim, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{title}</div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <button onClick={copy} style={{
      background: copied ? '#10b98122' : '#ffffff0d',
      border: `1px solid ${copied ? '#10b981' : '#333'}`,
      color: copied ? '#10b981' : '#aaa',
      borderRadius: 6, padding: '4px 10px', fontSize: 11,
      cursor: 'pointer', transition: 'all 0.2s',
    }}>
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

const PLATFORM_META = [
  { key: 'TWITTER',   label: 'Twitter / X', color: '#1d9bf0' },
  { key: 'INSTAGRAM', label: 'Instagram',   color: '#e1306c' },
  { key: 'TIKTOK',    label: 'TikTok',      color: '#69c9d0' },
];

function extractLegacyPlatformCaptions(text) {
  const clean = text.replace(/\*\*/g, '');
  return PLATFORM_META.map(({ key, label, color }) => {
    const re = new RegExp(`${key}:\\s*([\\s\\S]+?)(?=\\n(?:TWITTER|INSTAGRAM|TIKTOK):|$)`, 'i');
    const caption = (clean.match(re)?.[1] || '')
      .replace(/^---\s*/gm, '')
      .replace(/^\([^)]+\)\n?/, '')
      .trim();
    return { key, label, color, caption };
  }).filter(p => p.caption);
}

function CaptionsBlock({ content }) {
  const [, captionSection] = (content || '').split(/\n---+\s*\n/);
  if (!captionSection) return null;

  // New format: unified CAPTION: + HASHTAGS: per platform
  const unified = (captionSection.match(/CAPTION:\s*([\s\S]+?)(?=\nHASHTAGS:|\nTWITTER:)/i)?.[1] || '').replace(/\*\*/g, '').trim();
  const hashSection = (captionSection.match(/HASHTAGS:\s*([\s\S]+)$/i)?.[1] || '');
  const platformTags = hashSection ? PLATFORM_META.map(({ key, label, color }) => {
    const re = new RegExp(`${key}:\\s*(.+?)(?=\\n(?:TWITTER|INSTAGRAM|TIKTOK):|$)`, 'is');
    const tags = (hashSection.match(re)?.[1] || '').trim();
    return { key, label, color, tags };
  }).filter(p => p.tags) : [];

  if (unified) {
    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>Caption</div>
          <div style={{ background: '#060a0f', borderRadius: 8, padding: '10px 14px', borderLeft: '2px solid #00d4ff33', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <p style={{ flex: 1, margin: 0, fontSize: 12, color: '#ccc', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{unified}</p>
            <CopyButton text={unified} />
          </div>
        </div>
        {platformTags.length > 0 && (
          <div>
            <div style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>Hashtags</div>
            {platformTags.map(({ key, label, color, tags }) => (
              <div key={key} style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 10, color: '#444', marginBottom: 4 }}>{label}</div>
                <div style={{ background: '#060a0f', border: '1px solid #1a1a1a', borderRadius: 8, padding: '6px 12px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '4px 8px' }}>
                    {tags.split(/\s+/).filter(Boolean).map((tag, t) => <span key={t} style={{ fontSize: 11, color }}>{tag}</span>)}
                  </div>
                  <CopyButton text={tags} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Fallback: old per-platform full captions
  const legacyCaptions = extractLegacyPlatformCaptions(captionSection);
  if (!legacyCaptions.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8 }}>
      {legacyCaptions.map(({ key, label, color, caption }) => (
        <div key={key} style={{ background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 8, padding: '8px 12px' }}>
          <div style={{ fontSize: 10, color, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <pre style={{ flex: 1, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, color: '#ccc', lineHeight: 1.5, fontFamily: 'inherit' }}>{caption}</pre>
            <CopyButton text={caption} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Twitter thread: parse N/ numbered tweets, strip header metadata
function ThreadBlock({ content }) {
  if (!content) return null;
  const title = content.match(/^# Twitter Thread — (.+)/m)?.[1] || 'Twitter Thread';
  const [tweetSection] = content.split(/\n---+\s*\n/);
  const parts = tweetSection.split(/\n(?=\d+\/ )/);
  const tweets = parts
    .map(p => p.trim())
    .filter(p => /^\d+\//.test(p))
    .map(p => p.replace(/^\d+\/\s*/, '').trim());
  if (!tweets.length) return null;
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 12, color: '#00d4ff', fontWeight: 600, marginBottom: 3 }}>Twitter Thread</div>
      <div style={{ fontSize: 11, color: '#555', marginBottom: 10 }}>{title} · {tweets.length} tweets</div>
      <CaptionsBlock content={content} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8 }}>
        {tweets.map((tweet, i) => (
          <div key={i} style={{ background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span style={{ fontSize: 11, color: '#333', fontWeight: 700, flexShrink: 0, paddingTop: 1, minWidth: 20 }}>{i + 1}/</span>
            <pre style={{ flex: 1, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, color: '#ccc', lineHeight: 1.6, fontFamily: 'inherit' }}>{tweet}</pre>
            <CopyButton text={tweet} />
          </div>
        ))}
      </div>
    </div>
  );
}

// Instagram carousel: just show caption + hashtags (slides are the PNG images)
function CarouselBlock({ content }) {
  if (!content) return null;
  const title = content.match(/^# Instagram Carousel — (.+)/m)?.[1] || 'Instagram Carousel';
  const slideCount = (content.match(/SLIDE \d+:/g) || []).length;
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 12, color: '#00d4ff', fontWeight: 600, marginBottom: 3 }}>Instagram Carousel</div>
      <div style={{ fontSize: 11, color: '#555', marginBottom: 10 }}>{title} · {slideCount} slides</div>
      <CaptionsBlock content={content} />
    </div>
  );
}

// Image card: show key stat/info + captions. Delegates to CaptionsBlock for new/legacy format handling.
function ImageCardBlock({ title, content }) {
  if (!content) return null;
  const [main] = content.split(/\n---+\s*\n/);

  // Extract the main "what is this card" line — strip header comments and label prefixes
  const keyLine = (main || '').split('\n')
    .filter(l => l.trim() && !l.startsWith('#'))
    .map(l => l.replace(/^(?:STAT|CONTEXT|Caption|Player \d|Rank):\s*/i, '').trim())
    .find(l => l.length > 4) || '';

  // Single caption fallback (old format without --- separator: "Caption:\ntext" or "CAMPAIGN CAPTION:\ntext")
  const hasSeparator = /\n---+\s*\n/.test(content);
  const singleCaption = !hasSeparator
    ? (main.match(/^(?:Caption|CAMPAIGN CAPTION):\s*\n?([\s\S]+?)(?=\n\n|$)/im)?.[1] || '').trim()
    : '';

  // Need either a --- section or a single caption to render
  if (!hasSeparator && !singleCaption) return null;

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 12, color: '#00d4ff', fontWeight: 600, marginBottom: 3 }}>{title}</div>
      {keyLine && <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>{keyLine}</div>}
      {hasSeparator ? (
        <CaptionsBlock content={content} />
      ) : singleCaption && (
        <div style={{ background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <pre style={{ flex: 1, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, color: '#ccc', lineHeight: 1.6, fontFamily: 'inherit' }}>{singleCaption}</pre>
          <CopyButton text={singleCaption} />
        </div>
      )}
    </div>
  );
}

function NewsletterBlock({ content, apiBase, adminSecret }) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState(false);

  if (!content) return null;

  // Parse subject from content
  const subjectMatch = content.match(/^\*\*Subject Line Options:\*\*\s*\n+\d+\.\s*(.+)/m);
  const defaultSubject = subjectMatch ? subjectMatch[1].trim() : `NeuroRank Weekly — ${new Date().toISOString().split('T')[0]}`;
  // Strip the subject line options header for the body
  const body = content.replace(/^\*\*Subject Line Options:\*\*[\s\S]*?---\s*\n/, '').trim();

  const handleSend = async () => {
    if (!window.confirm(`Send newsletter to all registered users?\n\nSubject: ${defaultSubject}`)) return;
    setSending(true);
    setResult(null);
    try {
      const res = await fetch(`${apiBase}/api/admin/send-newsletter`, {
        method: 'POST',
        headers: { 'x-admin-secret': adminSecret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: defaultSubject, body }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setResult(data);
    } catch (err) {
      setResult({ error: err.message });
    }
    setSending(false);
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        Weekly Newsletter
        {result?.sent != null && (
          <span style={{ fontSize: 11, color: '#10b981', fontWeight: 400 }}>
            Sent to {result.sent}/{result.total} users
          </span>
        )}
        {result?.error && (
          <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 400 }}>{result.error}</span>
        )}
      </div>
      <div style={{ background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 10, padding: '14px 16px' }}>
        <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 700, marginBottom: 8 }}>Subject: {defaultSubject}</div>
        <pre style={{
          margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          fontSize: 12, color: '#ccc', lineHeight: 1.6, fontFamily: 'inherit',
          maxHeight: expanded ? 'none' : 200, overflow: 'hidden',
        }}>{body}</pre>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={() => setExpanded(e => !e)} style={{
            background: 'transparent', border: '1px solid #222', color: '#555',
            borderRadius: 7, padding: '7px 14px', fontSize: 11, cursor: 'pointer',
          }}>
            {expanded ? 'Collapse' : 'Expand'}
          </button>
          <CopyButton text={content} />
          <button onClick={handleSend} disabled={sending} style={{
            background: sending ? '#333' : '#f59e0b', border: 'none', color: '#000',
            borderRadius: 7, padding: '7px 18px', fontSize: 11, fontWeight: 700, cursor: sending ? 'default' : 'pointer',
          }}>
            {sending ? 'Sending...' : 'Send to All Users'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RetakeReminderBlock({ apiBase, adminSecret }) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const handleSend = async () => {
    if (!window.confirm('Send retake reminder emails to users whose last test was 7+ days ago?')) return;
    setSending(true);
    setResult(null);
    try {
      const res = await fetch(`${apiBase}/api/admin/send-retake-reminders`, {
        method: 'POST',
        headers: { 'x-admin-secret': adminSecret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ minDays: 7 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setResult(data);
    } catch (err) {
      setResult({ error: err.message });
    }
    setSending(false);
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 12, color: '#a78bfa', fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        Retake Reminders
        {result?.sent != null && (
          <span style={{ fontSize: 11, color: '#10b981', fontWeight: 400 }}>
            Sent {result.sent}, skipped {result.skipped} (of {result.total} users)
          </span>
        )}
        {result?.error && (
          <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 400 }}>{result.error}</span>
        )}
      </div>
      <div style={{ background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 10, padding: '14px 16px' }}>
        <p style={{ margin: '0 0 10px', fontSize: 12, color: '#94a3b8', lineHeight: 1.6 }}>
          Sends "your brain changed this week" email to users whose last test was 7+ days ago. Won't re-send within 14 days.
        </p>
        <button onClick={handleSend} disabled={sending} style={{
          background: sending ? '#333' : '#a78bfa', border: 'none', color: '#000',
          borderRadius: 7, padding: '7px 18px', fontSize: 11, fontWeight: 700, cursor: sending ? 'default' : 'pointer',
        }}>
          {sending ? 'Sending...' : 'Send Retake Reminders'}
        </button>
      </div>
    </div>
  );
}

function ContentBlock({ title, content, singleBlock = false }) {
  if (!content) return null;
  const [expanded, setExpanded] = useState(false);

  // Split on numbered markers like [POST 1], [MESSAGE 1], ## CONCEPT 1, ## SCRIPT 1, ---
  // If singleBlock or no markers found, treat as one item
  const markerRe = /\n(?=\s*(?:\[(?:POST|MESSAGE|CONCEPT|SCRIPT|VIDEO)\s*\d+)|##\s+(?:CONCEPT|SCRIPT|VIDEO)\s+\d+|---+\s*$)/m;
  const hasMarkers = !singleBlock && markerRe.test(content);
  const rawParts = singleBlock
    ? [content]
    : hasMarkers
      ? content.split(markerRe)
      : content.split(/\n\n+/);
  const posts = rawParts.map(p => p.replace(/^[-\s]+\n/, '').trim()).filter(Boolean);
  const hasMany = posts.length > 1;

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 12, color: '#00d4ff', fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        {title}
        {hasMany && <span style={{ fontSize: 11, color: '#555', fontWeight: 400 }}>{posts.length} items</span>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {(expanded ? posts : posts.slice(0, 3)).map((post, i) => (
          <div key={i} style={{
            background: '#0d1117', border: '1px solid #1a1a1a',
            borderRadius: 8, padding: '12px 14px',
            display: 'flex', alignItems: 'flex-start', gap: 10,
          }}>
            <pre style={{
              flex: 1, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              fontSize: 12, color: '#ccc', lineHeight: 1.6, fontFamily: 'inherit',
            }}>{post.trim()}</pre>
            <CopyButton text={post.trim()} />
          </div>
        ))}
        {hasMany && posts.length > 3 && (
          <button onClick={() => setExpanded(e => !e)} style={{
            background: 'transparent', border: '1px solid #222',
            color: '#555', borderRadius: 7, padding: '7px',
            fontSize: 11, cursor: 'pointer',
          }}>
            {expanded ? 'Show less' : `Show ${posts.length - 3} more`}
          </button>
        )}
      </div>
    </div>
  );
}


export default function AdminDashboard() {
  const [secret, setSecret] = useState(() => localStorage.getItem('admin_secret') || '');
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [section, setSection] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Toast system (declared early — referenced in deps of callbacks below)
  const [toasts, setToasts] = useState([]);
  const toast = useCallback((message, opts = {}) => {
    const id = Math.random().toString(36).slice(2);
    const entry = { id, message, kind: opts.kind || 'info', action: opts.action, ttl: opts.ttl ?? 4000 };
    setToasts(prev => [...prev, entry]);
    if (entry.ttl > 0) setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), entry.ttl);
    return id;
  }, []);
  const dismissToast = useCallback((id) => setToasts(prev => prev.filter(t => t.id !== id)), []);

  const api = process.env.NODE_ENV === 'production' ? '' : LOCAL_API;

  const load = useCallback(async (s = secret) => {
    setLoading(true);
    setError('');
    const base = process.env.NODE_ENV === 'production' ? '' : LOCAL_API;
    try {
      const headers = { 'x-admin-secret': s };
      const [statsRes, contentRes] = await Promise.all([
        fetch(`${base}/api/admin/stats`, { headers }),
        fetch(`${base}/api/admin/content`, { headers }),
      ]);
      if (statsRes.status === 403) {
        setError('Invalid secret.');
        setAuthed(false);
        setLoading(false);
        return;
      }
      const statsData = await statsRes.json();
      const contentJson = contentRes.ok ? await contentRes.json() : {};
      setStats(statsData);
      setContent(contentJson.content || {});
      setAuthed(true);
      setLastRefresh(new Date().toLocaleTimeString());
      localStorage.setItem('admin_secret', s);
    } catch (e) {
      setError('Failed to connect to server.');
    }
    setLoading(false);
  }, [secret]);

  // Attribution panel — Phase 1 Acquisition Lock-On (docs/growth/CHARTER.md)
  const [attribution, setAttribution] = useState(null);
  const [attributionDays, setAttributionDays] = useState(7);
  const [attributionLoading, setAttributionLoading] = useState(false);
  const [logPostUrl, setLogPostUrl] = useState('');
  const [logPostAccount, setLogPostAccount] = useState('personal');
  const [logPostAngle, setLogPostAngle] = useState('');
  const [logPostBusy, setLogPostBusy] = useState(false);

  const loadAttribution = useCallback(async (n = attributionDays) => {
    setAttributionLoading(true);
    try {
      const r = await fetch(`${api}/api/admin/attribution?days=${n}`, { headers: { 'x-admin-secret': secret } });
      const d = await r.json();
      setAttribution(d);
    } catch {
      setAttribution({ error: 'Failed to load attribution.' });
    }
    setAttributionLoading(false);
  }, [api, secret, attributionDays]);

  const submitManualPost = useCallback(async () => {
    if (!logPostUrl.trim()) return;
    setLogPostBusy(true);
    try {
      const r = await fetch(`${api}/api/admin/reddit-posts/log`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: logPostUrl.trim(), account: logPostAccount, angle: logPostAngle.trim() || null }),
      });
      const result = await r.json();
      if (result?.tracked) {
        toast(`Logged ${result.entry.subreddit || 'post'} (${logPostAccount})`, { kind: 'success' });
        setLogPostUrl('');
        setLogPostAngle('');
      } else if (result?.reason === 'already logged') {
        toast('Already logged.', { kind: 'info' });
      } else {
        toast(`Log failed: ${result?.error || 'unknown'}`, { kind: 'error' });
      }
    } catch (err) {
      toast(`Log failed: ${err.message}`, { kind: 'error' });
    }
    setLogPostBusy(false);
  }, [api, secret, logPostUrl, logPostAccount, logPostAngle, toast]);

  const [cmo, setCmo] = useState(null);
  const [cmoLoading, setCmoLoading] = useState(false);
  const [cmoScanning, setCmoScanning] = useState(false);
  const [draftingId, setDraftingId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyContext, setReplyContext] = useState('');
  const [replyDraft, setReplyDraft] = useState('');
  const [replyDrafting, setReplyDrafting] = useState(false);

  // CMO action state (close-the-loop buttons)
  const [cmoActionId, setCmoActionId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const flagOpportunity = useCallback(async (id, action) => {
    setCmoActionId(`${id}:${action}`);
    try {
      const r = await fetch(`${api}/api/admin/cmo/opportunity-action`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, action }),
      });
      const result = await r.json().catch(() => ({}));
      setCmo(prev => prev ? {
        ...prev,
        opportunities: (prev.opportunities || []).map(o => {
          if (o.id !== id) return o;
          if (action === 'reset') { const { posted, archived, postedAt, archivedAt, ...r } = o; return r; }
          return { ...o, [action]: true, [`${action}At`]: new Date().toISOString() };
        }),
      } : prev);
      if (result?.tracked) {
        toast(`Logged to reddit-posts.json (${action})`, { kind: 'success', ttl: 3000 });
      }
    } catch {}
    setCmoActionId(null);
  }, [api, secret, toast]);

  const flagMention = useCallback(async (url, action) => {
    setCmoActionId(`${url}:${action}`);
    try {
      await fetch(`${api}/api/admin/cmo/mention-action`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, action }),
      });
      setCmo(prev => prev ? {
        ...prev,
        mentions: (prev.mentions || []).map(m => {
          if (m.url !== url) return m;
          if (action === 'reset') { const { replied, dismissed, repliedAt, dismissedAt, ...r } = m; return r; }
          return { ...m, [action]: true, [`${action}At`]: new Date().toISOString() };
        }),
      } : prev);
    } catch {}
    setCmoActionId(null);
  }, [api, secret]);

  // Users action state
  const [userActionId, setUserActionId] = useState(null);
  const [userActionMsg, setUserActionMsg] = useState('');

  const userAction = useCallback(async (userId, kind) => {
    setUserActionId(`${userId}:${kind}`);
    setUserActionMsg('');
    try {
      let path;
      let body = {};
      if (kind === 'promote') path = `/api/admin/users/${userId}/promote`;
      else if (kind === 'retest') { path = `/api/admin/users/${userId}/grant-retests`; body = { n: 1 }; }
      else if (kind === 'resend') path = `/api/admin/users/${userId}/resend-combine`;
      const r = await fetch(`${api}${path}`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) setUserActionMsg(`${kind} failed: ${d.error || r.status}`);
      else {
        setUserActionMsg(`${kind} ok${d.retestCredits != null ? ` — credits: ${d.retestCredits}` : ''}${d.email ? ` — sent to ${d.email}` : ''}`);
        if (kind === 'retest' || kind === 'promote') load();
      }
    } catch (e) {
      setUserActionMsg(`${kind} error: ${e.message}`);
    }
    setUserActionId(null);
  }, [api, secret]); // eslint-disable-line

  // Funnel drilldown state
  const [funnelModal, setFunnelModal] = useState(null); // { module, sessions, loading }
  const [bulkEmailSubject, setBulkEmailSubject] = useState('Come back and finish your NeuroRank combine');
  const [bulkEmailBody, setBulkEmailBody] = useState("Hey — you started a NeuroRank combine but didn't finish. It takes about 12 minutes. Your score, archetype, and scouting report are waiting.\n\nhttps://neurorank.app");
  const [bulkSending, setBulkSending] = useState(false);

  const openFunnelModule = useCallback(async (moduleKey) => {
    setFunnelModal({ module: moduleKey, sessions: [], loading: true, selected: new Set() });
    try {
      const r = await fetch(`${api}/api/admin/funnel/dropped?module=${encodeURIComponent(moduleKey)}`, { headers: { 'x-admin-secret': secret } });
      const d = await r.json();
      setFunnelModal({ module: moduleKey, sessions: d.sessions || [], loading: false, selected: new Set() });
    } catch {
      setFunnelModal({ module: moduleKey, sessions: [], loading: false, selected: new Set(), error: true });
    }
  }, [api, secret]);

  const sendFunnelEmail = useCallback(async () => {
    if (!funnelModal) return;
    const userIds = [...(funnelModal.selected || new Set())];
    if (!userIds.length) return;
    setBulkSending(true);
    try {
      const r = await fetch(`${api}/api/admin/funnel/email-bulk`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ userIds, subject: bulkEmailSubject, body: bulkEmailBody }),
      });
      const d = await r.json();
      toast(`Sent ${d.sent || 0} / ${d.total || 0} emails${d.failed ? ` — ${d.failed} failed` : ''}`, { kind: d.failed ? 'warning' : 'success' });
      setFunnelModal(null);
    } catch (e) {
      toast('Send failed: ' + e.message, { kind: 'error' });
    }
    setBulkSending(false);
  }, [api, secret, funnelModal, bulkEmailSubject, bulkEmailBody, toast]);

  // Abandoned sessions state
  const [abandoned, setAbandoned] = useState(null);
  const [abandonedLoading, setAbandonedLoading] = useState(false);
  const [sessionActionId, setSessionActionId] = useState(null);
  const [inspectModal, setInspectModal] = useState(null);

  const loadAbandoned = useCallback(async () => {
    setAbandonedLoading(true);
    try {
      const r = await fetch(`${api}/api/admin/sessions/abandoned`, { headers: { 'x-admin-secret': secret } });
      const d = await r.json();
      setAbandoned(d.sessions || []);
    } catch {}
    setAbandonedLoading(false);
  }, [api, secret]);

  const sessionAction = useCallback(async (id, kind) => {
    if (kind === 'kill' && !window.confirm(`Kill session ${id}? This cannot be undone.`)) return;
    setSessionActionId(`${id}:${kind}`);
    try {
      if (kind === 'inspect') {
        const r = await fetch(`${api}/api/admin/sessions/${id}/inspect`, { headers: { 'x-admin-secret': secret } });
        const d = await r.json();
        setInspectModal(d);
      } else if (kind === 'kill') {
        await fetch(`${api}/api/admin/sessions/${id}/kill`, { method: 'POST', headers: { 'x-admin-secret': secret } });
        setAbandoned(prev => (prev || []).filter(s => s.sessionId !== id));
      } else if (kind === 'autocomplete') {
        const r = await fetch(`${api}/api/admin/sessions/${id}/autocomplete-secret`, { method: 'POST', headers: { 'x-admin-secret': secret } });
        if (r.ok) setAbandoned(prev => (prev || []).filter(s => s.sessionId !== id));
        else toast('Autocomplete failed: ' + (await r.json()).error, { kind: 'error' });
      }
    } catch (e) { toast(e.message, { kind: 'error' }); }
    setSessionActionId(null);
  }, [api, secret]);

  // Org Outreach state
  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgResearchName, setOrgResearchName] = useState('');
  const [orgResearching, setOrgResearching] = useState(false);
  const [orgDraftModal, setOrgDraftModal] = useState(null);
  const [orgDrafting, setOrgDrafting] = useState(false);

  const loadOrgs = useCallback(async () => {
    setOrgsLoading(true);
    try {
      const r = await fetch(`${api}/api/admin/cmo/org-research`, { headers: { 'x-admin-secret': secret } });
      const d = await r.json();
      setOrgs(d.orgs || []);
    } catch {}
    setOrgsLoading(false);
  }, [api, secret]);

  const researchOrg = useCallback(async () => {
    const name = orgResearchName.trim();
    if (!name) return;
    setOrgResearching(true);
    try {
      const r = await fetch(`${api}/api/admin/cmo/org-research`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgName: name }),
      });
      const d = await r.json();
      if (d.ok && d.intel) {
        setOrgs(prev => {
          const filtered = (prev || []).filter(o => o.orgName !== d.intel.orgName);
          return [d.intel, ...filtered];
        });
        setOrgResearchName('');
      } else {
        toast(d.error || 'Research failed', { kind: 'error' });
      }
    } catch (e) { toast(e.message, { kind: 'error' }); }
    setOrgResearching(false);
  }, [api, secret, orgResearchName]);

  const draftOrgOutreach = useCallback(async (orgName) => {
    setOrgDrafting(true);
    setOrgDraftModal({ orgName, draft: null });
    try {
      const r = await fetch(`${api}/api/admin/cmo/org-outreach`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgName }),
      });
      const d = await r.json();
      setOrgDraftModal({ orgName, draft: d.draft || null, error: d.error });
    } catch (e) {
      setOrgDraftModal({ orgName, draft: null, error: e.message });
    }
    setOrgDrafting(false);
  }, [api, secret]);

  // Bulk user selection state (moved up — toast already declared above)
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);

  const bulkUserAction = useCallback(async (action) => {
    const ids = [...selectedUserIds];
    if (!ids.length) return;
    const verb = action === 'delete' ? 'DELETE' : action === 'ban' ? 'BAN' : action === 'unban' ? 'UNBAN' : 'grant +1 retest to';
    if ((action === 'delete' || action === 'ban') && !window.confirm(`${verb} ${ids.length} user${ids.length !== 1 ? 's' : ''}? ${action === 'delete' ? 'This is permanent.' : ''}`)) return;
    setBulkRunning(true);
    try {
      const r = await fetch(`${api}/api/admin/users/bulk-action`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, action }),
      });
      const d = await r.json();
      if (r.ok) {
        toast(`${d.ok}/${d.total} ${action}`, { kind: 'success' });
        setSelectedUserIds(new Set());
        load();
      } else {
        toast(`Bulk action failed: ${d.error}`, { kind: 'error' });
      }
    } catch (e) { toast(`Bulk error: ${e.message}`, { kind: 'error' }); }
    setBulkRunning(false);
  }, [api, secret, selectedUserIds, toast]); // eslint-disable-line

  // Global search state
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searching, setSearching] = useState(false);

  const runSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const r = await fetch(`${api}/api/admin/search?q=${encodeURIComponent(q)}`, { headers: { 'x-admin-secret': secret } });
      setSearchResults(await r.json());
    } catch {}
    setSearching(false);
  }, [api, secret]);

  useEffect(() => {
    const t = setTimeout(() => runSearch(searchQ), 250);
    return () => clearTimeout(t);
  }, [searchQ, runSearch]);

  // Keyboard shortcut: Cmd/Ctrl+K to open search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Auto-refresh front-page stats every 30s while section is null + tab visible
  useEffect(() => {
    if (!authed || section !== null) return;
    const tick = () => { if (!document.hidden) load(); };
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [authed, section, load]);

  // Settings state
  const [auditLog, setAuditLog] = useState(null);
  const [consoleConfig, setConsoleConfig] = useState(null);
  const [consoleConfigEdits, setConsoleConfigEdits] = useState({});
  const [settingsSaveMsg, setSettingsSaveMsg] = useState('');
  const [agentsPaused, setAgentsPaused] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const [a, c, ag] = await Promise.all([
        fetch(`${api}/api/admin/audit-log`, { headers: { 'x-admin-secret': secret } }).then(r => r.json()),
        fetch(`${api}/api/admin/agents/console-config`, { headers: { 'x-admin-secret': secret } }).then(r => r.json()),
        fetch(`${api}/api/admin/agents`, { headers: { 'x-admin-secret': secret } }).then(r => r.json()),
      ]);
      setAuditLog(a);
      setConsoleConfig(c);
      setConsoleConfigEdits({});
      setAgentsPaused(!!ag?.paused);
    } catch {}
  }, [api, secret]);

  const saveConsoleConfig = useCallback(async () => {
    try {
      await fetch(`${api}/api/admin/agents/console-config`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify(consoleConfigEdits),
      });
      setSettingsSaveMsg('Saved');
      setTimeout(() => setSettingsSaveMsg(''), 2000);
      loadSettings();
    } catch (e) { setSettingsSaveMsg('Save failed: ' + e.message); }
  }, [api, secret, consoleConfigEdits, loadSettings]);

  const toggleAgentsPause = useCallback(async () => {
    try {
      await fetch(`${api}/api/admin/agents/pause`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ paused: !agentsPaused }),
      });
      setAgentsPaused(p => !p);
    } catch {}
  }, [api, secret, agentsPaused]);

  // Time-series state
  const [timeseries, setTimeseries] = useState(null);
  const loadTimeseries = useCallback(async () => {
    try {
      const r = await fetch(`${api}/api/admin/timeseries`, { headers: { 'x-admin-secret': secret } });
      setTimeseries(await r.json());
    } catch {}
  }, [api, secret]);

  useEffect(() => {
    if (!authed) return;
    if (section === null && timeseries === null) loadTimeseries();
    if (section === 'Settings' && auditLog === null) loadSettings();
  }, [authed, section]); // eslint-disable-line

  // Destructive user/profile actions
  const userDestructiveAction = useCallback(async (userId, kind, email) => {
    const label = kind === 'delete' ? 'DELETE' : kind === 'ban' ? 'BAN' : 'UNBAN';
    if (kind === 'delete' && !window.confirm(`${label} user ${email}? This is permanent.`)) return;
    if (kind === 'ban' && !window.confirm(`Ban ${email}? They will not be able to log in.`)) return;
    setUserActionId(`${userId}:${kind}`);
    try {
      const path = kind === 'delete' ? `/users/${userId}/delete` : `/users/${userId}/ban`;
      const body = kind === 'delete' ? {} : { banned: kind === 'ban' };
      const r = await fetch(`${api}/api/admin${path}`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) setUserActionMsg(`${kind} failed: ${d.error}`);
      else { setUserActionMsg(`${kind} ok`); load(); }
    } catch (e) { setUserActionMsg(`${kind} error: ${e.message}`); }
    setUserActionId(null);
  }, [api, secret]); // eslint-disable-line

  const profileAction = useCallback(async (shareableId, kind) => {
    if (kind === 'delete' && !window.confirm(`DELETE profile ${shareableId}? This is permanent.`)) return;
    try {
      const path = kind === 'delete' ? `/profiles/${shareableId}/delete` : `/profiles/${shareableId}/flag`;
      const body = kind === 'delete' ? {} : { flagged: true };
      await fetch(`${api}/api/admin${path}`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      load();
    } catch {}
  }, [api, secret]); // eslint-disable-line

  // SEO recs done state (localStorage)
  const [seoDone, setSeoDone] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nr_admin_seo_done') || '[]'); }
    catch { return []; }
  });
  const toggleSeoRec = useCallback((id) => {
    setSeoDone(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      try { localStorage.setItem('nr_admin_seo_done', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  // Events + anomalies state
  const [eventsData, setEventsData] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [anomalies, setAnomalies] = useState(null);
  const [anomaliesLoading, setAnomaliesLoading] = useState(false);

  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const r = await fetch(`${api}/api/admin/events`, { headers: { 'x-admin-secret': secret } });
      setEventsData(await r.json());
    } catch {}
    setEventsLoading(false);
  }, [api, secret]);

  const loadAnomalies = useCallback(async () => {
    setAnomaliesLoading(true);
    try {
      const r = await fetch(`${api}/api/admin/anomalies`, { headers: { 'x-admin-secret': secret } });
      setAnomalies(await r.json());
    } catch {}
    setAnomaliesLoading(false);
  }, [api, secret]);

  useEffect(() => {
    if (section === 'Marketing' && authed && attribution === null) loadAttribution();
    if (section === 'Orgs' && authed) loadOrgs();
    if (section === 'Overview' && authed && abandoned === null) loadAbandoned();
    if (section === 'Events' && authed && eventsData === null) { loadEvents(); loadAnomalies(); }
  }, [section, authed]); // eslint-disable-line


  // Chat state
  const [analyticsTab, setAnalyticsTab] = useState('overview');
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', text: 'NeuroRank CMO online. I have full platform context and live data. I can draft tweets, Reddit comments, blog posts, analyze player profiles, or strategize growth. What do you need?' },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  const sendChat = useCallback(async (msg) => {
    if (!msg.trim()) return;
    const userMsg = { role: 'user', text: msg.trim() };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setChatLoading(true);
    try {
      const r = await fetch(`${api}/api/admin/chat`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg.trim(), history: [...chatMessages, userMsg].slice(-10) }),
      });
      const d = await r.json();
      setChatMessages(prev => [...prev, { role: 'assistant', text: d.reply || 'No response.' }]);
    } catch (e) {
      setChatMessages(prev => [...prev, { role: 'assistant', text: 'Error connecting to server.' }]);
    }
    setChatLoading(false);
  }, [api, secret, chatMessages]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadCmo = useCallback(() => {
    setCmoLoading(true);
    fetch(`${api}/api/admin/cmo`, { headers: { 'x-admin-secret': secret } })
      .then(r => r.json())
      .then(d => { setCmo(d); setCmoLoading(false); })
      .catch(() => setCmoLoading(false));
  }, [api, secret]);

  const [scanStatus, setScanStatus] = useState('');
  const [agentMonitoring, setAgentMonitoring] = useState(false);
  const [mentionMonitoring, setMentionMonitoring] = useState(false);

  const runScan = useCallback(async () => {
    setCmoScanning(true);
    setScanStatus('Scanning Reddit…');
    try {
      const r = await fetch(`${api}/api/admin/cmo/scan`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
      });
      const d = await r.json();
      if (r.ok) {
        const base = `Found ${d.opportunities?.length || 0} opportunities across ${d.subsScanned || 0} subs`;
        const errTail = d.errors?.length ? ` — first error: ${d.errors[0]}` : '';
        setScanStatus(base + errTail);
        if (d.errors?.length) console.warn('[CMO scan errors]', d.errors);
        await loadCmo();
      } else {
        setScanStatus(`Scan error: ${d.error || r.status}`);
      }
    } catch (e) {
      setScanStatus(`Failed: ${e.message}`);
    }
    setCmoScanning(false);
  }, [api, secret, loadCmo]);

  const runAgentMonitor = useCallback(async () => {
    setAgentMonitoring(true);
    setScanStatus('Agent scanning Reddit…');
    try {
      const subs = CMO_SUBS.slice(0, 20).map(s => s.sub);
      const r = await fetch(`${api}/api/admin/cmo/agent-monitor`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subs }),
      });
      const d = await r.json();
      if (d.ok) {
        setScanStatus(`Agent found ${d.opportunityCount} opportunities`);
        await loadCmo();
      } else {
        setScanStatus(`Agent error: ${d.error}`);
      }
    } catch (e) {
      setScanStatus(`Failed: ${e.message}`);
    }
    setAgentMonitoring(false);
  }, [api, secret, loadCmo]);

  const runMentionMonitorAgent = useCallback(async () => {
    setMentionMonitoring(true);
    setScanStatus('Agent scanning for mentions…');
    try {
      const r = await fetch(`${api}/api/admin/cmo/mention-monitor`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
      });
      const d = await r.json();
      if (d.ok) {
        setScanStatus(`Found ${d.newCount} new mention${d.newCount !== 1 ? 's' : ''}`);
        await loadCmo();
      } else {
        setScanStatus(`Mention monitor error: ${d.error}`);
      }
    } catch (e) {
      setScanStatus(`Failed: ${e.message}`);
    }
    setMentionMonitoring(false);
  }, [api, secret, loadCmo]);

  const generateDraft = useCallback(async (opp) => {
    setDraftingId(opp.id);
    try {
      const r = await fetch(`${api}/api/admin/cmo/draft`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: opp.title, body: opp.body, sub: opp.sub }),
      });
      const d = await r.json();
      setCmo(prev => ({
        ...prev,
        opportunities: prev.opportunities.map(o => o.id === opp.id ? { ...o, draft: d.draft } : o),
      }));
    } catch {}
    setDraftingId(null);
  }, [api, secret]);

  useEffect(() => {
    if (authed && !cmo) loadCmo();
  }, [authed]); // eslint-disable-line

  // ── Agents tab ──────────────────────────────────────────────────────────────
  const [agentsData, setAgentsData] = useState(null);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [pauseLoading, setPauseLoading] = useState(false);

  const loadAgents = useCallback(() => {
    setAgentsLoading(true);
    Promise.all([
      fetch(`${api}/api/admin/agents`, { headers: { 'x-admin-secret': secret } }).then(r => r.json()),
      fetch(`${api}/api/admin/agents/health`, { headers: { 'x-admin-secret': secret } }).then(r => r.json()),
    ]).then(([runs, health]) => {
      setAgentsData({ ...runs, health });
      setAgentsLoading(false);
    }).catch(() => setAgentsLoading(false));
  }, [api, secret]);

  const [factoryTooltip, setFactoryTooltip] = useState(null);
  const [checkingNow, setCheckingNow] = useState(false);
  const checkNow = useCallback(async () => {
    setCheckingNow(true);
    try {
      const r = await fetch(`${api}/api/admin/agents/check-now`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
      });
      const d = await r.json();
      setAgentsData(prev => ({
        ...prev,
        health: {
          lastCheck:     new Date().toISOString(),
          lastSeverity:  d.severity,
          lastSummary:   d.summary,
          lastAnomalies: d.anomalies || [],
        },
      }));
    } catch {}
    setCheckingNow(false);
  }, [api, secret]);

  const togglePause = useCallback(async () => {
    setPauseLoading(true);
    try {
      const r = await fetch(`${api}/api/admin/agents/pause`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ paused: !agentsData?.paused }),
      });
      const d = await r.json();
      if (d.ok) setAgentsData(prev => ({ ...prev, paused: d.paused }));
    } catch {}
    setPauseLoading(false);
  }, [api, secret, agentsData]);

  useEffect(() => {
    if (authed && !agentsData) loadAgents();
  }, [authed]); // eslint-disable-line

  if (!authed) {
    return (
      <div style={{
        minHeight: '100vh', background: '#060a0f', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          background: '#0d1117', border: '1px solid #1a2a3a',
          borderRadius: 14, padding: '40px 48px', width: 360,
        }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 6 }}>Admin Dashboard</div>
          <div style={{ fontSize: 13, color: '#555', marginBottom: 24 }}>NeuroRank internal</div>
          <input
            type="password"
            placeholder="Admin secret"
            value={secret}
            onChange={e => setSecret(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load(secret)}
            style={{
              width: '100%', background: '#060a0f', border: '1px solid #333',
              borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14,
              outline: 'none', boxSizing: 'border-box', marginBottom: 12,
            }}
            autoFocus
          />
          {error && <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 10 }}>{error}</div>}
          <button
            onClick={() => load(secret)}
            disabled={loading || !secret}
            style={{
              width: '100%', background: '#00d4ff', border: 'none',
              borderRadius: 8, padding: '11px', color: '#060a0f',
              fontWeight: 700, fontSize: 14, cursor: 'pointer',
              opacity: loading || !secret ? 0.5 : 1,
            }}
          >
            {loading ? 'Loading…' : 'Enter'}
          </button>
        </div>
      </div>
    );
  }

  const p = stats?.platform || {};
  const u = stats?.users || {};

  // ── AGENT_META used by both dashboard + agents tab ──────────────────────────
  const AGENT_META = {
    anomaly_detector:  { label: 'Anomaly Detector',  color: '#10b981' },
    data_quality:      { label: 'Data Quality',      color: '#ef4444' },
    seo_content:       { label: 'SEO Content',       color: '#818cf8' },
    blog_writer:       { label: 'Blog Writer',       color: '#6366f1' },
    scout_digest:      { label: 'Scout Digest',      color: '#f59e0b' },
    team_report:       { label: 'Team Report',       color: '#06b6d4' },
    weekly_digest:     { label: 'Weekly Digest',     color: '#84cc16' },
    reengagement:      { label: 'Re-engagement',     color: '#fb923c' },
    onboarding_drip:   { label: 'Onboarding Drip',   color: '#34d399' },
    coach_free:        { label: 'Coach (Free)',       color: '#a855f7' },
    coach_pro:         { label: 'Coach (Pro)',        color: '#e879f9' },
    watchlist:         { label: 'Watchlist Intel',    color: '#00d4ff' },
    team_composition:  { label: 'Team Composition',  color: '#38bdf8' },
    reddit_monitor:    { label: 'Reddit Monitor',    color: '#ff6b35' },
    mention_monitor:   { label: 'Mention Monitor',   color: '#22d3ee' },
  };

  const timeAgo = (ts) => {
    if (!ts) return 'never';
    const mins = Math.round((Date.now() - new Date(ts)) / 60000);
    if (mins < 60)   return `${mins}m ago`;
    if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
    return `${Math.round(mins / 1440)}d ago`;
  };

  return (
    <div style={{ minHeight: '100vh', background: COLORS.bg, color: COLORS.text, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif' }}>

      {/* ═══ ANIMATION KEYFRAMES ═══ */}
      <style>{`
        @keyframes nr-pulse     { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        @keyframes nr-pulse-led { 0%,100% { opacity:1; } 50% { opacity:0.2; } }
        @keyframes nr-slide-in  { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:none; } }
        @keyframes nr-bob       { 0%,100% { transform:translateY(0px); } 50% { transform:translateY(-4px); } }
        @keyframes nr-conveyor  { from { transform:translateX(0); } to { transform:translateX(-16px); } }
        .nr-agent-card  { transition: transform 0.15s ease, box-shadow 0.2s ease !important; }
        .nr-agent-card:hover { transform: translateY(-3px) scale(1.02) !important; }
        .nr-log-row     { animation: nr-slide-in 0.2s ease both; }
        .nr-pill:hover  { background: ${COLORS.cardHover} !important; border-color: ${COLORS.borderLight} !important; }
        .nr-action-btn  { transition: all 0.15s ease; }
        .nr-action-btn:hover { transform: translateY(-1px); filter: brightness(1.2); }
        .nr-chat-input:focus { border-color: ${COLORS.accent} !important; outline: none; }
        @media (max-width: 1200px) {
          .nr-grid-4col { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 768px) {
          .nr-grid-4col { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ═══ TOP BAR — Terminal Style ═══ */}
      <div style={{
        background: COLORS.card, borderBottom: `1px solid ${COLORS.border}`,
        padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 16,
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        {/* Left — Title + Status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => setSection(null)}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: agentsData?.paused ? COLORS.red : COLORS.accent,
            animation: agentsData?.paused ? 'none' : 'nr-pulse 2s ease infinite',
            boxShadow: agentsData?.paused ? 'none' : `0 0 8px ${COLORS.accent}66`,
          }} />
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, letterSpacing: '0.02em' }}>NeuroRank Terminal</div>
          <div style={{ fontSize: 11, color: COLORS.dim }}>
            {agentsData?.paused ? 'Agents Paused' : 'Running Daily'}
          </div>
        </div>

        {/* Center — Status line */}
        <div style={{ fontSize: 10, color: COLORS.faint, fontFamily: 'monospace' }}>
          {scanStatus || (lastRefresh ? `synced ${lastRefresh}` : '')}
        </div>

        <div style={{ flex: 1 }} />

        {/* Right — Secondary nav pills */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {SECONDARY_VIEWS.map(s => (
            <button key={s} onClick={() => setSection(section === s ? null : s)}
              className="nr-pill"
              style={{
                background: section === s ? `${COLORS.accent}22` : 'transparent',
                border: `1px solid ${section === s ? COLORS.accent : COLORS.border}`,
                color: section === s ? COLORS.accent : COLORS.dim,
                borderRadius: 6, padding: '4px 10px', fontSize: 10,
                cursor: 'pointer', fontWeight: section === s ? 600 : 400,
              }}>{s}</button>
          ))}
        </div>

        <button onClick={() => setSearchOpen(true)} title="Global search (Ctrl/Cmd+K)" style={{
          background: COLORS.card, border: `1px solid ${COLORS.border}`,
          color: COLORS.dim, borderRadius: 6, padding: '4px 10px',
          fontSize: 10, cursor: 'pointer',
        }}>
          🔍 Search <span style={{ color: COLORS.faint, marginLeft: 4 }}>⌘K</span>
        </button>
        <button onClick={() => load()} disabled={loading} style={{
          background: COLORS.card, border: `1px solid ${COLORS.border}`,
          color: COLORS.dim, borderRadius: 6, padding: '4px 12px',
          fontSize: 10, cursor: 'pointer',
        }}>
          {loading ? '...' : 'Refresh'}
          {section === null && authed && <span title="Auto-refresh on" style={{ marginLeft: 6, width: 6, height: 6, display: 'inline-block', borderRadius: '50%', background: COLORS.accent, verticalAlign: 'middle', animation: 'nr-pulse-led 2s ease infinite' }} />}
        </button>
        <button onClick={() => { setAuthed(false); localStorage.removeItem('admin_secret'); }} style={{
          background: 'transparent', border: `1px solid ${COLORS.border}`,
          color: COLORS.faint, borderRadius: 6, padding: '4px 10px',
          fontSize: 10, cursor: 'pointer',
        }}>
          Logout
        </button>
      </div>

      {/* ═══ MAIN CONTENT ═══ */}

      {/* ─── 4-COLUMN DASHBOARD (default view) ─── */}
      {!section && (
        <div className="nr-grid-4col" style={{
          display: 'grid', gridTemplateColumns: '22% 25% 25% 28%',
          gap: 0, minHeight: 'calc(100vh - 48px)',
        }}>

          {/* ══════════ COLUMN 1 — COMPANY ══════════ */}
          <div style={{ borderRight: `1px solid ${COLORS.border}`, padding: '16px 14px', overflowY: 'auto', maxHeight: 'calc(100vh - 48px)' }}>

            {/* Company Summary */}
            <PanelCard title="NeuroRank">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, background: `${COLORS.accent}22`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${COLORS.accent}44`, fontSize: 16,
                }}>N</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>neurorank.app</div>
                  <div style={{ fontSize: 10, color: COLORS.dim }}>Cognitive Esports Combine</div>
                </div>
              </div>
              <div style={{ fontSize: 11, color: COLORS.muted, lineHeight: 1.7 }}>
                NeuroRank lets players complete a structured cognitive combine — choosing between General, FPS, or MOBA routes — that measures reaction speed, aim precision, tracking, working memory, composure under pressure. After completing 6 test modules, users receive a neural profile with dimension scores, an archetype classification, and an AI scouting report. Scouts and coaches can use a separate dashboard to discover and compare players based on cognitive performance data.
              </div>
            </PanelCard>

            {/* Documents */}
            <PanelCard title="Documents">
              {[
                { label: 'Product / Science', desc: 'Platform overview, modules, scoring', icon: '\u{1F4C4}', href: '/science' },
                { label: 'For Coaches', desc: 'Scout Pro / Team tier positioning', icon: '\u{1F4CA}', href: '/for-coaches' },
                { label: 'Brand Voice', desc: 'Tone, messaging, Reddit voice', icon: '\u{1F4DD}', action: () => setSection('CMO') },
                { label: 'Blog & Articles', desc: 'Blog posts + SEO content', icon: '\u{1F4F0}', href: '/blog' },
                { label: 'Pricing', desc: 'Free / Scout Pro / Team tiers', icon: '\u{1F4B0}', href: '/pricing' },
              ].map(doc => {
                const content = (
                  <>
                    <span style={{ fontSize: 14, opacity: 0.6 }}>{doc.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, color: COLORS.text, fontWeight: 500 }}>{doc.label}</div>
                      <div style={{ fontSize: 9, color: COLORS.dim }}>{doc.desc}</div>
                    </div>
                    <span style={{ fontSize: 10, color: COLORS.faint }}>{doc.href ? '↗' : '→'}</span>
                  </>
                );
                const sharedStyle = {
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 0', borderBottom: `1px solid ${COLORS.border}`,
                  cursor: 'pointer', color: 'inherit', textDecoration: 'none',
                };
                if (doc.href) {
                  return <a key={doc.label} href={doc.href} target="_blank" rel="noopener noreferrer" style={sharedStyle}>{content}</a>;
                }
                return <div key={doc.label} onClick={doc.action} style={sharedStyle}>{content}</div>;
              })}
            </PanelCard>

            {/* Competitors */}
            <PanelCard title="Competitors">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {[
                  { name: 'aimlab.gg', color: '#ff4444' },
                  { name: 'humanbenchmark.com', color: '#f59e0b' },
                  { name: 'stroop.gg', color: '#a855f7' },
                  { name: 'kovaaks.com', color: '#00d4ff' },
                  { name: 'aim400kg.com', color: '#10b981' },
                  { name: 'mouse-sensitivity.com', color: '#6366f1' },
                ].map(comp => (
                  <a key={comp.name} href={`https://${comp.name}`} target="_blank" rel="noopener noreferrer" style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                    borderRadius: 6, padding: '6px 8px', textDecoration: 'none',
                    cursor: 'pointer', transition: 'border-color 0.15s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = comp.color}
                    onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}
                  >
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: comp.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 9, color: COLORS.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{comp.name}</span>
                  </a>
                ))}
              </div>
            </PanelCard>

            {/* Integrations */}
            <PanelCard title="Integrations">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {[
                  { name: 'Railway', ok: true, href: 'https://railway.app/dashboard' },
                  { name: 'Supabase', ok: true, href: 'https://supabase.com/dashboard/projects' },
                  { name: 'Resend', ok: true, href: 'https://resend.com/emails' },
                  { name: 'Anthropic', ok: true, href: 'https://console.anthropic.com' },
                  { name: 'Reddit', ok: !!cmo?.lastScan, href: 'https://reddit.com' },
                ].map(int => (
                  <a key={int.name} href={int.href} target="_blank" rel="noopener noreferrer" style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                    borderRadius: 6, padding: '4px 8px', fontSize: 9,
                    textDecoration: 'none', cursor: 'pointer',
                  }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.accent}
                    onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}
                  >
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: int.ok ? COLORS.accent : COLORS.amber }} />
                    <span style={{ color: COLORS.muted }}>{int.name}</span>
                  </a>
                ))}
              </div>
            </PanelCard>
          </div>

          {/* ══════════ COLUMN 2 — ANALYTICS ══════════ */}
          <div style={{ borderRight: `1px solid ${COLORS.border}`, padding: '16px 14px', overflowY: 'auto', maxHeight: 'calc(100vh - 48px)' }}>

            {/* Sessions/Profiles time series (last 30d) */}
            <PanelCard title="Activity — last 30 days">
              {!timeseries ? (
                <div style={{ fontSize: 10, color: COLORS.dim, padding: '10px 0' }}>Loading…</div>
              ) : (() => {
                const s = timeseries.series || [];
                const max = Math.max(1, ...s.map(d => Math.max(d.sessions, d.completed)));
                const W = 280, H = 80, PAD = 4;
                const x = (i) => PAD + (i * (W - PAD * 2)) / Math.max(s.length - 1, 1);
                const y = (v) => H - PAD - (v / max) * (H - PAD * 2);
                const toPath = (key) => s.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d[key]).toFixed(1)}`).join(' ');
                const todaySess = s[s.length - 1]?.sessions || 0;
                const todayComp = s[s.length - 1]?.completed || 0;
                return (
                  <div>
                    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
                      <path d={toPath('sessions')} fill="none" stroke={COLORS.cyan} strokeWidth="1.5" />
                      <path d={toPath('completed')} fill="none" stroke={COLORS.accent} strokeWidth="1.5" />
                    </svg>
                    <div style={{ display: 'flex', gap: 12, fontSize: 10, marginTop: 6 }}>
                      <span style={{ color: COLORS.cyan }}>— Sessions started · today {todaySess}</span>
                      <span style={{ color: COLORS.accent }}>— Completed · today {todayComp}</span>
                    </div>
                  </div>
                );
              })()}
            </PanelCard>

            {/* Website Performance — Circular Rings (like Okara's Lighthouse scores) */}
            <PanelCard title="Website Performance">
              <div style={{ display: 'flex', justifyContent: 'space-around', padding: '6px 0 12px' }}>
                <ProgressRing value={p.completionRate || 0} max={100} size={68} color={COLORS.accent} label="Completion" sublabel={`${p.completionRate || 0}%`} />
                <ProgressRing value={Math.min(Math.round((u.verified || 0) / Math.max(u.total || 1, 1) * 100), 100)} max={100} size={68} color={COLORS.accent} label="Verified" sublabel={`${Math.round((u.verified || 0) / Math.max(u.total || 1, 1) * 100)}%`} />
                <ProgressRing value={p.avgScore || 0} max={100} size={68} color={p.avgScore >= 60 ? COLORS.accent : COLORS.amber} label="Avg Score" />
                <ProgressRing value={p.today || 0} max={Math.max(p.today || 1, 10)} size={68} color={COLORS.purple} label="Today" />
              </div>
            </PanelCard>

            {/* Core Platform Vitals (like Okara's Core Web Vitals) */}
            <PanelCard title="Core Platform Vitals">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                {[
                  { label: 'Sessions', value: p.totalSessions, unit: 'total', status: p.totalSessions > 50 ? 'GOOD' : 'LOW' },
                  { label: 'This Week', value: p.thisWeek, unit: 'sessions', status: p.thisWeek > 5 ? 'GOOD' : 'LOW', wow: p.wow?.sessions },
                  { label: 'Users', value: u.total, unit: 'registered', status: u.total > 20 ? 'GOOD' : 'GROWING', wow: p.wow?.users },
                ].map(v => {
                  const statusColor = v.status === 'GOOD' ? COLORS.accent : COLORS.amber;
                  return (
                    <div key={v.label} style={{
                      background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                      borderRadius: 8, padding: '10px 10px', textAlign: 'center',
                    }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.text, fontVariantNumeric: 'tabular-nums' }}>
                        {v.value ?? '—'}
                        {v.wow && <WoWDelta curr={v.wow.curr} prev={v.wow.prev} />}
                      </div>
                      <div style={{ fontSize: 9, color: COLORS.dim, marginTop: 2 }}>{v.unit}</div>
                      <div style={{
                        fontSize: 8, fontWeight: 700, color: statusColor, marginTop: 4,
                        background: `${statusColor}15`, borderRadius: 3, padding: '2px 6px', display: 'inline-block',
                      }}>{v.status}</div>
                    </div>
                  );
                })}
              </div>
            </PanelCard>

            {/* SEO Health (like Okara) */}
            <PanelCard title="SEO Health">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  { label: 'Blog posts published', status: 'active', detail: '/blog' },
                  { label: 'Science page (E-E-A-T)', status: 'active', detail: '/science' },
                  { label: 'OG images (dynamic)', status: 'active', detail: 'profile sharing' },
                  { label: 'Shareable profile links', status: 'active', detail: 'viral loop' },
                  { label: 'Challenge system (backlinks)', status: 'active', detail: 'player-to-player' },
                ].map(item => (
                  <div key={item.label} style={{
                    display: 'flex', alignItems: 'center', gap: 8, fontSize: 11,
                    padding: '4px 0', borderBottom: `1px solid ${COLORS.border}`,
                  }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: COLORS.accent, flexShrink: 0 }} />
                    <span style={{ color: COLORS.muted, flex: 1 }}>{item.label}</span>
                    <span style={{ fontSize: 9, color: COLORS.dim }}>{item.detail}</span>
                  </div>
                ))}
              </div>
            </PanelCard>

            {/* Tabbed sub-sections (like Okara's Links / Technical / AI-GEO / Checks) */}
            <PanelCard>
              <div style={{ display: 'flex', gap: 0, marginBottom: 10, borderBottom: `1px solid ${COLORS.border}` }}>
                {['overview', 'genres', 'archetypes', 'funnel'].map(tab => (
                  <button key={tab} onClick={() => setAnalyticsTab(tab)} style={{
                    background: 'transparent', border: 'none', borderBottom: analyticsTab === tab ? `2px solid ${COLORS.accent}` : '2px solid transparent',
                    color: analyticsTab === tab ? COLORS.accent : COLORS.dim,
                    padding: '6px 12px', fontSize: 10, fontWeight: analyticsTab === tab ? 700 : 400,
                    cursor: 'pointer', textTransform: 'capitalize',
                  }}>{tab}</button>
                ))}
              </div>

              {/* Overview tab */}
              {analyticsTab === 'overview' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    { label: 'Total Sessions', value: p.totalSessions, color: COLORS.cyan },
                    { label: 'Avg Score', value: p.avgScore, color: COLORS.accent },
                    { label: 'Referrals', value: u.totalReferrals, color: COLORS.purple },
                    { label: 'Retests Used', value: u.totalRetestCreditsUsed, color: COLORS.amber },
                  ].map(m => (
                    <div key={m.label} style={{
                      background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                      borderRadius: 8, padding: '8px 10px', textAlign: 'center',
                    }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: m.color, fontVariantNumeric: 'tabular-nums' }}>{m.value ?? '—'}</div>
                      <div style={{ fontSize: 9, color: COLORS.dim, marginTop: 2 }}>{m.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Genres tab */}
              {analyticsTab === 'genres' && (
                <div>
                  {p.genreCounts && Object.entries(p.genreCounts).map(([genre, count]) => {
                    const total = Object.values(p.genreCounts).reduce((a, b) => a + b, 0);
                    const pct = total ? Math.round((count / total) * 100) : 0;
                    const gc = genre === 'fps' ? COLORS.red : genre === 'moba' ? COLORS.purple : COLORS.cyan;
                    return (
                      <div key={genre} style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
                          <span style={{ color: COLORS.muted }}>{genre.toUpperCase()}</span>
                          <span style={{ color: gc, fontWeight: 700 }}>{count} ({pct}%)</span>
                        </div>
                        <div style={{ height: 4, background: COLORS.border, borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: gc, borderRadius: 2, transition: 'width 0.4s ease' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Archetypes tab */}
              {analyticsTab === 'archetypes' && (
                <div>
                  {(p.topArchetypes || []).slice(0, 8).map(([name, count]) => (
                    <div key={name} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '4px 0', borderBottom: `1px solid ${COLORS.border}`, fontSize: 11,
                    }}>
                      <span style={{ color: COLORS.muted }}>{name}</span>
                      <span style={{ color: COLORS.accent, fontWeight: 700 }}>{count}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Funnel tab */}
              {analyticsTab === 'funnel' && (
                <div>
                  {(() => {
                    if (!p.dropOff || !Object.keys(p.dropOff).length) return <div style={{ fontSize: 11, color: COLORS.dim }}>No data yet</div>;
                    const LABELS = {
                      before_first_module: 'Before 1st module', after_reaction: 'After Reaction',
                      after_aim: 'After Aim', after_tracking: 'After Tracking', after_composure: 'After Composure',
                      after_tilt: 'After Tilt',
                    };
                    const total = Object.values(p.dropOff).reduce((a, b) => a + b, 0);
                    return Object.entries(p.dropOff).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([key, count]) => {
                      const pct = total ? Math.round((count / total) * 100) : 0;
                      return (
                        <div key={key} style={{ marginBottom: 6 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 2 }}>
                            <span style={{ color: COLORS.dim }}>{LABELS[key] || key}</span>
                            <span style={{ color: COLORS.amber }}>{count} ({pct}%)</span>
                          </div>
                          <div style={{ height: 3, background: COLORS.border, borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: COLORS.amber, borderRadius: 2 }} />
                          </div>
                        </div>
                      );
                    });
                  })()}
                  {/* Challenge funnel */}
                  {(() => {
                    const c = p.challengeStats || {};
                    const convRate = c.views ? Math.round((c.accepts / c.views) * 100) : 0;
                    const completeRate = c.accepts ? Math.round((c.completes / c.accepts) * 100) : 0;
                    return (
                      <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${COLORS.border}` }}>
                        <div style={{ fontSize: 10, color: COLORS.dim, fontWeight: 600, marginBottom: 6 }}>CHALLENGE LOOP</div>
                        <div style={{ display: 'flex', gap: 8, fontSize: 10 }}>
                          <span style={{ color: COLORS.cyan }}>{c.views || 0} views</span>
                          <span style={{ color: COLORS.faint }}>&rarr;</span>
                          <span style={{ color: COLORS.amber }}>{c.accepts || 0} accepts ({convRate}%)</span>
                          <span style={{ color: COLORS.faint }}>&rarr;</span>
                          <span style={{ color: COLORS.accent }}>{c.completes || 0} done ({completeRate}%)</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </PanelCard>

            <button onClick={() => setSection('Overview')} style={{
              background: 'transparent', border: 'none',
              color: COLORS.dim, fontSize: 10, cursor: 'pointer', padding: 0,
            }}>Full analytics &rarr;</button>
          </div>

          {/* ══════════ COLUMN 3 — ACTIONS FEED ══════════ */}
          <div style={{ borderRight: `1px solid ${COLORS.border}`, padding: '16px 14px', overflowY: 'auto', maxHeight: 'calc(100vh - 48px)' }}>

            {/* Reddit Opportunities (like Okara's Reddit Opportunities) */}
            <PanelCard title="Reddit Opportunities" action={
              <span style={{ fontSize: 10, color: COLORS.dim }}>
                {cmo?.opportunities?.length || 0} found · {cmo?.lastScan ? timeAgo(new Date(cmo.lastScan).getTime()) : 'no scan'}
              </span>
            }>
              {cmoLoading && !cmo ? (
                <div style={{ fontSize: 11, color: COLORS.dim, padding: '12px 0' }}>Loading...</div>
              ) : !cmo?.opportunities?.length ? (
                <div style={{ fontSize: 11, color: COLORS.dim, padding: '12px 0' }}>No opportunities. Run a scan from terminal.</div>
              ) : (
                cmo.opportunities.filter(o => !o.archived && !o.posted).slice(0, 5).map((opp) => {
                  const urgency = opp.age < 3 ? COLORS.red : opp.age < 12 ? COLORS.amber : COLORS.dim;
                  return (
                    <div key={opp.id} style={{ padding: '8px 0', borderBottom: `1px solid ${COLORS.border}` }}>
                      <a href={opp.url} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: 11, fontWeight: 600, color: COLORS.text, marginBottom: 3, lineHeight: 1.4, display: 'block', textDecoration: 'none', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.color = COLORS.accent}
                        onMouseLeave={e => e.currentTarget.style.color = COLORS.text}
                      >
                        {opp.title} <span style={{ fontSize: 9, color: COLORS.faint }}>↗</span>
                      </a>
                      <div style={{ display: 'flex', gap: 8, fontSize: 10, marginBottom: 4 }}>
                        <span style={{ color: COLORS.cyan }}>r/{opp.sub}</span>
                        <span style={{ color: COLORS.dim }}>{opp.score}pts</span>
                        <span style={{ color: urgency }}>{opp.age}h</span>
                      </div>
                      {opp.draft ? (
                        <div style={{ fontSize: 10, color: COLORS.muted, background: COLORS.bg, borderRadius: 6, padding: '6px 8px', marginTop: 4, lineHeight: 1.5 }}>
                          {opp.draft.slice(0, 120)}...
                          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                            <CopyButton text={opp.draft} />
                            <button onClick={() => flagOpportunity(opp.id, 'posted')} style={{ background: 'transparent', border: `1px solid ${COLORS.accent}55`, color: COLORS.accent, borderRadius: 4, padding: '2px 7px', fontSize: 9, cursor: 'pointer' }}>
                              ✓ Posted
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => generateDraft(opp)} disabled={draftingId === opp.id} style={{
                            background: 'transparent', border: `1px solid ${COLORS.border}`,
                            color: COLORS.dim, borderRadius: 5, padding: '3px 8px', fontSize: 9, cursor: 'pointer',
                          }}>
                            {draftingId === opp.id ? 'Drafting...' : 'Draft'}
                          </button>
                          <button onClick={() => flagOpportunity(opp.id, 'posted')} style={{ background: 'transparent', border: `1px solid ${COLORS.accent}55`, color: COLORS.accent, borderRadius: 5, padding: '3px 8px', fontSize: 9, cursor: 'pointer' }}>
                            ✓ Posted
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              <button onClick={() => setSection('CMO')} style={{
                marginTop: 8, background: 'transparent', border: 'none',
                color: COLORS.dim, fontSize: 10, cursor: 'pointer', padding: 0,
              }}>View all &rarr;</button>
            </PanelCard>

            {/* SEO & Growth Recommendations — togglable todos saved to localStorage */}
            <PanelCard title="SEO & Growth Recommendations">
              {(() => {
                const RECS = [
                  { id: 'h1', text: 'Add H1 Tags, Structured Data & Fix Critical On-Page Gaps', priority: 'high' },
                  { id: 'compare', text: 'Create comparison pages: NeuroRank vs AimLab, vs Human Benchmark', priority: 'high' },
                  { id: 'faq', text: 'Add FAQ Schema to Blog Posts for AI/GEO visibility', priority: 'medium' },
                  { id: 'weekly', text: 'Publish weekly cognitive science content for search authority', priority: 'medium' },
                  { id: 'og', text: 'Optimize profile sharing OG meta for social backlinks', priority: 'low' },
                ];
                const doneSet = new Set(seoDone);
                return RECS.map(rec => {
                  const isDone = doneSet.has(rec.id);
                  return (
                    <div key={rec.id} onClick={() => toggleSeoRec(rec.id)} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                      padding: '7px 0', borderBottom: `1px solid ${COLORS.border}`,
                      cursor: 'pointer',
                    }}>
                      <div style={{
                        width: 12, height: 12, borderRadius: 3, marginTop: 2, flexShrink: 0,
                        border: `1px solid ${isDone ? COLORS.accent : rec.priority === 'high' ? COLORS.red : COLORS.amber}`,
                        background: isDone ? COLORS.accent : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9, color: '#000', fontWeight: 900,
                      }}>{isDone ? '✓' : ''}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, color: isDone ? COLORS.dim : COLORS.text, lineHeight: 1.4, textDecoration: isDone ? 'line-through' : 'none' }}>{rec.text}</div>
                      </div>
                    </div>
                  );
                });
              })()}
            </PanelCard>

            {/* Activities (like Okara's Activities section) */}
            <PanelCard title="Activities">
              {/* Content pipeline status */}
              {[
                { label: 'Twitter Posts', key: 'twitter_posts', color: '#1DA1F2' },
                { label: 'Reddit Content', key: 'reddit_posts', color: '#FF4500' },
                { label: 'Discord Messages', key: 'discord_messages', color: '#5865F2' },
                { label: 'YouTube Script', key: 'youtube_short', color: '#FF0000' },
              ].map(item => (
                <div key={item.label} onClick={() => setSection('Marketing')} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '5px 0', borderBottom: `1px solid ${COLORS.border}`, fontSize: 11,
                  cursor: 'pointer',
                }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                  <span style={{ color: COLORS.muted, flex: 1 }}>{item.label}</span>
                  <span style={{ fontSize: 9, color: content?.[item.key] ? COLORS.accent : COLORS.dim }}>
                    {content?.[item.key] ? 'Content ready →' : 'Pending →'}
                  </span>
                </div>
              ))}

              {/* Divider */}
              <div style={{ height: 1, background: COLORS.border, margin: '8px 0' }} />

              {/* Agent runs — clickable to Agents tab */}
              {(agentsData?.runs || []).slice(0, 6).map((run, i) => {
                const meta = AGENT_META[run.agent_type] || { label: run.agent_type, color: COLORS.dim };
                const statusColor = run.status === 'completed' ? COLORS.accent : run.status === 'failed' ? COLORS.red : COLORS.cyan;
                return (
                  <div key={i} onClick={() => setSection('Agents')} className="nr-log-row" style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '4px 0', borderBottom: `1px solid ${COLORS.border}`, fontSize: 10,
                    cursor: 'pointer',
                  }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
                    <span style={{ color: COLORS.muted, flex: 1 }}>{meta.label}</span>
                    <span style={{ color: statusColor, fontWeight: 600, textTransform: 'uppercase', fontSize: 9 }}>{run.status}</span>
                    <span style={{ color: COLORS.faint, fontSize: 9 }}>{timeAgo(run.started_at)}</span>
                  </div>
                );
              })}

              {/* Mentions — each links to the source URL */}
              {(cmo?.mentions || []).length > 0 && (
                <>
                  <div style={{ height: 1, background: COLORS.border, margin: '8px 0' }} />
                  <div style={{ fontSize: 9, color: COLORS.dim, fontWeight: 600, marginBottom: 4 }}>MENTIONS</div>
                  {(cmo?.mentions || []).slice(0, 3).map((m, i) => {
                    const sentColor = { positive: '#22c55e', negative: '#ef4444', question: '#f59e0b', neutral: '#555' }[m.sentiment] || '#555';
                    return (
                      <a key={i} href={m.url} target="_blank" rel="noopener noreferrer" style={{
                        padding: '4px 0', borderBottom: `1px solid ${COLORS.border}`, fontSize: 10,
                        display: 'block', textDecoration: 'none', cursor: 'pointer',
                      }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <span>{m.source === 'twitter' ? '\uD83D\uDC26' : '\uD83D\uDFE0'}</span>
                          <span style={{ color: COLORS.muted, flex: 1, fontSize: 10 }}>{(m.title || m.body || '').slice(0, 50)} ↗</span>
                          <span style={{ color: sentColor, fontSize: 8, fontWeight: 700, textTransform: 'uppercase' }}>{m.sentiment}</span>
                        </div>
                      </a>
                    );
                  })}
                </>
              )}
              <button onClick={() => setSection('CMO')} style={{
                marginTop: 8, background: 'transparent', border: 'none',
                color: COLORS.dim, fontSize: 10, cursor: 'pointer', padding: 0,
              }}>View all &rarr;</button>
            </PanelCard>
          </div>

          {/* ══════════ COLUMN 4 — TERMINAL + CHAT ══════════ */}
          <div style={{ padding: '16px 14px', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 48px)' }}>

            {/* System Health Banner */}
            {(() => {
              const h = agentsData?.health;
              if (!h?.lastCheck) return null;
              const sev = h.lastSeverity || 'ok';
              const sevColor = sev === 'critical' ? COLORS.red : sev === 'warning' ? COLORS.amber : COLORS.accent;
              return (
                <PanelCard>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%', background: sevColor,
                      animation: sev !== 'ok' ? 'nr-pulse-led 1.2s ease infinite' : 'none',
                    }} />
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 800, color: sevColor, letterSpacing: 1.5 }}>
                        {sev === 'ok' ? 'ALL SYSTEMS OPERATIONAL' : sev === 'warning' ? 'WARNING' : 'CRITICAL'}
                      </div>
                      <div style={{ fontSize: 10, color: COLORS.dim }}>{h.lastSummary?.slice(0, 80)}</div>
                    </div>
                    <span style={{ fontSize: 9, color: COLORS.faint, marginLeft: 'auto' }}>{timeAgo(h.lastCheck)}</span>
                  </div>
                </PanelCard>
              );
            })()}

            {/* Quick Actions */}
            <PanelCard title="Quick Actions">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {[
                  { label: 'Run Scan', onClick: runScan, disabled: cmoScanning, color: COLORS.cyan },
                  { label: 'AI Monitor', onClick: runAgentMonitor, disabled: agentMonitoring, color: COLORS.purple },
                  { label: 'Scan Mentions', onClick: runMentionMonitorAgent, disabled: mentionMonitoring, color: '#22d3ee' },
                  { label: 'Check Health', onClick: checkNow, disabled: checkingNow, color: COLORS.accent },
                ].map(btn => (
                  <button key={btn.label} onClick={btn.onClick} disabled={btn.disabled} className="nr-action-btn" style={{
                    background: btn.disabled ? `${btn.color}08` : `${btn.color}18`,
                    border: `1px solid ${btn.color}44`,
                    color: btn.disabled ? COLORS.dim : btn.color,
                    borderRadius: 8, padding: '8px 0', fontSize: 10,
                    fontWeight: 600, cursor: btn.disabled ? 'not-allowed' : 'pointer',
                    opacity: btn.disabled ? 0.5 : 1,
                  }}>
                    {btn.disabled ? '...' : btn.label}
                  </button>
                ))}
              </div>
            </PanelCard>

            {/* Mission Control Mini */}
            {agentsData && (() => {
              const totalWeekRuns = Object.values(agentsData.summary || {}).reduce((a, s) => a + (s.weekRuns || 0), 0);
              const totalFailed = Object.values(agentsData.summary || {}).reduce((a, s) => a + (s.failed || 0), 0);
              const days = Array.from({ length: 7 }, (_, i) => {
                const d = new Date(Date.now() - (6 - i) * 24 * 60 * 60 * 1000);
                return d.toISOString().slice(0, 10);
              });
              const totalWeekCost = days.map(day => {
                const dc = agentsData.dailyCost?.[day] || {};
                return Object.values(dc).reduce((a, v) => a + v, 0);
              }).reduce((a, v) => a + v, 0);
              const costPct = Math.min(totalWeekCost / 5.0, 1);
              const costColor = costPct > 0.8 ? COLORS.red : costPct > 0.5 ? COLORS.amber : COLORS.accent;
              const successRate = totalWeekRuns > 0 ? Math.round(((totalWeekRuns - totalFailed) / totalWeekRuns) * 100) : 100;
              return (
                <PanelCard title="Mission Control">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 8 }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.purple }}>{totalWeekRuns}</div>
                      <div style={{ fontSize: 8, color: COLORS.dim }}>RUNS/WK</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: costColor }}>${totalWeekCost.toFixed(3)}</div>
                      <div style={{ fontSize: 8, color: COLORS.dim }}>COST</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: successRate === 100 ? COLORS.accent : COLORS.amber }}>{successRate}%</div>
                      <div style={{ fontSize: 8, color: COLORS.dim }}>SUCCESS</div>
                    </div>
                  </div>
                  <div style={{ height: 4, background: COLORS.border, borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.max(costPct * 100, 1)}%`, height: '100%', background: costColor, borderRadius: 2, transition: 'width 0.4s ease' }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: COLORS.faint, marginTop: 4 }}>
                    <span>$0</span>
                    <span>${totalWeekCost.toFixed(3)} / $5.00</span>
                  </div>
                </PanelCard>
              );
            })()}

            {/* ── AI CHAT ── */}
            <PanelCard title="NeuroRank CMO" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div style={{
                flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8,
                minHeight: 200, maxHeight: 'calc(100vh - 520px)', padding: '4px 0',
              }}>
                {chatMessages.map((msg, i) => (
                  <div key={i} style={{
                    alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    background: msg.role === 'user' ? `${COLORS.accent}22` : COLORS.bg,
                    border: `1px solid ${msg.role === 'user' ? `${COLORS.accent}44` : COLORS.border}`,
                    borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                    padding: '8px 12px', fontSize: 12, color: COLORS.muted, lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {msg.text}
                  </div>
                ))}
                {chatLoading && (
                  <div style={{ alignSelf: 'flex-start', fontSize: 11, color: COLORS.dim, padding: '4px 0' }}>
                    <span style={{ animation: 'nr-pulse 1s ease infinite' }}>Thinking...</span>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input
                  className="nr-chat-input"
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChat(chatInput)}
                  placeholder="Ask anything..."
                  style={{
                    flex: 1, background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                    borderRadius: 8, padding: '8px 12px', color: COLORS.text,
                    fontSize: 12, outline: 'none',
                  }}
                />
                <button onClick={() => sendChat(chatInput)} disabled={chatLoading || !chatInput.trim()} style={{
                  background: chatLoading || !chatInput.trim() ? COLORS.card : COLORS.accent,
                  border: 'none', borderRadius: 8, padding: '8px 14px',
                  color: chatLoading || !chatInput.trim() ? COLORS.dim : '#000',
                  fontWeight: 700, fontSize: 11, cursor: chatLoading || !chatInput.trim() ? 'not-allowed' : 'pointer',
                }}>Send</button>
              </div>
            </PanelCard>
          </div>
        </div>
      )}

      {/* ─── SECONDARY VIEWS ─── */}
      {section && (
        <div style={{ padding: '20px 32px', maxWidth: 1200, margin: '0 auto' }}>
          <button onClick={() => setSection(null)} style={{
            background: 'transparent', border: `1px solid ${COLORS.border}`,
            color: COLORS.dim, borderRadius: 8, padding: '6px 16px', fontSize: 12,
            cursor: 'pointer', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            &larr; Dashboard
          </button>

        {/* OVERVIEW */}
        {section === 'Overview' && (
          <>
            <Section title="Platform — Sessions">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <StatCard label="Total Sessions" value={p.totalSessions} color="#00d4ff" />
                <StatCard label="Completed" value={p.completedSessions} sub={`${p.completionRate}% rate`} color="#10b981" />
                <StatCard label="Today" value={p.today} color="#a855f7" />
                <StatCard label={<span>This Week <WoWDelta curr={p.wow?.sessions?.curr} prev={p.wow?.sessions?.prev} inline /></span>} value={p.thisWeek} color="#f59e0b" />
                <StatCard label={<span>Completed 7d <WoWDelta curr={p.wow?.completed?.curr} prev={p.wow?.completed?.prev} inline /></span>} value={p.wow?.completed?.curr || 0} color="#10b981" />
                <StatCard label="Avg Score" value={p.avgScore} color="#00d4ff" />
              </div>
            </Section>

            <Section title="Genre Breakdown">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {p.genreCounts && Object.entries(p.genreCounts).map(([genre, count]) => (
                  <StatCard key={genre} label={genre.toUpperCase()} value={count}
                    color={genre === 'fps' ? '#ef4444' : genre === 'moba' ? '#a855f7' : '#00d4ff'} />
                ))}
              </div>
            </Section>

            <Section title="Top Archetypes">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {(p.topArchetypes || []).map(([name, count]) => (
                  <div key={name} style={{
                    background: '#0d1117', border: '1px solid #1a2a3a',
                    borderRadius: 8, padding: '8px 14px', fontSize: 13,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <span style={{ color: '#fff' }}>{name}</span>
                    <span style={{ color: '#10b981', fontWeight: 700 }}>{count}</span>
                  </div>
                ))}
              </div>
            </Section>

            <Section title={`Drop-off — ${p.abandonedCount ?? 0} abandoned sessions`}>
              {!p.dropOff || !Object.keys(p.dropOff).length ? (
                <div style={{ fontSize: 13, color: '#444' }}>No abandoned session data yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(() => {
                    const ORDER = ['before_first_module','after_reaction','after_aim','after_sequencing','after_tracking','after_memory','after_flicker','after_composure','after_tilt'];
                    const LABELS = {
                      before_first_module: 'Never started a module',
                      after_reaction: 'After Reaction',
                      after_aim: 'After Aim',
                      after_sequencing: 'After Sequencing',
                      after_tracking: 'After Tracking',
                      after_memory: 'After Memory',
                      after_flicker: 'After Flicker',
                      after_composure: 'After Composure',
                      after_tilt: 'After Tilt (last module)',
                    };
                    const total = Object.values(p.dropOff).reduce((a, b) => a + b, 0);
                    const sorted = [...ORDER.filter(k => p.dropOff[k]), ...Object.keys(p.dropOff).filter(k => !ORDER.includes(k))];
                    return sorted.map(key => {
                      const count = p.dropOff[key];
                      const pct = total ? Math.round((count / total) * 100) : 0;
                      const isEarly = key === 'before_first_module' || key === 'after_reaction';
                      const moduleKey = key === 'before_first_module' ? 'none' : key.replace('after_', '');
                      return (
                        <div
                          key={key}
                          onClick={() => openFunnelModule(moduleKey)}
                          title="Click to see who dropped here and send bulk retake email"
                          style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '4px 6px', borderRadius: 6, transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#111a24'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <div style={{ width: 180, fontSize: 12, color: '#aaa', flexShrink: 0 }}>
                            {LABELS[key] || key}
                          </div>
                          <div style={{ flex: 1, background: '#1a1a1a', borderRadius: 4, height: 10, overflow: 'hidden' }}>
                            <div style={{
                              width: `${pct}%`, height: '100%', borderRadius: 4,
                              background: isEarly ? '#ef4444' : '#f59e0b',
                            }} />
                          </div>
                          <div style={{ fontSize: 12, color: isEarly ? '#ef4444' : '#f59e0b', width: 80, textAlign: 'right' }}>
                            {count} ({pct}%) →
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </Section>

            <Section title={`Abandoned Sessions${abandoned ? ` — ${abandoned.length}` : ''}`}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <button onClick={loadAbandoned} disabled={abandonedLoading} style={{ background: 'transparent', border: '1px solid #333', color: '#888', borderRadius: 6, padding: '5px 12px', fontSize: 11, cursor: 'pointer' }}>
                  {abandonedLoading ? 'Loading…' : 'Refresh'}
                </button>
                <CsvButton rows={abandoned || []} filename={`abandoned-sessions-${new Date().toISOString().slice(0,10)}.csv`} columns={['sessionId', 'email', 'playerTag', 'genre', 'modulesCompleted', 'modulesTotal', 'lastModule', 'createdAt', 'ageHours']} />
              </div>
              {!abandoned ? (
                <div style={{ fontSize: 13, color: '#444' }}>Click Refresh to load.</div>
              ) : !abandoned.length ? (
                <div style={{ fontSize: 13, color: '#444' }}>No abandoned sessions.</div>
              ) : (
                <div style={{ background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 10, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #1a1a1a' }}>
                        {['Session', 'User', 'Genre', 'Progress', 'Last Module', 'Age', 'Actions'].map(h => (
                          <th key={h} style={{ padding: '8px 12px', color: '#555', fontWeight: 600, textAlign: 'left' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {abandoned.slice(0, 30).map(s => {
                        const inspecting = sessionActionId === `${s.sessionId}:inspect`;
                        const killing = sessionActionId === `${s.sessionId}:kill`;
                        const autocompleting = sessionActionId === `${s.sessionId}:autocomplete`;
                        return (
                          <tr key={s.sessionId} style={{ borderBottom: '1px solid #111' }}>
                            <td style={{ padding: '7px 12px', color: '#00d4ff', fontFamily: 'monospace', fontSize: 10 }}>{(s.sessionId || '').slice(0, 10)}…</td>
                            <td style={{ padding: '7px 12px', color: '#ccc' }}>{s.email || s.playerTag || '—'}</td>
                            <td style={{ padding: '7px 12px', color: '#a855f7' }}>{(s.genre || '').toUpperCase()}</td>
                            <td style={{ padding: '7px 12px', color: '#f59e0b' }}>{s.modulesCompleted}/{s.modulesTotal}</td>
                            <td style={{ padding: '7px 12px', color: '#888' }}>{s.lastModule || 'none'}</td>
                            <td style={{ padding: '7px 12px', color: '#555' }}>{s.ageHours}h</td>
                            <td style={{ padding: '7px 12px' }}>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <button onClick={() => sessionAction(s.sessionId, 'inspect')} disabled={inspecting} style={{ background: 'transparent', border: '1px solid #00d4ff55', color: '#00d4ff', borderRadius: 4, padding: '2px 7px', fontSize: 10, cursor: 'pointer' }}>
                                  {inspecting ? '…' : 'Inspect'}
                                </button>
                                <button onClick={() => sessionAction(s.sessionId, 'autocomplete')} disabled={autocompleting} title="Fill with dummy data + complete (for testing)" style={{ background: 'transparent', border: '1px solid #f59e0b55', color: '#f59e0b', borderRadius: 4, padding: '2px 7px', fontSize: 10, cursor: 'pointer' }}>
                                  {autocompleting ? '…' : 'Auto'}
                                </button>
                                <button onClick={() => sessionAction(s.sessionId, 'kill')} disabled={killing} style={{ background: 'transparent', border: '1px solid #ef444455', color: '#ef4444', borderRadius: 4, padding: '2px 7px', fontSize: 10, cursor: 'pointer' }}>
                                  {killing ? '…' : 'Kill'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>

            <Section title="Challenge Viral Loop">
              {(() => {
                const c = p.challengeStats || {};
                const items = [
                  { label: 'Views (all time)', val: c.views || 0, color: '#00d4ff' },
                  { label: 'Accepts', val: c.accepts || 0, color: '#f59e0b' },
                  { label: 'Completes', val: c.completes || 0, color: '#10b981' },
                  { label: 'Views (7d)', val: c.weekViews || 0, color: '#00d4ff' },
                  { label: 'Accepts (7d)', val: c.weekAccepts || 0, color: '#f59e0b' },
                  { label: 'Completes (7d)', val: c.weekCompletes || 0, color: '#10b981' },
                ];
                const convRate = c.views ? Math.round((c.accepts / c.views) * 100) : 0;
                const completeRate = c.accepts ? Math.round((c.completes / c.accepts) * 100) : 0;
                return (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
                      {items.map(({ label, val, color }) => (
                        <div key={label} style={{ background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
                          <div style={{ fontSize: 28, fontWeight: 800, color }}>{val}</div>
                          <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>{label}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 12, color: '#888' }}>
                      View→Accept: <span style={{ color: '#f59e0b', fontWeight: 700 }}>{convRate}%</span>
                      {' · '}
                      Accept→Complete: <span style={{ color: '#10b981', fontWeight: 700 }}>{completeRate}%</span>
                    </div>
                  </div>
                );
              })()}
            </Section>

            <Section title="Recent Profiles">
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <CsvButton rows={p.recentProfiles || []} filename={`profiles-${new Date().toISOString().slice(0,10)}.csv`} columns={['shareable_id', 'playerTag', 'genre', 'archetype', 'overall_score', 'created_at']} />
              </div>
              <div style={{
                background: '#0d1117', border: '1px solid #1a1a1a',
                borderRadius: 10, overflow: 'hidden',
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1a1a1a' }}>
                      {['Profile ID', 'Genre', 'Archetype', 'Score', 'Created', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', color: '#555', fontWeight: 600, textAlign: 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(p.recentProfiles || []).map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #111' }}>
                        <td style={{ padding: '9px 14px', color: '#00d4ff', fontFamily: 'monospace', fontSize: 11 }}>
                          <a href={`/profile/${row.shareable_id}`} target="_blank" rel="noreferrer"
                            style={{ color: '#00d4ff', textDecoration: 'none' }}>
                            {row.shareable_id?.slice(0, 12)}…
                          </a>
                        </td>
                        <td style={{ padding: '9px 14px', color: '#aaa' }}>{row.genre?.toUpperCase()}</td>
                        <td style={{ padding: '9px 14px', color: '#f59e0b' }}>{row.archetype}</td>
                        <td style={{ padding: '9px 14px', color: '#10b981' }}>{row.overall_score}</td>
                        <td style={{ padding: '9px 14px', color: '#555' }}>
                          {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ padding: '9px 14px' }}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={() => profileAction(row.shareable_id, 'flag')} title="Flag for review" style={{ background: 'transparent', border: '1px solid #f59e0b55', color: '#f59e0b', borderRadius: 4, padding: '2px 7px', fontSize: 10, cursor: 'pointer' }}>Flag</button>
                            <button onClick={() => profileAction(row.shareable_id, 'delete')} title="Permanently delete" style={{ background: 'transparent', border: '1px solid #ef444455', color: '#ef4444', borderRadius: 4, padding: '2px 7px', fontSize: 10, cursor: 'pointer' }}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          </>
        )}

        {/* USERS */}
        {section === 'Users' && (
          <>
            <Section title="User Stats">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <StatCard label="Total Users" value={u.total} color="#00d4ff" />
                <StatCard label="Verified" value={u.verified} sub={`${u.total ? Math.round((u.verified / u.total) * 100) : 0}%`} color="#10b981" />
                <StatCard label="Referrals Given" value={u.totalReferrals} color="#a855f7" />
                <StatCard label="Retests Used" value={u.totalRetestCreditsUsed} color="#f59e0b" />
              </div>
            </Section>

            <Section title="Recent Signups">
              {userActionMsg && (
                <div style={{ fontSize: 11, color: userActionMsg.includes('failed') || userActionMsg.includes('error') ? '#ef4444' : '#10b981', marginBottom: 10 }}>
                  {userActionMsg}
                </div>
              )}

              {/* Bulk action bar */}
              {selectedUserIds.size > 0 && (
                <div style={{ background: '#00d4ff11', border: '1px solid #00d4ff44', borderRadius: 8, padding: '10px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: '#00d4ff', fontWeight: 700 }}>{selectedUserIds.size} selected</span>
                  <button onClick={() => bulkUserAction('grant_retest')} disabled={bulkRunning} style={{ background: 'transparent', border: '1px solid #f59e0b55', color: '#f59e0b', borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>+1 Retest All</button>
                  <button onClick={() => bulkUserAction('ban')} disabled={bulkRunning} style={{ background: 'transparent', border: '1px solid #f59e0b55', color: '#f59e0b', borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>Ban All</button>
                  <button onClick={() => bulkUserAction('unban')} disabled={bulkRunning} style={{ background: 'transparent', border: '1px solid #10b98155', color: '#10b981', borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>Unban All</button>
                  <button onClick={() => bulkUserAction('delete')} disabled={bulkRunning} style={{ background: 'transparent', border: '1px solid #ef444455', color: '#ef4444', borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>Delete All</button>
                  <button onClick={() => setSelectedUserIds(new Set())} style={{ background: 'transparent', border: '1px solid #333', color: '#888', borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer', marginLeft: 'auto' }}>Clear</button>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <CsvButton rows={u.recentUsers || []} filename={`users-${new Date().toISOString().slice(0,10)}.csv`} columns={['id', 'email', 'playerTag', 'is_verified', 'is_admin', 'referral_count', 'retest_credits', 'claimed_count', 'created_at']} />
              </div>

              <div style={{
                background: '#0d1117', border: '1px solid #1a1a1a',
                borderRadius: 10, overflow: 'hidden',
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1a1a1a' }}>
                      <th style={{ padding: '10px 14px', width: 30 }}>
                        <input type="checkbox"
                          checked={selectedUserIds.size > 0 && selectedUserIds.size === (u.recentUsers || []).filter(r => r.id).length}
                          onChange={e => {
                            if (e.target.checked) setSelectedUserIds(new Set((u.recentUsers || []).filter(r => r.id).map(r => r.id)));
                            else setSelectedUserIds(new Set());
                          }}
                        />
                      </th>
                      {['Email', 'Verified', 'Admin', 'Refs', 'Retests', 'Claimed', 'Joined', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', color: '#555', fontWeight: 600, textAlign: 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(u.recentUsers || []).map((row, i) => {
                      const promoting = userActionId === `${row.id}:promote`;
                      const granting = userActionId === `${row.id}:retest`;
                      const resending = userActionId === `${row.id}:resend`;
                      const selected = selectedUserIds.has(row.id);
                      return (
                        <tr key={row.id || i} style={{ borderBottom: '1px solid #111', background: selected ? '#00d4ff0a' : 'transparent' }}>
                          <td style={{ padding: '9px 14px' }}>
                            {row.id && (
                              <input type="checkbox" checked={selected} onChange={e => {
                                setSelectedUserIds(prev => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(row.id); else next.delete(row.id);
                                  return next;
                                });
                              }} />
                            )}
                          </td>
                          <td style={{ padding: '9px 14px', color: '#ccc' }}>{row.email}</td>
                          <td style={{ padding: '9px 14px' }}>
                            <span style={{ color: row.is_verified ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                              {row.is_verified ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td style={{ padding: '9px 14px' }}>
                            {row.is_admin ? <span style={{ color: '#00d4ff', fontWeight: 700 }}>YES</span> : <span style={{ color: '#333' }}>—</span>}
                          </td>
                          <td style={{ padding: '9px 14px', color: '#a855f7' }}>{row.referral_count || 0}</td>
                          <td style={{ padding: '9px 14px', color: '#f59e0b' }}>{row.retest_credits || 0}</td>
                          <td style={{ padding: '9px 14px', color: '#10b981' }}>{row.claimed_count || 0}</td>
                          <td style={{ padding: '9px 14px', color: '#555' }}>
                            {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                          </td>
                          <td style={{ padding: '9px 14px' }}>
                            {row.id ? (
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {!row.is_admin && (
                                  <button onClick={() => userAction(row.id, 'promote')} disabled={promoting} title="Promote to admin" style={{ background: 'transparent', border: '1px solid #00d4ff55', color: '#00d4ff', borderRadius: 4, padding: '3px 8px', fontSize: 10, cursor: 'pointer' }}>
                                    {promoting ? '…' : '+Admin'}
                                  </button>
                                )}
                                <button onClick={() => userAction(row.id, 'retest')} disabled={granting} title="Grant +1 retest credit" style={{ background: 'transparent', border: '1px solid #f59e0b55', color: '#f59e0b', borderRadius: 4, padding: '3px 8px', fontSize: 10, cursor: 'pointer' }}>
                                  {granting ? '…' : '+Retest'}
                                </button>
                                {row.claimed_count > 0 && (
                                  <button onClick={() => userAction(row.id, 'resend')} disabled={resending} title="Resend combine email for latest profile" style={{ background: 'transparent', border: '1px solid #10b98155', color: '#10b981', borderRadius: 4, padding: '3px 8px', fontSize: 10, cursor: 'pointer' }}>
                                    {resending ? '…' : '✉ Resend'}
                                  </button>
                                )}
                                <button onClick={() => userDestructiveAction(row.id, 'ban', row.email)} disabled={userActionId === `${row.id}:ban`} title="Ban user" style={{ background: 'transparent', border: '1px solid #f59e0b55', color: '#f59e0b', borderRadius: 4, padding: '3px 8px', fontSize: 10, cursor: 'pointer' }}>
                                  Ban
                                </button>
                                <button onClick={() => userDestructiveAction(row.id, 'delete', row.email)} disabled={userActionId === `${row.id}:delete`} title="Permanently delete user" style={{ background: 'transparent', border: '1px solid #ef444455', color: '#ef4444', borderRadius: 4, padding: '3px 8px', fontSize: 10, cursor: 'pointer' }}>
                                  Delete
                                </button>
                              </div>
                            ) : <span style={{ color: '#333', fontSize: 10 }}>—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Section>
          </>
        )}

        {/* CHECKLIST */}
        {section === 'Checklist' && <DailyChecklist />}

        {/* GROWTH */}
        {section === 'Growth' && <GrowthTracker stats={stats} />}

        {/* MARKETING */}
        {section === 'Marketing' && (
          <>
            <Section title="Attribution Funnel">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: COLORS.dim }}>Window:</span>
                {[7, 14, 30].map(n => (
                  <button key={n} onClick={() => { setAttributionDays(n); setAttribution(null); loadAttribution(n); }}
                    style={{
                      background: attributionDays === n ? `${COLORS.accent}22` : 'transparent',
                      border: `1px solid ${attributionDays === n ? COLORS.accent : COLORS.border}`,
                      color: attributionDays === n ? COLORS.accent : COLORS.muted,
                      borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer',
                    }}>{n}d</button>
                ))}
                <button onClick={() => { setAttribution(null); loadAttribution(attributionDays); }}
                  disabled={attributionLoading}
                  style={{ background: 'transparent', border: `1px solid ${COLORS.border}`, color: COLORS.muted, borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', marginLeft: 'auto' }}>
                  {attributionLoading ? 'Loading…' : 'Refresh'}
                </button>
              </div>

              {attribution?.error && (
                <div style={{ color: COLORS.red, fontSize: 12 }}>{attribution.error}</div>
              )}

              {attribution && !attribution.error && (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                    <StatCard label="Attributed clicks" value={attribution.totals.attributedClicks} sub={`${attribution.days}d`} color={COLORS.cyan} />
                    <StatCard label="Attributed starts" value={attribution.totals.attributedStarts} sub={`of ${attribution.totals.totalStartsInWindow} total`} color={COLORS.accent} />
                    <StatCard label="Coverage" value={`${attribution.totals.coveragePct}%`} sub="Phase 1 target: 50%" color={attribution.totals.coveragePct >= 50 ? COLORS.accent : COLORS.amber} />
                    <StatCard label="Attributed completes" value={attribution.totals.attributedCompletes} sub={`of ${attribution.totals.totalCompletesInWindow}`} color={COLORS.purple} />
                    <StatCard label="Attributed users" value={attribution.totals.attributedUsers} sub="account creates" color={COLORS.cyan} />
                    <StatCard label="Attributed paid" value={attribution.totals.attributedPaid} sub="any paid plan" color={COLORS.amber} />
                  </div>

                  <div style={{ background: '#0d1117', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr repeat(5, 1fr)', gap: 8, fontSize: 10, color: COLORS.dim, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: `1px solid ${COLORS.border}` }}>
                      <span>Platform</span><span>Clicks</span><span>Starts</span><span>Completes</span><span>Users</span><span>Paid</span>
                    </div>
                    {attribution.perPlatform.length === 0 && (
                      <div style={{ padding: '14px 0', fontSize: 12, color: COLORS.dim }}>
                        No attributed clicks in window. Confirm bio links use <code style={{ color: COLORS.cyan }}>/v?p=&lt;platform&gt;&amp;d=YYYY-MM-DD&amp;v=&lt;slug&gt;&amp;dest=/</code>.
                      </div>
                    )}
                    {attribution.perPlatform.map(p => (
                      <div key={p.platform} style={{ display: 'grid', gridTemplateColumns: '1.5fr repeat(5, 1fr)', gap: 8, padding: '8px 0', fontSize: 12, borderBottom: `1px solid ${COLORS.border}` }}>
                        <span style={{ color: COLORS.text, fontWeight: 600 }}>{p.platform}</span>
                        <span style={{ color: COLORS.cyan }}>{p.clicks}</span>
                        <span style={{ color: COLORS.accent }}>{p.starts}</span>
                        <span style={{ color: COLORS.purple }}>{p.completes}</span>
                        <span style={{ color: COLORS.text }}>{p.users}</span>
                        <span style={{ color: COLORS.amber }}>{p.paid}</span>
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: 8, fontSize: 10, color: COLORS.dim }}>
                    Source: <code style={{ color: COLORS.cyan }}>video_attribution</code> joined to <code style={{ color: COLORS.cyan }}>sessions</code> + <code style={{ color: COLORS.cyan }}>user_profiles</code>. Coverage = attributed starts / total starts in window. Phase 1 charter: <code style={{ color: COLORS.cyan }}>docs/growth/CHARTER.md</code>.
                  </div>
                </>
              )}

              <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 10, color: COLORS.dim, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
                  Log a manual Reddit post
                </div>
                <div style={{ fontSize: 10, color: COLORS.dim, marginBottom: 10 }}>
                  Use this for personal-account posts that did not go through CMO. Subreddit and thread id are inferred from the URL.
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={logPostUrl}
                    onChange={e => setLogPostUrl(e.target.value)}
                    placeholder="https://reddit.com/r/.../comments/..."
                    style={{ flex: '2 1 300px', minWidth: 260, background: '#0d1117', border: `1px solid ${COLORS.border}`, color: COLORS.text, borderRadius: 6, padding: '6px 10px', fontSize: 11, fontFamily: 'inherit' }}
                  />
                  <select
                    value={logPostAccount}
                    onChange={e => setLogPostAccount(e.target.value)}
                    style={{ background: '#0d1117', border: `1px solid ${COLORS.border}`, color: COLORS.text, borderRadius: 6, padding: '6px 10px', fontSize: 11 }}
                  >
                    <option value="personal">personal</option>
                    <option value="neurorank">neurorank</option>
                  </select>
                  <input
                    type="text"
                    value={logPostAngle}
                    onChange={e => setLogPostAngle(e.target.value)}
                    placeholder="angle (optional)"
                    style={{ flex: '1 1 200px', minWidth: 160, background: '#0d1117', border: `1px solid ${COLORS.border}`, color: COLORS.text, borderRadius: 6, padding: '6px 10px', fontSize: 11, fontFamily: 'inherit' }}
                  />
                  <button
                    onClick={submitManualPost}
                    disabled={logPostBusy || !logPostUrl.trim()}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${COLORS.accent}55`,
                      color: COLORS.accent,
                      borderRadius: 6,
                      padding: '6px 14px',
                      fontSize: 11,
                      cursor: logPostBusy || !logPostUrl.trim() ? 'not-allowed' : 'pointer',
                      opacity: logPostBusy || !logPostUrl.trim() ? 0.5 : 1,
                    }}
                  >
                    {logPostBusy ? 'Logging…' : 'Log post'}
                  </button>
                </div>
              </div>
            </Section>

            <Section title="Content Pipeline">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <StatCard label="Daily Generator" value="6:30 AM" sub="Runs locally via Task Scheduler" color="#10b981" />
                <StatCard label="Feature Post" value="1/day" sub="Thread / carousel / card — rotates by day" color="#7b61ff" />
                <StatCard label="Videos" value="On demand" sub="Rendered locally — upload manually" color="#a855f7" />
                <StatCard label="Discord" value="Auto" sub="Bot posts generated messages" color="#5865f2" />
              </div>
            </Section>

            <Section title="What's Automated vs Manual">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { label: 'Content generation', status: 'auto', note: 'Generates all content daily at 6:30am via Task Scheduler (npm run go)' },
                  { label: 'Content sync to dashboard', status: 'auto', note: 'Uploads to Railway after generation — visible in Content tab' },
                  { label: 'Discord', status: 'auto', note: 'Bot posts messages to configured channels' },
                  { label: 'Twitter / X', status: 'manual', note: 'Text posts + one feature post (thread/card) — paste manually' },
                  { label: 'Instagram', status: 'manual', note: 'Carousel slides or image card — post manually with caption from Content tab' },
                  { label: 'TikTok', status: 'manual', note: 'Short videos rendered locally — upload manually' },
                  { label: 'Reddit', status: 'manual', note: '1 post/day to r/neurorank — paste manually' },
                ].map(({ label, status, note }) => (
                  <div key={label} style={{
                    background: '#0d1117', border: '1px solid #1a1a1a',
                    borderRadius: 8, padding: '12px 16px',
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      background: status === 'auto' ? '#10b981' : '#f59e0b',
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: '#fff' }}>{label}</div>
                      <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>{note}</div>
                    </div>
                    <div style={{
                      fontSize: 11, padding: '3px 8px', borderRadius: 4,
                      background: status === 'auto' ? '#10b98122' : '#f59e0b22',
                      color: status === 'auto' ? '#10b981' : '#f59e0b',
                      border: `1px solid ${status === 'auto' ? '#10b98133' : '#f59e0b33'}`,
                    }}>
                      {status === 'auto' ? 'Auto' : 'Manual'}
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Local Commands">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { label: 'Generate all content + sync to dashboard', cmd: 'cd marketing-automation && npm run go' },
                  { label: 'Post to Discord', cmd: 'cd marketing-automation && npm run post:discord' },
                  { label: 'Render videos (on demand)', cmd: 'cd marketing-automation && npm run video:all' },
                ].map(({ label, cmd }) => (
                  <div key={label} style={{
                    background: '#0d1117', border: '1px solid #1a1a1a',
                    borderRadius: 8, padding: '10px 16px',
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: '#aaa', marginBottom: 3 }}>{label}</div>
                      <code style={{ fontSize: 11, color: '#00d4ff' }}>{cmd}</code>
                    </div>
                    <CopyButton text={cmd} />
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Today's Content">
              {content?.date && (() => {
                const d = new Date(content.date + 'T12:00:00');
                const dow = d.getDay();
                const SCHEDULES = {
                  0: [{ time: '11am', platform: 'Twitter/X', action: 'Weekly roundup thread' }, { time: '3pm', platform: 'YouTube', action: 'Weekly highlight Short' }],
                  1: [{ time: '—', platform: 'TikTok + IG + YT', action: 'Video posts' }, { time: '—', platform: 'Twitter/X', action: '2 tweets' }, { time: '4pm', platform: 'Newsletter', action: 'Weekly newsletter' }, { time: '8:30pm', platform: 'Discord', action: 'Weekly challenge' }],
                  2: [{ time: '—', platform: 'TikTok + IG + YT', action: 'Video posts' }, { time: '—', platform: 'Twitter/X', action: '2 tweets' }, { time: '12pm', platform: 'Twitter/X', action: 'Thread' }],
                  3: [{ time: '—', platform: 'TikTok + IG + YT', action: 'Video posts' }, { time: '—', platform: 'Twitter/X', action: '2 tweets' }, { time: '5pm', platform: 'Twitter/X', action: 'Poll' }, { time: '9pm', platform: 'Discord', action: 'Game night' }],
                  4: [{ time: '—', platform: 'TikTok + IG + YT', action: 'Video posts' }, { time: '—', platform: 'Twitter/X', action: '2 tweets' }, { time: '2pm', platform: 'Reddit', action: 'Data insight post' }],
                  5: [{ time: '—', platform: 'TikTok + IG + YT', action: 'Video posts' }, { time: '—', platform: 'Twitter/X', action: '3 tweets' }, { time: '5pm', platform: 'TikTok', action: 'Bonus viral attempt' }, { time: '8pm', platform: 'Discord', action: 'Weekend kickoff' }],
                  6: [{ time: '—', platform: 'TikTok + IG + YT', action: 'Video posts' }, { time: '—', platform: 'Twitter/X', action: '2 tweets' }, { time: '2pm', platform: 'Instagram', action: 'Community showcase Reel' }, { time: '8pm', platform: 'Reddit', action: 'Casual gaming post' }],
                };
                const dayNames = ['Sunday Reset', 'Motivation Monday', 'Tutorial Tuesday', 'Midweek Matchup', 'Data Thursday', 'Friday Flex', 'Saturday Showcase'];
                const schedule = SCHEDULES[dow] || [];
                return (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, color: '#555', marginBottom: 10 }}>
                      <span style={{ color: '#00d4ff', fontWeight: 600 }}>{dayNames[dow]}</span> · {content.date}
                    </div>
                    <div style={{ background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 8, padding: '10px 14px' }}>
                      {schedule.map((s, i) => (
                        <div key={i} style={{ display: 'flex', gap: 10, padding: '4px 0', fontSize: 11, borderBottom: i < schedule.length - 1 ? '1px solid #111' : 'none' }}>
                          <span style={{ color: '#555', minWidth: 50, fontWeight: 600 }}>{s.time}</span>
                          <span style={{ color: '#94a3b8', minWidth: 100 }}>{s.platform}</span>
                          <span style={{ color: '#ccc' }}>{s.action}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Twitter posts */}
              <ContentBlock title="Twitter / X Posts" content={content?.['twitter_posts']} />

              {/* Reddit post — only on scheduled days */}
              <ContentBlock title="Reddit Post" content={content?.['reddit_posts']} singleBlock />

              {/* Twitter thread — only on scheduled days */}
              <ThreadBlock content={content?.['twitter_thread']} />

              {/* Instagram carousel — only on scheduled days */}
              <CarouselBlock content={content?.['instagram_carousel']} />

              {/* Image card captions — only one runs per day based on day-of-week rotation */}
              <ImageCardBlock title="Stat Card — Captions" content={content?.['stat_card']} />
              <ImageCardBlock title="Profile Reveal — Captions" content={content?.['profile_reveal']} />
              <ImageCardBlock title="Archetype Card — Captions" content={content?.['archetype_card']} />
              <ImageCardBlock title="Comparison Card — Captions" content={content?.['comparison_card']} />
              <ImageCardBlock title="Leaderboard Card — Captions" content={content?.['leaderboard_card']} />

              {/* Video captions + hashtags — synced from render-remotion after each render run */}
              {content?.['video_metadata'] && (() => {
                let videoMeta = [];
                try { videoMeta = JSON.parse(content['video_metadata']); } catch {}
                if (!videoMeta.length) return null;
                return (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, color: '#00d4ff', fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                      Video Captions & Hashtags
                      <span style={{ fontSize: 11, color: '#555', fontWeight: 400 }}>{videoMeta.length} videos</span>
                    </div>
                    {videoMeta.map((v, i) => (
                      <div key={i} style={{ marginBottom: 14, background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 10, padding: '14px 16px' }}>
                        <div style={{ fontSize: 12, color: '#fff', fontWeight: 600, marginBottom: 12 }}>{v.title || v.filename}</div>
                        {v.caption && (
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>Caption</div>
                            <div style={{ background: '#060a0f', borderRadius: 8, padding: '10px 14px', borderLeft: '2px solid #00d4ff33', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                              <p style={{ flex: 1, margin: 0, fontSize: 12, color: '#ccc', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{v.caption}</p>
                              <CopyButton text={v.caption} />
                            </div>
                          </div>
                        )}
                        {v.hashtags && (() => {
                          const ht = v.hashtags;
                          const platforms = typeof ht === 'object'
                            ? Object.entries(ht)
                            : [['all', typeof ht === 'string' ? ht.split(' ') : []]];
                          return (
                            <div>
                              <div style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>Hashtags</div>
                              {platforms.map(([plat, tags]) => {
                                const tagArr = Array.isArray(tags) ? tags : tags.split(' ');
                                return (
                                  <div key={plat} style={{ marginBottom: 6 }}>
                                    <div style={{ fontSize: 10, color: '#444', marginBottom: 4, textTransform: 'capitalize' }}>{plat}</div>
                                    <div style={{ background: '#060a0f', border: '1px solid #1a1a1a', borderRadius: 8, padding: '6px 12px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                      <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '4px 8px' }}>
                                        {tagArr.map((tag, t) => <span key={t} style={{ fontSize: 11, color: '#00d4ff' }}>{tag}</span>)}
                                      </div>
                                      <CopyButton text={tagArr.join(' ')} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Newsletter */}
              <NewsletterBlock content={content?.['newsletter']} apiBase={api} adminSecret={secret} />

              {/* Retake reminders */}
              <RetakeReminderBlock apiBase={api} adminSecret={secret} />

              {!content?.['twitter_posts'] && !content?.['reddit_posts'] && !content?.['video_metadata'] && !content?.['twitter_thread'] && !content?.['stat_card'] && (
                <div style={{ color: '#555', fontSize: 13 }}>
                  No content synced yet. Run <code style={{ color: '#00d4ff' }}>npm run daily</code> locally to generate and sync.
                </div>
              )}
            </Section>
          </>
        )}

        {/* CMO */}
        {section === 'CMO' && (
          <CMOErrorBoundary><>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>CMO Dashboard</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  {cmoLoading ? 'Loading…' : cmo?.lastScan ? `Last scan: ${new Date(cmo.lastScan).toLocaleString()}` : 'No scan yet'}
                  {cmo?.lastMentionCheck ? ` · Mentions checked: ${new Date(cmo.lastMentionCheck).toLocaleString()}` : ''}
                </div>
                {scanStatus && (
                  <div style={{ fontSize: 11, marginTop: 4, color: scanStatus.includes('failed') || scanStatus.includes('Error') ? '#f59e0b' : '#10b981' }}>
                    {scanStatus}
                  </div>
                )}
              </div>
              <div style={{ flex: 1 }} />
              <button onClick={loadCmo} disabled={cmoLoading} style={{
                background: 'transparent', border: '1px solid #333', color: '#555',
                borderRadius: 7, padding: '7px 14px', fontSize: 12, cursor: 'pointer',
              }}>
                {cmoLoading ? '…' : 'Reload'}
              </button>
              <button onClick={runScan} disabled={cmoScanning} style={{
                background: cmoScanning ? '#00d4ff11' : '#00d4ff22',
                border: '1px solid #00d4ff55', color: '#00d4ff',
                borderRadius: 7, padding: '7px 16px', fontSize: 12,
                fontWeight: 700, cursor: 'pointer',
              }}>
                {cmoScanning ? 'Scanning…' : 'Run Scan'}
              </button>
              <button onClick={runAgentMonitor} disabled={agentMonitoring} style={{
                background: agentMonitoring ? '#a855f711' : '#a855f722',
                border: '1px solid #a855f755', color: '#a855f7',
                borderRadius: 7, padding: '7px 16px', fontSize: 12,
                fontWeight: 700, cursor: 'pointer',
              }}>
                {agentMonitoring ? '🤖 Running…' : '🤖 AI Monitor'}
              </button>
              <button onClick={runMentionMonitorAgent} disabled={mentionMonitoring} style={{
                background: mentionMonitoring ? '#22d3ee11' : '#22d3ee22',
                border: '1px solid #22d3ee55', color: '#22d3ee',
                borderRadius: 7, padding: '7px 16px', fontSize: 12,
                fontWeight: 700, cursor: 'pointer',
              }}>
                {mentionMonitoring ? '🔍 Scanning…' : '🔍 Scan Mentions'}
              </button>
            </div>

            <Section title={(() => {
              const opps = cmo?.opportunities || [];
              const open = opps.filter(o => !o.posted && !o.archived).length;
              const acted = opps.length - open;
              return `Reddit Opportunities${opps.length ? ` — ${open} open · ${acted} actioned` : ''}`;
            })()}>
              {(() => {
                const allOpps = cmo?.opportunities || [];
                const visible = showArchived ? allOpps : allOpps.filter(o => !o.archived);
                if (!allOpps.length) return (
                  <div style={{ fontSize: 13, color: cmoScanning ? '#00d4ff' : '#888', padding: '20px 0' }}>
                    {cmoScanning ? 'Scanning Reddit for opportunities…' : 'No opportunities found. Click "Run Scan" to try again.'}
                  </div>
                );
                return (
                  <>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 12, fontSize: 11 }}>
                      <label style={{ color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
                        Show archived
                      </label>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {visible.map((opp) => {
                        const urgency = opp.age < 3 ? '#ef4444' : opp.age < 12 ? '#f59e0b' : '#555';
                        const actioned = opp.posted || opp.archived;
                        const posting = cmoActionId === `${opp.id}:posted`;
                        const archiving = cmoActionId === `${opp.id}:archived`;
                        const resetting = cmoActionId === `${opp.id}:reset`;
                        return (
                          <div key={opp.id} style={{ background: '#0d1117', border: `1px solid ${opp.posted ? '#10b98144' : opp.archived ? '#44444444' : '#1a2a3a'}`, borderRadius: 10, padding: '16px 18px', opacity: opp.archived ? 0.55 : 1 }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 4 }}>
                                  {opp.title}
                                  {opp.posted && <span style={{ marginLeft: 8, fontSize: 10, color: '#10b981', border: '1px solid #10b98155', borderRadius: 4, padding: '2px 6px' }}>POSTED</span>}
                                  {opp.archived && <span style={{ marginLeft: 8, fontSize: 10, color: '#888', border: '1px solid #444', borderRadius: 4, padding: '2px 6px' }}>ARCHIVED</span>}
                                </div>
                                <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>
                                  <span style={{ color: '#00d4ff' }}>r/{opp.sub}</span>
                                  <span style={{ color: '#555' }}>↑{opp.score} · {opp.comments} comments</span>
                                  <span style={{ color: urgency }}>{opp.age}h ago</span>
                                </div>
                              </div>
                              <a href={opp.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#00d4ff', textDecoration: 'none', border: '1px solid #00d4ff33', borderRadius: 6, padding: '4px 10px', whiteSpace: 'nowrap' }}>Open →</a>
                            </div>
                            {opp.body && <div style={{ fontSize: 11, color: '#555', marginBottom: 10, fontStyle: 'italic', lineHeight: 1.5, borderLeft: '2px solid #1a1a1a', paddingLeft: 10 }}>{opp.body.slice(0, 200)}{opp.body.length > 200 ? '…' : ''}</div>}
                            {opp.draft ? (
                              <div style={{ background: '#060a0f', borderRadius: 8, padding: '12px 14px' }}>
                                <div style={{ fontSize: 10, color: '#00d4ff', fontWeight: 700, marginBottom: 6, letterSpacing: 1, textTransform: 'uppercase' }}>Draft Response</div>
                                <div style={{ fontSize: 12, color: '#ccc', lineHeight: 1.6, whiteSpace: 'pre-wrap', marginBottom: 8 }}>{opp.draft}</div>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  <CopyButton text={opp.draft} />
                                  {!actioned && (
                                    <>
                                      <button onClick={() => flagOpportunity(opp.id, 'posted')} disabled={posting} style={{ background: 'transparent', border: '1px solid #10b98155', color: '#10b981', borderRadius: 6, padding: '5px 12px', fontSize: 11, cursor: 'pointer' }}>
                                        {posting ? '…' : '✓ Mark Posted'}
                                      </button>
                                    </>
                                  )}
                                  {actioned && (
                                    <button onClick={() => flagOpportunity(opp.id, 'reset')} disabled={resetting} style={{ background: 'transparent', border: '1px solid #333', color: '#666', borderRadius: 6, padding: '5px 12px', fontSize: 11, cursor: 'pointer' }}>
                                      {resetting ? '…' : 'Undo'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button onClick={() => generateDraft(opp)} disabled={draftingId === opp.id} style={{ background: 'transparent', border: '1px solid #333', color: '#777', borderRadius: 7, padding: '6px 14px', fontSize: 11, cursor: 'pointer' }}>
                                  {draftingId === opp.id ? 'Generating…' : 'Generate response'}
                                </button>
                                {!actioned && (
                                  <button onClick={() => flagOpportunity(opp.id, 'posted')} disabled={posting} style={{ background: 'transparent', border: '1px solid #10b98155', color: '#10b981', borderRadius: 6, padding: '6px 14px', fontSize: 11, cursor: 'pointer' }}>
                                    {posting ? '…' : '✓ Mark Posted'}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </Section>

            <Section title={`NeuroRank Mentions${cmo?.mentions?.length ? ` — ${cmo.mentions.length}` : ''}`}>
              {!cmo?.mentions?.length ? (
                <div style={{ fontSize: 13, color: mentionMonitoring ? '#22d3ee' : '#888', padding: '20px 0' }}>
                  {mentionMonitoring ? 'Agent scanning Reddit for mentions…' : 'No mentions found. Click "Scan Mentions" to check for NeuroRank mentions on Reddit.'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {(cmo.mentions || []).filter(m => showArchived || !m.dismissed).slice(0, 20).map((m, i) => {
                    const sentimentColor = { positive: '#22c55e', negative: '#ef4444', question: '#f59e0b', neutral: '#555' }[m.sentiment] || '#555';
                    const actioned = m.replied || m.dismissed;
                    const replying = cmoActionId === `${m.url}:replied`;
                    const dismissing = cmoActionId === `${m.url}:dismissed`;
                    const resetting = cmoActionId === `${m.url}:reset`;
                    return (
                      <div key={i} style={{ background: '#0d1117', border: `1px solid ${m.replied ? '#22d3ee44' : m.dismissed ? '#44444444' : '#1a1a1a'}`, borderRadius: 8, padding: '14px 16px', opacity: m.dismissed ? 0.5 : 1 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                          <div style={{ fontSize: 16, flexShrink: 0 }}>{m.source === 'twitter' ? '🐦' : '🟠'}</div>
                          <div style={{ flex: 1 }}>
                            {m.title && <div style={{ fontSize: 13, fontWeight: 600, color: '#ccc', marginBottom: 4 }}>
                              {m.title}
                              {m.replied && <span style={{ marginLeft: 8, fontSize: 10, color: '#22d3ee', border: '1px solid #22d3ee55', borderRadius: 4, padding: '2px 6px' }}>REPLIED</span>}
                              {m.dismissed && <span style={{ marginLeft: 8, fontSize: 10, color: '#888', border: '1px solid #444', borderRadius: 4, padding: '2px 6px' }}>DISMISSED</span>}
                            </div>}
                            <div style={{ fontSize: 12, color: '#888', marginBottom: 6, lineHeight: 1.5 }}>{(m.body || '').slice(0, 160)}{(m.body || '').length > 160 ? '…' : ''}</div>
                            <div style={{ display: 'flex', gap: 10, fontSize: 11, flexWrap: 'wrap' }}>
                              <span style={{ color: '#555' }}>u/{m.author}</span>
                              {m.subreddit && <span style={{ color: '#00d4ff88' }}>r/{m.subreddit}</span>}
                              <span style={{ color: '#444' }}>{m.createdAt?.split('T')[0]}</span>
                              {m.sentiment && <span style={{ color: sentimentColor, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{m.sentiment}</span>}
                            </div>
                          </div>
                          <a href={m.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#00d4ff', textDecoration: 'none', border: '1px solid #00d4ff33', borderRadius: 6, padding: '4px 10px', whiteSpace: 'nowrap', flexShrink: 0 }}>View →</a>
                        </div>
                        {m.draft && (
                          <div style={{ background: '#060a0f', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
                            <div style={{ fontSize: 10, color: '#22d3ee', fontWeight: 700, marginBottom: 6, letterSpacing: 1, textTransform: 'uppercase' }}>Draft Response</div>
                            <div style={{ fontSize: 12, color: '#ccc', lineHeight: 1.6, whiteSpace: 'pre-wrap', marginBottom: 8 }}>{m.draft}</div>
                            <CopyButton text={m.draft} />
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {!actioned && (
                            <>
                              <button onClick={() => flagMention(m.url, 'replied')} disabled={replying} style={{ background: 'transparent', border: '1px solid #22d3ee55', color: '#22d3ee', borderRadius: 6, padding: '5px 12px', fontSize: 11, cursor: 'pointer' }}>
                                {replying ? '…' : '✓ Mark Replied'}
                              </button>
                              <button onClick={() => flagMention(m.url, 'dismissed')} disabled={dismissing} style={{ background: 'transparent', border: '1px solid #444', color: '#888', borderRadius: 6, padding: '5px 12px', fontSize: 11, cursor: 'pointer' }}>
                                {dismissing ? '…' : 'Dismiss'}
                              </button>
                            </>
                          )}
                          {actioned && (
                            <button onClick={() => flagMention(m.url, 'reset')} disabled={resetting} style={{ background: 'transparent', border: '1px solid #333', color: '#666', borderRadius: 6, padding: '5px 12px', fontSize: 11, cursor: 'pointer' }}>
                              {resetting ? '…' : 'Undo'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>
            <Section title="Reply Generator">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Reply you received *</div>
                  <textarea
                    value={replyText}
                    onChange={e => { setReplyText(e.target.value); setReplyDraft(''); }}
                    placeholder="Paste the reply someone left on your comment…"
                    rows={3}
                    style={{ width: '100%', background: '#0d1117', border: '1px solid #222', borderRadius: 8, color: '#ccc', padding: '10px 12px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Your original comment (optional — helps with context)</div>
                  <textarea
                    value={replyContext}
                    onChange={e => setReplyContext(e.target.value)}
                    placeholder="Paste your original comment…"
                    rows={2}
                    style={{ width: '100%', background: '#0d1117', border: '1px solid #222', borderRadius: 8, color: '#ccc', padding: '10px 12px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </div>
                <button
                  onClick={async () => {
                    if (!replyText.trim()) return;
                    setReplyDrafting(true);
                    setReplyDraft('');
                    try {
                      const r = await fetch(`${api}/api/admin/cmo/reply-draft`, {
                        method: 'POST',
                        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ replyText: replyText.trim(), originalComment: replyContext.trim() || undefined }),
                      });
                      const data = await r.json();
                      if (data.draft) setReplyDraft(data.draft);
                    } catch (e) {
                      setReplyDraft('Error generating draft.');
                    }
                    setReplyDrafting(false);
                  }}
                  disabled={replyDrafting || !replyText.trim()}
                  style={{ alignSelf: 'flex-start', background: replyDrafting || !replyText.trim() ? '#111' : '#00d4ff22', border: '1px solid #00d4ff55', color: replyDrafting || !replyText.trim() ? '#555' : '#00d4ff', borderRadius: 8, padding: '8px 18px', fontSize: 13, cursor: replyDrafting || !replyText.trim() ? 'not-allowed' : 'pointer' }}
                >
                  {replyDrafting ? 'Generating…' : 'Draft reply'}
                </button>
                {replyDraft && (
                  <div style={{ background: '#0d1117', border: '1px solid #1a2a1a', borderRadius: 8, padding: '14px 16px' }}>
                    <div style={{ fontSize: 11, color: '#555', marginBottom: 8 }}>Draft</div>
                    <div style={{ fontSize: 13, color: '#ccc', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{replyDraft}</div>
                    <button
                      onClick={() => { navigator.clipboard.writeText(replyDraft); }}
                      style={{ marginTop: 12, background: 'transparent', border: '1px solid #333', color: '#777', borderRadius: 6, padding: '5px 12px', fontSize: 11, cursor: 'pointer' }}
                    >
                      Copy
                    </button>
                  </div>
                )}
              </div>
            </Section>
          </></CMOErrorBoundary>
        )}

        {/* AGENTS */}
        {section === 'Agents' && (
          <>
            {/* Header row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', flex: 1 }}>Agent Infrastructure</div>
              <button onClick={loadAgents} disabled={agentsLoading} style={{
                background: 'transparent', border: '1px solid #333', color: '#555',
                borderRadius: 7, padding: '7px 14px', fontSize: 12, cursor: 'pointer',
              }}>{agentsLoading ? '…' : 'Refresh'}</button>
              <button onClick={checkNow} disabled={checkingNow} style={{
                background: checkingNow ? '#00d4ff11' : '#00d4ff22',
                border: '1px solid #00d4ff55', color: '#00d4ff',
                borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}>{checkingNow ? 'Checking…' : 'Check Now'}</button>
              <button onClick={togglePause} disabled={pauseLoading} style={{
                background: agentsData?.paused ? '#ef444422' : '#10b98122',
                border: `1px solid ${agentsData?.paused ? '#ef4444' : '#10b981'}`,
                color: agentsData?.paused ? '#ef4444' : '#10b981',
                borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}>
                {pauseLoading ? '…' : agentsData?.paused ? '⏸ Agents Paused — Click to Resume' : '▶ Agents Active — Click to Pause All'}
              </button>
            </div>

            {agentsLoading && !agentsData && (
              <div style={{ color: '#444', fontSize: 13, padding: '40px 0' }}>Loading agent data…</div>
            )}

            {agentsData && (() => {
              const AGENT_META = {
                anomaly_detector:  { label: 'Anomaly Detector',  color: '#10b981', trigger: 'cron',             schedule: 'every 30 min' },
                data_quality:      { label: 'Data Quality',      color: '#ef4444', trigger: 'cron',             schedule: 'Sunday 9am UTC' },
                seo_content:       { label: 'SEO Content',       color: '#818cf8', trigger: 'cron',             schedule: 'Mon · Wed · Fri 7am UTC' },
                blog_writer:       { label: 'Blog Writer',        color: '#6366f1', trigger: 'cron',             schedule: 'Friday 7am UTC' },
                scout_digest:      { label: 'Scout Digest',       color: '#f59e0b', trigger: 'cron',             schedule: 'Sunday 10am UTC' },
                team_report:       { label: 'Team Report',        color: '#06b6d4', trigger: 'cron',             schedule: 'Monday 8am UTC' },
                weekly_digest:     { label: 'Weekly Digest',      color: '#84cc16', trigger: 'cron',             schedule: 'Monday 9am UTC' },
                reengagement:      { label: 'Re-engagement',      color: '#fb923c', trigger: 'cron',             schedule: 'Wed · Sat noon UTC' },
                onboarding_drip:   { label: 'Onboarding Drip',    color: '#34d399', trigger: 'registration',     schedule: 'daily 10am UTC' },
                coach_free:        { label: 'Coach (Free)',        color: '#a855f7', trigger: 'session_complete', schedule: null },
                coach_pro:         { label: 'Coach (Pro)',         color: '#e879f9', trigger: 'session_complete', schedule: null },
                watchlist:         { label: 'Watchlist Intel',     color: '#00d4ff', trigger: 'bookmark_added',   schedule: null },
                team_composition:  { label: 'Team Composition',   color: '#38bdf8', trigger: 'on_demand',        schedule: null },
                reddit_monitor:    { label: 'Reddit Monitor',      color: '#ff6b35', trigger: 'cron',             schedule: '8am · 1pm · 7pm UTC' },
                mention_monitor:   { label: 'Mention Monitor',     color: '#22d3ee', trigger: 'cron',             schedule: '9am · 5pm UTC' },
              };

              // 7-day cost chart data — last 7 days
              const days = Array.from({ length: 7 }, (_, i) => {
                const d = new Date(Date.now() - (6 - i) * 24 * 60 * 60 * 1000);
                return d.toISOString().slice(0, 10);
              });
              const totalCostByDay = days.map(day => {
                const dc = agentsData.dailyCost?.[day] || {};
                return Object.values(dc).reduce((a, v) => a + v, 0);
              });
              const maxDayCost = Math.max(...totalCostByDay, 0.001);
              const totalWeekCost = totalCostByDay.reduce((a, v) => a + v, 0);
              const totalWeekRuns = Object.values(agentsData.summary || {}).reduce((a, s) => a + (s.weekRuns || 0), 0);

              // ── Derived values for gamified UI ─────────────────────────
              const costPct         = Math.min(totalWeekCost / 5.0, 1);
              const costColor       = costPct > 0.8 ? '#ef4444' : costPct > 0.5 ? '#f59e0b' : '#10b981';
              const totalFailed     = Object.values(agentsData.summary || {}).reduce((a, s) => a + (s.failed || 0), 0);
              const successRate     = totalWeekRuns > 0 ? Math.round(((totalWeekRuns - totalFailed) / totalWeekRuns) * 100) : 100;
              const activeAgents    = Object.values(agentsData.summary || {}).filter(s => s.lastStatus === 'running').length;

              const timeAgo = (ts) => {
                if (!ts) return 'never';
                const mins = Math.round((Date.now() - new Date(ts)) / 60000);
                if (mins < 60)   return `${mins}m ago`;
                if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
                return `${Math.round(mins / 1440)}d ago`;
              };

              return (
                <>
                  {/* ── Animation keyframes ──────────────────────────────────── */}
                  <style>{`
                    @keyframes nr-pulse-led  { 0%,100% { opacity:1; } 50% { opacity:0.2; } }
                    @keyframes nr-slide-in   { from { opacity:0; transform:translateX(-6px); } to { opacity:1; transform:none; } }
                    @keyframes nr-bob        { 0%,100% { transform:translateY(0px); } 50% { transform:translateY(-4px); } }
                    @keyframes nr-conveyor   { from { transform:translateX(0); } to { transform:translateX(-16px); } }
                    .nr-agent-card  { transition: transform 0.15s ease, box-shadow 0.2s ease !important; }
                    .nr-agent-card:hover { transform: translateY(-3px) scale(1.02) !important; }
                    .nr-log-row { animation: nr-slide-in 0.2s ease both; }
                  `}</style>

                  {/* ── SYSTEM STATUS BANNER ─────────────────────────────────── */}
                  {(() => {
                    const h = agentsData.health;
                    if (!h?.lastCheck) return null;
                    const sev      = h.lastSeverity || 'ok';
                    const sevColor = sev === 'critical' ? '#ef4444' : sev === 'warning' ? '#f59e0b' : '#10b981';
                    const sevBg    = sev === 'critical' ? '#ef444409' : sev === 'warning' ? '#f59e0b09' : '#10b98109';
                    const checkAgo = timeAgo(h.lastCheck);
                    const statusLabel = sev === 'ok' ? 'ALL SYSTEMS OPERATIONAL' : sev === 'warning' ? 'WARNING DETECTED' : 'CRITICAL ANOMALY';
                    return (
                      <div style={{ background: sevBg, border: `1px solid ${sevColor}33`, borderRadius: 12, padding: '14px 20px', marginBottom: 20, position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', background: sevColor, borderRadius: '12px 0 0 12px' }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14, paddingLeft: 8 }}>
                          <div style={{
                            width: 10, height: 10, borderRadius: '50%', background: sevColor, flexShrink: 0,
                            animation: sev !== 'ok' ? 'nr-pulse-led 1.2s ease infinite' : 'none',
                          }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: sevColor, letterSpacing: 2, marginBottom: 3 }}>{statusLabel}</div>
                            <div style={{ fontSize: 12, color: '#888' }}>{h.lastSummary || 'Anomaly detector has not run yet.'}</div>
                          </div>
                          <div style={{ fontSize: 10, color: '#444', textAlign: 'right', lineHeight: 1.6 }}>
                            <div style={{ color: '#555' }}>Last check</div>
                            <div>{checkAgo}</div>
                          </div>
                        </div>
                        {h.lastAnomalies?.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 12, paddingLeft: 8 }}>
                            {h.lastAnomalies.map((a, i) => (
                              <div key={i} style={{ background: '#060a0f', borderRadius: 6, padding: '7px 12px', fontSize: 11, color: '#888', lineHeight: 1.5, display: 'flex', gap: 10, alignItems: 'baseline' }}>
                                <span style={{ color: a.severity === 'critical' ? '#ef4444' : '#f59e0b', fontWeight: 800, fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', flexShrink: 0 }}>{a.severity}</span>
                                <span>{a.detail}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* ── MISSION CONTROL STRIP ───────────────────────────────── */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) 2fr', gap: 10, marginBottom: 20 }}>
                    {[
                      { label: 'RUNS THIS WEEK', value: totalWeekRuns,              color: '#a855f7' },
                      { label: 'TOTAL COST',     value: `$${totalWeekCost.toFixed(4)}`, color: costColor },
                      { label: 'SUCCESS RATE',   value: `${successRate}%`,           color: successRate === 100 ? '#10b981' : successRate > 90 ? '#f59e0b' : '#ef4444' },
                      { label: 'ACTIVE NOW',     value: activeAgents,               color: activeAgents > 0 ? '#00d4ff' : '#555' },
                    ].map(({ label, value, color }) => (
                      <div key={label} style={{ background: '#0d1117', border: '1px solid #1a2a3a', borderRadius: 10, padding: '14px 16px' }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#444', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
                      </div>
                    ))}
                    {/* Budget gauge */}
                    <div style={{ background: '#0d1117', border: `1px solid ${costColor}33`, borderRadius: 10, padding: '14px 16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#444', letterSpacing: 2, textTransform: 'uppercase' }}>WEEKLY BUDGET</div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: costColor }}>${totalWeekCost.toFixed(4)} <span style={{ color: '#333', fontWeight: 400 }}>/ $5.00</span></div>
                      </div>
                      <div style={{ height: 8, background: '#1a1a2e', borderRadius: 4, overflow: 'hidden', marginBottom: 6 }}>
                        <div style={{
                          height: '100%',
                          width: `${Math.max(costPct * 100, costPct > 0 ? 1 : 0)}%`,
                          background: 'linear-gradient(90deg, #10b981 0%, #f59e0b 55%, #ef4444 85%)',
                          backgroundSize: '400px 100%',
                          backgroundPosition: `${(1 - costPct) * -300}px 0`,
                          borderRadius: 4,
                          transition: 'width 0.6s ease',
                        }} />
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {/* 7-day sparkline inline */}
                        {totalCostByDay.map((cost, i) => (
                          <div key={i} title={`${days[i]}: $${cost.toFixed(4)}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flex: 1 }}>
                            <div style={{
                              width: '100%', minWidth: 6,
                              height: Math.max(4, Math.round((cost / maxDayCost) * 28)),
                              background: cost > 0 ? '#a855f755' : '#1a1a2e',
                              borderRadius: 2,
                              borderTop: cost > 0 ? `1px solid #a855f7` : 'none',
                            }} />
                            <div style={{ fontSize: 8, color: '#333' }}>{days[i].slice(5)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ── SVG FACTORY FLOOR ─────────────────────────────────────── */}
                  {/* FLOORPLAN SKETCH (1100×570):
                       x=20  w=230: DEPT-01 Analytics  (y=20–268)
                       x=20  w=230: DEPT-05 Outreach    (y=288–536)
                       x=258 w=54:  Corridor-L          (y=20–536)
                       x=320 w=268: DEPT-03 Player Ops  (y=20–536)
                       x=596 w=54:  Corridor-R          (y=20–536)
                       x=658 w=422: DEPT-02 Content Lab (y=20–268)
                       x=658 w=422: DEPT-04 Intel Div   (y=288–536)
                       y=544:       Legend strip
                  */}
                  {(() => {
                    // ── Room layout — add new rooms/agents here to scale ─────────
                    const ROOMS_DATA = [
                      {
                        id: 'analytics', code: 'DEPT-01', label: 'Analytics Floor', color: '#4a8fe0',
                        x:20, y:20, w:230, h:248,
                        agents: {
                          anomaly_detector: { rx:35, ry:80 },
                          data_quality:     { rx:130, ry:80 },
                        },
                      },
                      {
                        id: 'outreach', code: 'DEPT-05', label: 'Outreach Dept', color: '#fb923c',
                        x:20, y:288, w:230, h:248,
                        agents: {
                          reddit_monitor:  { rx:35, ry:80 },
                          mention_monitor: { rx:130, ry:80 },
                        },
                      },
                      {
                        id: 'player_ops', code: 'DEPT-03', label: 'Player Ops', color: '#22d3ee',
                        x:320, y:20, w:268, h:516,
                        agents: {
                          coach_free:      { rx:30,  ry:70  },
                          coach_pro:       { rx:160, ry:70  },
                          onboarding_drip: { rx:30,  ry:220 },
                          reengagement:    { rx:160, ry:220 },
                          weekly_digest:   { rx:95,  ry:370 },
                        },
                      },
                      {
                        id: 'content', code: 'DEPT-02', label: 'Content Lab', color: '#818cf8',
                        x:658, y:20, w:422, h:248,
                        agents: {
                          seo_content: { rx:60,  ry:80 },
                          blog_writer: { rx:240, ry:80 },
                        },
                      },
                      {
                        id: 'intel', code: 'DEPT-04', label: 'Intel Division', color: '#fbbf24',
                        x:658, y:288, w:422, h:248,
                        agents: {
                          scout_digest:     { rx:30,  ry:65  },
                          team_report:      { rx:145, ry:65  },
                          watchlist:        { rx:260, ry:65  },
                          team_composition: { rx:185, ry:155 },
                        },
                      },
                    ];

                    // ── Machine type per agent ───────────────────────────────────
                    const MACHINE_TYPE = {
                      anomaly_detector:  'radar',    data_quality:     'server',
                      seo_content:       'terminal', blog_writer:      'press',
                      scout_digest:      'scope',    team_report:      'table',
                      watchlist:         'camera',   team_composition: 'board',
                      coach_free:        'mat',      coach_pro:        'pod',
                      onboarding_drip:   'funnel',   reengagement:     'tower',
                      weekly_digest:     'drum',     reddit_monitor:   'antenna',
                      mention_monitor:   'satellite',
                    };

                    // ── Top-down SVG machine renderer ─────────────────────────────
                    // Each machine occupies a ~62×58 footprint starting at (mx, my)
                    const Machine = (type, mx, my, col, running, failed) => {
                      const dim = failed ? '#1c0808' : '#0b1622';
                      switch(type) {
                        case 'radar': {
                          const cx = mx+31, cy = my+29;
                          return (
                            <g>
                              <circle cx={cx} cy={cy} r={27} fill={dim} stroke={col} strokeWidth="1.5" opacity="0.75"/>
                              <circle cx={cx} cy={cy} r={19} fill="none" stroke={col} strokeWidth="0.8" opacity="0.35"/>
                              <circle cx={cx} cy={cy} r={11} fill="none" stroke={col} strokeWidth="0.8" opacity="0.35"/>
                              <circle cx={cx} cy={cy} r={4}  fill={col} opacity="0.65"/>
                              <line x1={cx-27} y1={cy} x2={cx+27} y2={cy} stroke={col} strokeWidth="0.5" opacity="0.25"/>
                              <line x1={cx} y1={cy-27} x2={cx} y2={cy+27} stroke={col} strokeWidth="0.5" opacity="0.25"/>
                              {running && (
                                <path d={`M${cx},${cy} L${cx+27},${cy} A27,27 0 0,0 ${cx+19},${cy-19} Z`} fill={col} opacity="0.22">
                                  <animateTransform attributeName="transform" type="rotate" from={`0 ${cx} ${cy}`} to={`360 ${cx} ${cy}`} dur="3s" repeatCount="indefinite"/>
                                </path>
                              )}
                            </g>
                          );
                        }
                        case 'server': {
                          return (
                            <g>
                              <rect x={mx+4} y={my+4} width={54} height={52} rx="2" fill={dim} stroke={col} strokeWidth="1.5" opacity="0.8"/>
                              {[0,1,2,3].map(i => (
                                <g key={i}>
                                  <rect x={mx+8} y={my+9+i*11} width={46} height={8} rx="1" fill={running ? col+'1a' : '#050d18'} stroke={col} strokeWidth="0.5" opacity="0.65"/>
                                  <circle cx={mx+49} cy={my+13+i*11} r="2" fill={i===0&&running ? col : i===0&&failed ? '#ef4444' : col+'40'} opacity="0.9"/>
                                  <rect x={mx+10} y={my+11+i*11} width={26} height={4} rx="1" fill={col} opacity={running ? 0.10 : 0.04}/>
                                </g>
                              ))}
                            </g>
                          );
                        }
                        case 'terminal': {
                          return (
                            <g>
                              <rect x={mx+4} y={my+3} width={54} height={40} rx="3" fill={dim} stroke={col} strokeWidth="1.5" opacity="0.8"/>
                              <rect x={mx+7} y={my+6} width={48} height={34} rx="1" fill={running ? '#001a26' : '#040b14'} opacity="0.9"/>
                              <circle cx={mx+28} cy={my+21} r="10" fill="none" stroke={col} strokeWidth="1.5" opacity="0.65"/>
                              <line x1={mx+35} y1={my+28} x2={mx+43} y2={my+36} stroke={col} strokeWidth="2" strokeLinecap="round" opacity="0.65"/>
                              {running && (
                                <line x1={mx+9} y1={my+21} x2={mx+51} y2={my+21} stroke={col} strokeWidth="0.5" opacity="0.4">
                                  <animate attributeName="y1" values={`${my+8};${my+37};${my+8}`} dur="1.5s" repeatCount="indefinite"/>
                                  <animate attributeName="y2" values={`${my+8};${my+37};${my+8}`} dur="1.5s" repeatCount="indefinite"/>
                                </line>
                              )}
                              <rect x={mx+23} y={my+43} width={16} height={5} rx="1" fill={col} opacity="0.35"/>
                              <rect x={mx+18} y={my+47} width={26} height={3} rx="1" fill={col} opacity="0.25"/>
                            </g>
                          );
                        }
                        case 'press': {
                          return (
                            <g>
                              <rect x={mx+3} y={my+6} width={56} height={46} rx="2" fill={dim} stroke={col} strokeWidth="1.5" opacity="0.8"/>
                              {[0,1,2].map(i => (
                                <rect key={i} x={mx+3} y={my+12+i*14} width={56} height={7} rx="3" fill={running ? col+'28' : col+'10'} stroke={col} strokeWidth="0.8" opacity="0.8"/>
                              ))}
                              {running && (
                                <rect x={mx+19} y={my+1} width={24} height={9} rx="1" fill={col} opacity="0.28">
                                  <animate attributeName="y" values={`${my+1};${my+52};${my+1}`} dur="2.2s" repeatCount="indefinite"/>
                                </rect>
                              )}
                            </g>
                          );
                        }
                        case 'scope': {
                          return (
                            <g>
                              <circle cx={mx+17} cy={my+29} r={17} fill={dim} stroke={col} strokeWidth="1.5" opacity="0.8"/>
                              <circle cx={mx+45} cy={my+29} r={17} fill={dim} stroke={col} strokeWidth="1.5" opacity="0.8"/>
                              <circle cx={mx+17} cy={my+29} r={11} fill={running ? col+'15' : '#040c16'} stroke={col} strokeWidth="0.8" opacity="0.6"/>
                              <circle cx={mx+45} cy={my+29} r={11} fill={running ? col+'15' : '#040c16'} stroke={col} strokeWidth="0.8" opacity="0.6"/>
                              <line x1={mx+8}  y1={my+29} x2={mx+26} y2={my+29} stroke={col} strokeWidth="0.5" opacity="0.5"/>
                              <line x1={mx+17} y1={my+20} x2={mx+17} y2={my+38} stroke={col} strokeWidth="0.5" opacity="0.5"/>
                              <line x1={mx+36} y1={my+29} x2={mx+54} y2={my+29} stroke={col} strokeWidth="0.5" opacity="0.5"/>
                              <line x1={mx+45} y1={my+20} x2={mx+45} y2={my+38} stroke={col} strokeWidth="0.5" opacity="0.5"/>
                              <rect x={mx+30} y={my+25} width={2} height={8} rx="1" fill={col} opacity="0.6"/>
                            </g>
                          );
                        }
                        case 'table': {
                          const cx = mx+31, cy = my+29;
                          return (
                            <g>
                              <circle cx={cx} cy={cy} r={23} fill={dim} stroke={col} strokeWidth="1.5" opacity="0.72"/>
                              <circle cx={cx} cy={cy} r={14} fill={running ? col+'10' : '#050e18'} stroke={col} strokeWidth="0.8" opacity="0.5"/>
                              {[0,1,2,3,4,5,6,7].map(i => {
                                const ang = i * Math.PI / 4;
                                const bx = cx + Math.cos(ang) * 20;
                                const by = cy + Math.sin(ang) * 20;
                                return <rect key={i} x={bx-4} y={by-3} width={8} height={6} rx="1.5" fill={col} opacity={running ? 0.4 : 0.18} transform={`rotate(${i*45} ${bx} ${by})`}/>;
                              })}
                            </g>
                          );
                        }
                        case 'camera': {
                          return (
                            <g>
                              <rect x={mx+6} y={my+14} width={42} height={28} rx="3" fill={dim} stroke={col} strokeWidth="1.5" opacity="0.8"/>
                              <circle cx={mx+44} cy={my+28} r={10} fill={running ? col+'1a' : '#040c16'} stroke={col} strokeWidth="1.5" opacity="0.8"/>
                              <circle cx={mx+44} cy={my+28} r={6}  fill={running ? col+'40' : col+'14'} opacity="0.9"/>
                              <circle cx={mx+44} cy={my+28} r={2.5} fill={col} opacity="0.75"/>
                              <rect x={mx+25} y={my+42} width={10} height={8} rx="1" fill={col} opacity="0.35"/>
                              <rect x={mx+20} y={my+49} width={20} height={3} rx="1" fill={col} opacity="0.25"/>
                              {running && (
                                <circle cx={mx+11} cy={my+19} r="2.5" fill="#ef4444">
                                  <animate attributeName="opacity" values="1;0.15;1" dur="1s" repeatCount="indefinite"/>
                                </circle>
                              )}
                            </g>
                          );
                        }
                        case 'board': {
                          const sz = 7;
                          return (
                            <g>
                              <rect x={mx+4} y={my+6} width={54} height={46} rx="2" fill={dim} stroke={col} strokeWidth="1.5" opacity="0.72"/>
                              {Array.from({length:4}, (_,row) =>
                                Array.from({length:4}, (_,c) => (
                                  <rect key={`${row}-${c}`} x={mx+11+c*(sz+1)} y={my+11+row*(sz+1)} width={sz} height={sz} rx="0.5"
                                    fill={(row+c)%2===0 ? col+'44' : col+'14'} stroke={col} strokeWidth="0.3" opacity="0.85"/>
                                ))
                              )}
                              <circle cx={mx+14} cy={my+14} r="2" fill={col} opacity="0.75"/>
                              <circle cx={mx+30} cy={my+30} r="2" fill={col} opacity="0.75"/>
                              {running && <circle cx={mx+44} cy={my+14} r="2" fill={col}><animate attributeName="r" values="2;3.5;2" dur="0.8s" repeatCount="indefinite"/></circle>}
                            </g>
                          );
                        }
                        case 'mat': {
                          return (
                            <g>
                              <rect x={mx+3} y={my+7} width={56} height={44} rx="4" fill={dim} stroke={col} strokeWidth="1.5" opacity="0.8"/>
                              <line x1={mx+9}  y1={my+13} x2={mx+53} y2={my+45} stroke={col} strokeWidth="1.5" opacity="0.3"/>
                              <line x1={mx+53} y1={my+13} x2={mx+9}  y2={my+45} stroke={col} strokeWidth="1.5" opacity="0.3"/>
                              {[[3,7],[50,7],[3,43],[50,43]].map(([cx2,cy2],i) => (
                                <rect key={i} x={mx+cx2} y={my+cy2} width={9} height={9} rx="1" fill={col} opacity="0.22"/>
                              ))}
                              <circle cx={mx+31} cy={my+29} r={9} fill="none" stroke={col} strokeWidth="1" opacity="0.38"/>
                            </g>
                          );
                        }
                        case 'pod': {
                          return (
                            <g>
                              <rect x={mx+5} y={my+5} width={52} height={50} rx="10" fill={dim} stroke={col} strokeWidth="2" opacity="0.82"/>
                              <rect x={mx+9} y={my+11} width={44} height={15} rx="4" fill={running ? col+'30' : col+'10'} stroke={col} strokeWidth="0.8" opacity="0.82">
                                {running && <animate attributeName="opacity" values="0.82;1;0.82" dur="1.6s" repeatCount="indefinite"/>}
                              </rect>
                              {[29,35,41].map(y2 => (
                                <line key={y2} x1={mx+11} y1={my+y2} x2={mx+51} y2={my+y2} stroke={col} strokeWidth="0.8" opacity="0.2"/>
                              ))}
                            </g>
                          );
                        }
                        case 'funnel': {
                          return (
                            <g>
                              <polygon points={`${mx+7},${my+4} ${mx+55},${my+4} ${mx+43},${my+40} ${mx+19},${my+40}`} fill={dim} stroke={col} strokeWidth="1.5" opacity="0.8"/>
                              <rect x={mx+24} y={my+40} width={14} height={14} rx="2" fill={dim} stroke={col} strokeWidth="1.2" opacity="0.72"/>
                              {[0,1,2].map(i => (
                                <line key={i} x1={mx+13+i*3} y1={my+13+i*7} x2={mx+49-i*3} y2={my+13+i*7} stroke={col} strokeWidth="0.7" opacity="0.22"/>
                              ))}
                              {running && (
                                <circle cx={mx+31} cy={my+55} r="2.5" fill={col} opacity="0.8">
                                  <animate attributeName="cy" values={`${my+55};${my+61};${my+55}`} dur="0.8s" repeatCount="indefinite"/>
                                  <animate attributeName="opacity" values="0.8;0;0.8" dur="0.8s" repeatCount="indefinite"/>
                                </circle>
                              )}
                            </g>
                          );
                        }
                        case 'tower': {
                          const cx = mx+31;
                          return (
                            <g>
                              <polygon points={`${cx},${my+3} ${cx+19},${my+55} ${cx-19},${my+55}`} fill={dim} stroke={col} strokeWidth="1.5" opacity="0.72"/>
                              {(running ? [1,2,3] : [1]).map(i => (
                                <path key={i} d={`M${cx-9*i},${my+24-3*i} A${9*i},${7*i} 0 0,1 ${cx+9*i},${my+24-3*i}`} fill="none" stroke={col} strokeWidth="1" opacity={running ? 0.6/i : 0.2}/>
                              ))}
                              <circle cx={cx} cy={my+5} r="3.5" fill={running ? col : col+'44'}>
                                {running && <animate attributeName="opacity" values="1;0.15;1" dur="0.6s" repeatCount="indefinite"/>}
                              </circle>
                            </g>
                          );
                        }
                        case 'drum': {
                          const cx = mx+31;
                          return (
                            <g>
                              <ellipse cx={cx} cy={my+15} rx={25} ry={8} fill={dim} stroke={col} strokeWidth="1.5" opacity="0.82"/>
                              <rect x={cx-25} y={my+15} width={50} height={28} fill={dim} stroke={col} strokeWidth="0.8" opacity="0.82"/>
                              <ellipse cx={cx} cy={my+43} rx={25} ry={8} fill={running ? col+'20' : '#050d18'} stroke={col} strokeWidth="1.5" opacity="0.82"/>
                              {[0,60,120,180,240,300].map(a => (
                                <line key={a}
                                  x1={cx} y1={my+43}
                                  x2={cx+Math.cos(a*Math.PI/180)*23}
                                  y2={my+43+Math.sin(a*Math.PI/180)*7}
                                  stroke={col} strokeWidth="0.8" opacity="0.38">
                                  {running && <animateTransform attributeName="transform" type="rotate" from={`0 ${cx} ${my+29}`} to={`360 ${cx} ${my+29}`} dur="2s" repeatCount="indefinite" additive="sum"/>}
                                </line>
                              ))}
                            </g>
                          );
                        }
                        case 'antenna': {
                          const cx = mx+31;
                          return (
                            <g>
                              <rect x={cx-2} y={my+6} width={4} height={48} rx="1" fill={col} opacity="0.6"/>
                              {[[30,4],[22,3],[14,3]].map(([w,h],i) => (
                                <rect key={i} x={cx-w/2} y={my+14+i*12} width={w} height={h} rx="1" fill={dim} stroke={col} strokeWidth="1" opacity={0.9-i*0.15}/>
                              ))}
                              <ellipse cx={cx} cy={my+7} rx={13} ry={5} fill={dim} stroke={col} strokeWidth="1.5" opacity="0.82"/>
                              {running && [1,2].map(i => (
                                <ellipse key={i} cx={cx} cy={my+7} rx={13+i*9} ry={5+i*3} fill="none" stroke={col} strokeWidth="0.5" opacity={0.4/i}>
                                  <animate attributeName="rx" values={`${13+i*9};${13+i*9+6};${13+i*9}`} dur={`${1+i*0.5}s`} repeatCount="indefinite"/>
                                  <animate attributeName="ry" values={`${5+i*3};${5+i*3+2};${5+i*3}`} dur={`${1+i*0.5}s`} repeatCount="indefinite"/>
                                </ellipse>
                              ))}
                            </g>
                          );
                        }
                        case 'satellite': {
                          return (
                            <g>
                              <rect x={mx+21} y={my+17} width={20} height={24} rx="2" fill={dim} stroke={col} strokeWidth="1.5" opacity="0.82"/>
                              {[[1,18],[42,18]].map(([sx,sy],idx) => (
                                <g key={idx}>
                                  <rect x={mx+sx} y={my+sy} width={19} height={11} rx="1" fill={dim} stroke={col} strokeWidth="1.2" opacity="0.72"/>
                                  {[0,1,2].map(i => <line key={i} x1={mx+sx+i*6+3} y1={my+sy} x2={mx+sx+i*6+3} y2={my+sy+11} stroke={col} strokeWidth="0.5" opacity="0.4"/>)}
                                </g>
                              ))}
                              <line x1={mx+31} y1={my+17} x2={mx+31} y2={my+7} stroke={col} strokeWidth="1.5" opacity="0.72"/>
                              <path d={`M${mx+17},${my+7} Q${mx+31},${my+1} ${mx+45},${my+7}`} fill="none" stroke={col} strokeWidth="2" opacity="0.82"/>
                              {running && <circle cx={mx+31} cy={my+4} r="2" fill={col} opacity="0.85"><animate attributeName="opacity" values="0.85;0.15;0.85" dur="0.9s" repeatCount="indefinite"/></circle>}
                            </g>
                          );
                        }
                        default:
                          return <rect x={mx+5} y={my+5} width={52} height={50} rx="4" fill={dim} stroke={col} strokeWidth="1.5" opacity="0.65"/>;
                      }
                    };

                    // ── Top-down worker sprite ────────────────────────────────────
                    // (cx, cy) = centre-bottom of sprite (stands on the machine)
                    // Running: animates side-to-side at workstation (meaningful — actively working)
                    // Idle/done: stands still
                    const Sprite = (cx, cy, col, running, failed) => {
                      const hat = failed ? '#ef4444' : col;
                      return (
                        <g transform={`translate(${cx-8},${cy-20})`}>
                          <rect x="2"  y="0"  width="12" height="4"  rx="1" fill={hat}        opacity="0.9"/>
                          <rect x="0"  y="3"  width="16" height="2"  rx="1" fill={hat}        opacity="0.55"/>
                          <rect x="3"  y="5"  width="10" height="9"  rx="2" fill="#0d1e2e"    stroke={hat} strokeWidth="0.5" opacity="0.9"/>
                          <rect x="5"  y="8"  width="2"  height="2"  rx="0.5" fill={running ? hat : '#1a3040'}/>
                          <rect x="9"  y="8"  width="2"  height="2"  rx="0.5" fill={running ? hat : '#1a3040'}/>
                          <rect x="2"  y="14" width="12" height="10" rx="1" fill={running ? hat+'28' : '#0a1820'} stroke={hat} strokeWidth="0.5" opacity="0.9"/>
                          <rect x="5"  y="16" width="6"  height="4"  rx="0.5" fill={hat}      opacity={running ? 0.38 : 0.12}/>
                          {/* Walk animation: only when actually running a job */}
                          {running && (
                            <animateTransform
                              attributeName="transform" type="translate"
                              values="0,0; -5,0; -5,-1; 0,-1; 5,-1; 5,0; 0,0"
                              dur="1.4s" repeatCount="indefinite" additive="sum"
                            />
                          )}
                        </g>
                      );
                    };

                    // ── Global state ──────────────────────────────────────────────
                    const totalWorkers = Object.keys(AGENT_META).length;
                    const runningTotal = Object.values(agentsData.summary || {}).filter(s => s.lastStatus === 'running').length;
                    const leftActive   = ['analytics','outreach'].some(id => {
                      const r = ROOMS_DATA.find(r2 => r2.id === id);
                      return r && Object.keys(r.agents).some(a => agentsData.summary?.[a]?.lastStatus === 'running');
                    });
                    const rightActive  = ['content','intel'].some(id => {
                      const r = ROOMS_DATA.find(r2 => r2.id === id);
                      return r && Object.keys(r.agents).some(a => agentsData.summary?.[a]?.lastStatus === 'running');
                    });
                    const centerActive = Object.keys(ROOMS_DATA.find(r => r.id === 'player_ops').agents).some(
                      a => agentsData.summary?.[a]?.lastStatus === 'running'
                    );

                    return (
                      <div style={{ marginBottom: 8 }}>
                        {/* Section header */}
                        <div style={{ fontSize:10, fontWeight:700, color:'#2a3a4a', letterSpacing:2, textTransform:'uppercase', marginBottom:12, display:'flex', alignItems:'center', gap:10 }}>
                          <span>Factory Floor — {totalWorkers} workers · {ROOMS_DATA.length} departments</span>
                          {runningTotal > 0 && <span style={{ color:'#f59e0b', fontSize:9, padding:'2px 8px', background:'#f59e0b15', borderRadius:4, border:'1px solid #f59e0b33' }}>{runningTotal} ACTIVE</span>}
                        </div>

                        {/* SVG canvas */}
                        <div style={{ position:'relative', borderRadius:10, overflow:'hidden', border:'1px solid #0c1826' }}>
                          <svg viewBox="0 0 1100 570" style={{ width:'100%', display:'block' }} xmlns="http://www.w3.org/2000/svg">
                            <defs>
                              {/* Per-room floor tile patterns */}
                              {ROOMS_DATA.map(room => (
                                <pattern key={room.id} id={`floor-${room.id}`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                                  <rect width="20" height="20" fill={room.color + '05'}/>
                                  <circle cx="0"  cy="0"  r="0.8" fill={room.color + '2a'}/>
                                  <circle cx="20" cy="0"  r="0.8" fill={room.color + '2a'}/>
                                  <circle cx="0"  cy="20" r="0.8" fill={room.color + '2a'}/>
                                  <circle cx="20" cy="20" r="0.8" fill={room.color + '2a'}/>
                                  <circle cx="10" cy="10" r="0.4" fill={room.color + '16'}/>
                                </pattern>
                              ))}
                              {/* Scanline overlay */}
                              <pattern id="scanlines" x="0" y="0" width="1" height="3" patternUnits="userSpaceOnUse">
                                <rect width="1" height="1" fill="rgba(0,0,0,0.07)"/>
                              </pattern>
                            </defs>

                            {/* Base floor */}
                            <rect x="0" y="0" width="1100" height="570" fill="#020810"/>
                            <rect x="0" y="0" width="1100" height="570" fill="url(#scanlines)" opacity="0.5"/>

                            {/* ── CORRIDORS ─────────────────────────────────────────── */}
                            {/* Left corridor x=258–312 */}
                            <g>
                              <rect x={258} y={20} width={54} height={516} fill="#020c18"/>
                              {/* Belt stripes */}
                              <rect x={258} y={20} width={54} height={516}
                                fill="none"
                                style={{ backgroundImage:'repeating-linear-gradient(0deg,#0d2030 0px,#0d2030 6px,transparent 6px,transparent 14px)' }}
                              />
                              {[60,105,150,195,240,285,330,375,420,465,510].map(y2 => (
                                <rect key={y2} x={265} y={y2} width={22} height={6} rx="1" fill="#00d4ff" opacity={leftActive||centerActive ? 0.12 : 0.04}/>
                              ))}
                              {/* Animated belt arrows when active */}
                              {(leftActive || centerActive) && [44,84,124,164,204,244,284,324,364,404,444,484,524].map(y2 => (
                                <polygon key={y2} points={`284,${y2} 284,${y2+12} 290,${y2+6}`} fill="#00d4ff" opacity="0.22"/>
                              ))}
                              <line x1={258} y1={20} x2={258} y2={536} stroke="#0d2232" strokeWidth="1"/>
                              <line x1={312} y1={20} x2={312} y2={536} stroke="#0d2232" strokeWidth="1"/>
                              {/* Door markers */}
                              <rect x={254} y={100} width={5} height={36} rx="1" fill="#4a8fe0" opacity="0.55"/>
                              <rect x={254} y={370} width={5} height={36} rx="1" fill="#fb923c" opacity="0.55"/>
                              <rect x={311} y={240} width={5} height={36} rx="1" fill="#22d3ee" opacity="0.55"/>
                            </g>

                            {/* Right corridor x=596–650 */}
                            <g>
                              <rect x={596} y={20} width={54} height={516} fill="#020c18"/>
                              {[60,105,150,195,240,285,330,375,420,465,510].map(y2 => (
                                <rect key={y2} x={603} y={y2} width={22} height={6} rx="1" fill="#00d4ff" opacity={rightActive||centerActive ? 0.12 : 0.04}/>
                              ))}
                              {(rightActive || centerActive) && [44,84,124,164,204,244,284,324,364,404,444,484,524].map(y2 => (
                                <polygon key={y2} points={`622,${y2} 622,${y2+12} 628,${y2+6}`} fill="#00d4ff" opacity="0.22"/>
                              ))}
                              <line x1={596} y1={20} x2={596} y2={536} stroke="#0d2232" strokeWidth="1"/>
                              <line x1={650} y1={20} x2={650} y2={536} stroke="#0d2232" strokeWidth="1"/>
                              <rect x={593} y={240} width={5} height={36} rx="1" fill="#22d3ee" opacity="0.55"/>
                              <rect x={649} y={100} width={5} height={36} rx="1" fill="#818cf8" opacity="0.55"/>
                              <rect x={649} y={370} width={5} height={36} rx="1" fill="#fbbf24" opacity="0.55"/>
                            </g>

                            {/* ── ROOMS ─────────────────────────────────────────────── */}
                            {ROOMS_DATA.map(room => {
                              const roomAgents  = Object.keys(room.agents).filter(a => AGENT_META[a]);
                              const roomRunning = roomAgents.filter(a => agentsData.summary?.[a]?.lastStatus === 'running').length;
                              const roomFailed  = roomAgents.filter(a => agentsData.summary?.[a]?.lastStatus === 'failed').length;
                              return (
                                <g key={room.id}>
                                  {/* Floor tiles */}
                                  <rect x={room.x} y={room.y} width={room.w} height={room.h} fill={`url(#floor-${room.id})`}/>
                                  {/* Active room ambient glow */}
                                  {roomRunning > 0 && (
                                    <rect x={room.x} y={room.y} width={room.w} height={room.h} fill={room.color} opacity="0.04">
                                      <animate attributeName="opacity" values="0.04;0.09;0.04" dur="2.2s" repeatCount="indefinite"/>
                                    </rect>
                                  )}
                                  {/* Walls */}
                                  <rect x={room.x} y={room.y} width={room.w} height={room.h} fill="none" stroke={room.color} strokeWidth="2" rx="2" opacity={roomRunning > 0 ? 0.65 : 0.22}/>
                                  {/* Corner rivets */}
                                  {[[room.x+4,room.y+4],[room.x+room.w-4,room.y+4],[room.x+4,room.y+room.h-4],[room.x+room.w-4,room.y+room.h-4]].map(([rx2,ry2],i) => (
                                    <circle key={i} cx={rx2} cy={ry2} r="2.5" fill={room.color} opacity="0.28"/>
                                  ))}
                                  {/* Nameplate */}
                                  <rect x={room.x+7} y={room.y+7} width={136} height={24} rx="3" fill="#030c18" stroke={room.color} strokeWidth="0.5" opacity="0.82"/>
                                  <text x={room.x+13} y={room.y+16} fill={room.color} fontSize="7" fontWeight="800" fontFamily="monospace" letterSpacing="2" opacity="0.68">{room.code}</text>
                                  <text x={room.x+13} y={room.y+26} fill="#8fb0cc" fontSize="9.5" fontWeight="700">{room.label}</text>
                                  {/* Status badges */}
                                  {roomRunning > 0 && (
                                    <g>
                                      <rect x={room.x+room.w-57} y={room.y+7} width={50} height={15} rx="3" fill="#f59e0b12" stroke="#f59e0b" strokeWidth="0.8" opacity="0.92"/>
                                      <text x={room.x+room.w-32} y={room.y+18} fill="#f59e0b" fontSize="7.5" fontWeight="800" textAnchor="middle" letterSpacing="0.8">{roomRunning} ACTIVE</text>
                                    </g>
                                  )}
                                  {roomFailed > 0 && (
                                    <g>
                                      <rect x={room.x+room.w-(roomRunning>0?112:57)} y={room.y+7} width={50} height={15} rx="3" fill="#ef444412" stroke="#ef4444" strokeWidth="0.8" opacity="0.92"/>
                                      <text x={room.x+room.w-(roomRunning>0?87:32)} y={room.y+18} fill="#ef4444" fontSize="7.5" fontWeight="800" textAnchor="middle" letterSpacing="0.8">{roomFailed} DOWN</text>
                                    </g>
                                  )}
                                  {/* Machine stations */}
                                  {Object.entries(room.agents).map(([agentKey, pos]) => {
                                    const meta  = AGENT_META[agentKey];
                                    if (!meta) return null;
                                    const s     = agentsData.summary?.[agentKey] || {};
                                    const isRun = s.lastStatus === 'running';
                                    const isFail= s.lastStatus === 'failed';
                                    const isDone= s.lastStatus === 'completed';
                                    const ledCol= isRun ? '#f59e0b' : isFail ? '#ef4444' : isDone ? '#10b981' : '#1d3050';
                                    const mtype = MACHINE_TYPE[agentKey] || 'default';
                                    const mx    = room.x + pos.rx;
                                    const my    = room.y + pos.ry;
                                    return (
                                      <g key={agentKey} style={{ cursor:'pointer' }}
                                         onMouseEnter={e => {
                                           const svgEl  = e.currentTarget.closest('svg');
                                           const svgBox = svgEl.parentElement.getBoundingClientRect();
                                           const elBox  = e.currentTarget.getBoundingClientRect();
                                           setFactoryTooltip({
                                             x: elBox.left - svgBox.left + elBox.width / 2,
                                             y: elBox.top  - svgBox.top  - 6,
                                             label: meta.label, color: meta.color,
                                             status:   s.lastStatus || 'idle',
                                             weekRuns: s.weekRuns || 0,
                                             weekCost: s.weekCost || 0,
                                             lastRun:  s.lastRun ? timeAgo(s.lastRun) : 'never',
                                             schedule: meta.schedule || meta.trigger,
                                           });
                                         }}
                                         onMouseLeave={() => setFactoryTooltip(null)}
                                      >
                                        {/* Drop shadow */}
                                        <rect x={mx+3} y={my+3} width={62} height={62} rx="4" fill="#000" opacity="0.35"/>
                                        {/* Machine SVG */}
                                        {Machine(mtype, mx, my, meta.color, isRun, isFail)}
                                        {/* Status LED */}
                                        <circle cx={mx+58} cy={my+4} r="3.5" fill={ledCol} opacity="0.92">
                                          {(isRun || isFail) && <animate attributeName="opacity" values="0.92;0.25;0.92" dur={isRun ? "0.9s" : "1.5s"} repeatCount="indefinite"/>}
                                        </circle>
                                        {/* Worker sprite */}
                                        {Sprite(mx+31, my+72, meta.color, isRun, isFail)}
                                        {/* Agent label */}
                                        <text x={mx+31} y={my+86} textAnchor="middle" fill="#607080" fontSize="6.5" fontWeight="700" letterSpacing="0.4">
                                          {meta.label.toUpperCase()}
                                        </text>
                                      </g>
                                    );
                                  })}
                                </g>
                              );
                            })}

                            {/* ── COURIER SPRITES ─────────────────────────────────── */}
                            {/* Couriers only travel when their corridor is active (delivering between rooms) */}
                            {/* Left corridor courier — waits at entry when nothing is running */}
                            {leftActive || centerActive ? (
                              <g opacity="0.72">
                                <animateMotion dur="9s" repeatCount="indefinite" calcMode="linear" path="M284,65 L284,510 L284,65"/>
                                <rect x="-7" y="-12" width="14" height="11" rx="2" fill="#00d4ff" opacity="0.6"/>
                                <rect x="-5" y="-22" width="10" height="9"  rx="2" fill="#0a1e30" stroke="#00d4ff" strokeWidth="0.8" opacity="0.9"/>
                                <rect x="-6" y="-17" width="12" height="9"  rx="1" fill="#00d4ff" opacity="0.4"/>
                              </g>
                            ) : (
                              /* Standing idle at corridor entrance */
                              <g transform="translate(277,75)" opacity="0.28">
                                <rect x="0"  y="0"  width="14" height="11" rx="2" fill="#00d4ff"/>
                                <rect x="2"  y="-10" width="10" height="9"  rx="2" fill="#0a1e30" stroke="#00d4ff" strokeWidth="0.8"/>
                                <rect x="1"  y="-5"  width="12" height="9"  rx="1" fill="#00d4ff" opacity="0.4"/>
                              </g>
                            )}
                            {/* Right corridor courier */}
                            {rightActive || centerActive ? (
                              <g opacity="0.72">
                                <animateMotion dur="12s" repeatCount="indefinite" calcMode="linear" path="M623,495 L623,65 L623,495"/>
                                <rect x="-7" y="-12" width="14" height="11" rx="2" fill="#a855f7" opacity="0.6"/>
                                <rect x="-5" y="-22" width="10" height="9"  rx="2" fill="#1a0a30" stroke="#a855f7" strokeWidth="0.8" opacity="0.9"/>
                                <rect x="-6" y="-17" width="12" height="9"  rx="1" fill="#a855f7" opacity="0.4"/>
                              </g>
                            ) : (
                              <g transform="translate(616,490)" opacity="0.28">
                                <rect x="0"  y="0"  width="14" height="11" rx="2" fill="#a855f7"/>
                                <rect x="2"  y="-10" width="10" height="9"  rx="2" fill="#1a0a30" stroke="#a855f7" strokeWidth="0.8"/>
                                <rect x="1"  y="-5"  width="12" height="9"  rx="1" fill="#a855f7" opacity="0.4"/>
                              </g>
                            )}

                            {/* ── LEGEND STRIP ─────────────────────────────────────── */}
                            <g transform="translate(20,544)">
                              <rect x="0" y="0" width="1060" height="18" rx="2" fill="#030910" stroke="#0c1928" strokeWidth="0.8" opacity="0.75"/>
                              {[
                                { col:'#10b981', label:'OPERATIONAL' },
                                { col:'#f59e0b', label:'RUNNING'     },
                                { col:'#ef4444', label:'FAULT'       },
                                { col:'#1d3050', label:'IDLE'        },
                              ].map(({col, label}, i) => (
                                <g key={label} transform={`translate(${14+i*130},0)`}>
                                  <circle cx="5" cy="9" r="3.5" fill={col} opacity="0.82"/>
                                  <text x="13" y="13" fill="#3a5570" fontSize="7.5" fontWeight="700" letterSpacing="0.4">{label}</text>
                                </g>
                              ))}
                              <text x="600"  y="13" fill="#192840" fontSize="7" letterSpacing="0.8">CONVEYOR CORRIDOR  ·  AUTO-ROUTE ACTIVE</text>
                              <text x="880"  y="13" fill="#192840" fontSize="7" letterSpacing="0.8">NEURORANK FACTORY FLOOR v2</text>
                            </g>
                          </svg>

                          {/* HTML tooltip — rendered over SVG */}
                          {factoryTooltip && (
                            <div style={{
                              position:'absolute',
                              left: factoryTooltip.x,
                              top:  factoryTooltip.y,
                              transform:'translate(-50%,-100%)',
                              background:'#050d1a',
                              border:`1px solid ${factoryTooltip.color}55`,
                              borderRadius:8,
                              padding:'10px 14px',
                              pointerEvents:'none',
                              minWidth:168,
                              boxShadow:`0 4px 20px ${factoryTooltip.color}1a`,
                              zIndex:20,
                            }}>
                              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                                <div style={{ width:8, height:8, borderRadius:'50%', background:factoryTooltip.color, flexShrink:0 }}/>
                                <span style={{ fontSize:11, fontWeight:800, color:'#cce0f4' }}>{factoryTooltip.label}</span>
                              </div>
                              {[
                                { label:'Status',    value: (factoryTooltip.status || 'idle').toUpperCase() },
                                { label:'Runs (7d)', value: factoryTooltip.weekRuns },
                                { label:'Cost (7d)', value: `$${(factoryTooltip.weekCost || 0).toFixed(4)}` },
                                { label:'Last run',  value: factoryTooltip.lastRun },
                                { label:'Schedule',  value: factoryTooltip.schedule },
                              ].map(({label, value}) => value !== undefined && value !== null && (
                                <div key={label} style={{ display:'flex', justifyContent:'space-between', gap:18, marginTop:3 }}>
                                  <span style={{ fontSize:9, color:'#2a4060', fontWeight:600 }}>{label}</span>
                                  <span style={{ fontSize:9, color:'#7aa8cc', fontWeight:700 }}>{value}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* ── OPERATIONS LOG ───────────────────────────────────────── */}
                  <div style={{ marginTop: 24 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#333', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>
                      Operations Log — {agentsData.runs?.length || 0} recent runs
                    </div>
                    <div style={{ background: '#06090f', border: '1px solid #111a24', borderRadius: 12, overflow: 'hidden' }}>
                      {/* Column headers */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 80px 70px 60px 130px', gap: 0, padding: '8px 16px', borderBottom: '1px solid #111a24', fontSize: 9, fontWeight: 700, color: '#2a3a4a', letterSpacing: 1.5, textTransform: 'uppercase' }}>
                        <span>Agent</span><span>Status</span><span>Duration</span><span>Cost</span><span>Trigger</span><span>Started</span>
                      </div>
                      {(agentsData.runs || []).length === 0 && (
                        <div style={{ padding: '24px 16px', fontSize: 12, color: '#2a3a4a' }}>No agent runs yet.</div>
                      )}
                      {(agentsData.runs || []).map((run, idx) => {
                        const meta = AGENT_META[run.agent_type] || { label: run.agent_type, color: '#555' };
                        const isCompleted = run.status === 'completed';
                        const isFailed    = run.status === 'failed';
                        const isRunning   = run.status === 'running';
                        const statusColor = isCompleted ? '#10b981' : isFailed ? '#ef4444' : isRunning ? '#f59e0b' : '#555';
                        const statusBg    = isCompleted ? '#10b98115' : isFailed ? '#ef444415' : isRunning ? '#f59e0b15' : '#1a1a1a';
                        const ago         = timeAgo(run.created_at);
                        return (
                          <div
                            key={run.id}
                            className="nr-log-row"
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 90px 80px 70px 60px 130px',
                              gap: 0,
                              padding: '10px 16px',
                              borderBottom: idx < (agentsData.runs.length - 1) ? '1px solid #0d1520' : 'none',
                              borderLeft: `3px solid ${meta.color}55`,
                              animationDelay: `${idx * 0.03}s`,
                              background: idx % 2 === 0 ? 'transparent' : '#07101a',
                            }}
                          >
                            <div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: meta.color }}>{meta.label}</span>
                              {run.input_summary && (
                                <div style={{ fontSize: 10, color: '#33405a', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>{run.input_summary}</div>
                              )}
                              {isFailed && run.error && (
                                <div style={{ fontSize: 10, color: '#ef444466', marginTop: 1 }}>{run.error.slice(0, 70)}</div>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: 1 }}>
                              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, padding: '2px 8px', borderRadius: 5, background: statusBg, color: statusColor }}>
                                {run.status.toUpperCase()}
                              </span>
                            </div>
                            <div style={{ fontSize: 11, color: '#445566', paddingTop: 2 }}>{run.duration_ms ? `${(run.duration_ms / 1000).toFixed(1)}s` : '—'}</div>
                            <div style={{ fontSize: 11, color: run.cost_usd > 0.001 ? '#f59e0b88' : '#2a3a4a', paddingTop: 2 }}>{run.cost_usd ? `$${Number(run.cost_usd).toFixed(5)}` : '—'}</div>
                            <div style={{ fontSize: 10, color: '#2a3a4a', paddingTop: 2 }}>{run.trigger}</div>
                            <div style={{ fontSize: 10, color: '#2a3a4a', paddingTop: 2 }}>{ago}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Console Agent IDs config */}
                  <AgentConfigPanel api={api} secret={secret} />

                  {/* Data Quality report */}
                  <DataQualityPanel api={api} secret={secret} />

                  {/* SEO Content controls */}
                  <SeoAgentPanel api={api} secret={secret} />

                  {/* Blog Writer controls */}
                  <BlogWriterPanel api={api} secret={secret} />

                  {/* Scout Digest controls */}
                  <ScoutDigestPanel api={api} secret={secret} />

                  {/* Onboarding Drip controls */}
                  <OnboardingPanel api={api} secret={secret} />

                  {/* Team Report controls */}
                  <TeamReportPanel api={api} secret={secret} />

                  {/* Re-engagement controls */}
                  <ReengagementPanel api={api} secret={secret} />

                  {/* Weekly Digest controls */}
                  <WeeklyDigestPanel api={api} secret={secret} />
                </>
              );
            })()}
          </>
        )}

        {/* EVENTS / ANALYTICS */}
        {section === 'Events' && (() => {
          const e = eventsData || {};
          const f = e.funnel || {};
          const pct = (num, denom) => denom ? Math.round((num / denom) * 100) : 0;
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Site Events & Analytics</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                    Pageviews, CTAs, funnel steps, email engagement, data anomalies.
                  </div>
                </div>
                <div style={{ flex: 1 }} />
                <button onClick={() => { loadEvents(); loadAnomalies(); }} disabled={eventsLoading || anomaliesLoading} style={{
                  background: 'transparent', border: '1px solid #333', color: '#888',
                  borderRadius: 7, padding: '7px 14px', fontSize: 12, cursor: 'pointer',
                }}>
                  {(eventsLoading || anomaliesLoading) ? '…' : 'Refresh'}
                </button>
              </div>

              <Section title="Visitors (7d)">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  <StatCard label="Unique Visitors (all)" value={e.uniqueVisitors || 0} color="#00d4ff" />
                  <StatCard label="Unique Today" value={e.uniqueVisitorsToday || 0} color="#10b981" />
                  <StatCard label="Total Events" value={e.totalEvents || 0} color="#a855f7" />
                </div>
              </Section>

              <Section title="Top-of-Funnel (7d)">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                  {[
                    { label: 'Pageviews', val: f.landing, color: '#00d4ff' },
                    { label: 'Landing CTA Clicks', val: f.landingCta, color: '#f59e0b', conv: pct(f.landingCta, f.landing) },
                    { label: 'Combine Started', val: f.combineStart, color: '#10b981', conv: pct(f.combineStart, f.landingCta) },
                    { label: 'Report Gate Viewed', val: f.reportGateView, color: '#a855f7' },
                    { label: 'Report Gate CTA', val: f.reportGateCta, color: '#22d3ee', conv: pct(f.reportGateCta, f.reportGateView) },
                    { label: 'Pricing Viewed', val: f.pricingView, color: '#00d4ff' },
                    { label: 'Pricing CTA Clicked', val: f.pricingCta, color: '#ef4444', conv: pct(f.pricingCta, f.pricingView) },
                  ].map(item => (
                    <div key={item.label} style={{ background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 10, padding: '14px 16px' }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: item.color }}>{item.val || 0}</div>
                      <div style={{ fontSize: 10, color: '#666', marginTop: 3 }}>{item.label}</div>
                      {item.conv !== undefined && (
                        <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                          {item.conv}% conversion
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Event Counts (7d)">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                  {Object.entries(e.countsByNameWeek || {}).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
                    <div key={name} style={{ background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 6, padding: '8px 12px', display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 11, color: '#888' }}>{name}</span>
                      <span style={{ fontSize: 12, color: '#00d4ff', fontWeight: 700 }}>{count}</span>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Top Pages (all time)">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {Object.entries(e.pathCounts || {}).sort((a, b) => b[1] - a[1]).slice(0, 20).map(([path, count]) => (
                    <div key={path} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: '#0d1117', borderRadius: 6 }}>
                      <div style={{ flex: 1, fontSize: 12, color: '#ccc', fontFamily: 'monospace' }}>{path}</div>
                      <div style={{ fontSize: 12, color: '#00d4ff', fontWeight: 700 }}>{count}</div>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title={`Email Engagement${e.emailTotal ? ` — ${e.emailTotal} events` : ''}`}>
                {!e.emailTotal ? (
                  <div style={{ fontSize: 12, color: '#555' }}>
                    No email events yet. Configure Resend webhook → <code style={{ color: '#00d4ff' }}>https://neurorank.app/api/email/webhook</code> and set <code>RESEND_WEBHOOK_SECRET</code> env var.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {Object.entries(e.emailCounts || {}).map(([type, count]) => {
                      const color = type.includes('opened') ? '#10b981' : type.includes('clicked') ? '#00d4ff' : type.includes('bounced') ? '#ef4444' : '#888';
                      return (
                        <div key={type} style={{ background: '#0d1117', border: `1px solid ${color}33`, borderRadius: 8, padding: '10px 14px' }}>
                          <div style={{ fontSize: 18, fontWeight: 800, color }}>{count}</div>
                          <div style={{ fontSize: 10, color: '#666', marginTop: 3 }}>{type}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Section>

              <Section title={`Data Anomalies${anomalies?.count ? ` — ${anomalies.count} suspicious sessions` : ''}`}>
                {anomalies?.sessions?.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <CsvButton rows={anomalies.sessions.map(s => ({ sessionId: s.sessionId, playerTag: s.playerTag, genre: s.genre, completedAt: s.completedAt, flags: s.flags.map(f => `${f.kind}${f.module ? ':' + f.module : ''}`).join(';') }))} filename={`anomalies-${new Date().toISOString().slice(0,10)}.csv`} columns={['sessionId', 'playerTag', 'genre', 'completedAt', 'flags']} />
                  </div>
                )}
                {!anomalies ? (
                  <div style={{ fontSize: 12, color: '#555' }}>Loading…</div>
                ) : !anomalies.count ? (
                  <div style={{ fontSize: 12, color: '#10b981' }}>
                    ✓ All {anomalies.totalChecked} completed sessions look clean.
                  </div>
                ) : (
                  <div style={{ background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 10, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #1a1a1a' }}>
                          {['Session', 'Player', 'Genre', 'Completed', 'Flags'].map(h => (
                            <th key={h} style={{ padding: '8px 12px', color: '#555', fontWeight: 600, textAlign: 'left' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {anomalies.sessions.map(s => (
                          <tr key={s.sessionId} style={{ borderBottom: '1px solid #111' }}>
                            <td style={{ padding: '7px 12px', color: '#00d4ff', fontFamily: 'monospace', fontSize: 10 }}>{(s.sessionId || '').slice(0, 10)}…</td>
                            <td style={{ padding: '7px 12px', color: '#ccc' }}>{s.playerTag || '—'}</td>
                            <td style={{ padding: '7px 12px', color: '#a855f7' }}>{(s.genre || '').toUpperCase()}</td>
                            <td style={{ padding: '7px 12px', color: '#555' }}>{s.completedAt ? new Date(s.completedAt).toLocaleDateString() : '—'}</td>
                            <td style={{ padding: '7px 12px' }}>
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {s.flags.map((f, i) => (
                                  <span key={i} title={f.detail || ''} style={{ fontSize: 10, color: '#ef4444', border: '1px solid #ef444433', borderRadius: 4, padding: '2px 6px' }}>
                                    {f.kind}{f.module ? `:${f.module}` : ''}
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              <Section title="Recent Raw Events">
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                  <CsvButton rows={(e.recent || []).map(ev => ({ at: ev.at, name: ev.name, sid: ev.sid, props: JSON.stringify(ev.props || {}), ref: ev.ref }))} filename={`events-${new Date().toISOString().slice(0,10)}.csv`} columns={['at', 'name', 'sid', 'props', 'ref']} />
                </div>
                <div style={{ maxHeight: 280, overflowY: 'auto', background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 8 }}>
                  {(e.recent || []).map((ev, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, padding: '5px 10px', borderBottom: '1px solid #111', fontSize: 10, fontFamily: 'monospace' }}>
                      <div style={{ color: '#555', width: 120, flexShrink: 0 }}>{new Date(ev.at).toLocaleTimeString()}</div>
                      <div style={{ color: '#00d4ff', width: 160, flexShrink: 0 }}>{ev.name}</div>
                      <div style={{ color: '#888', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{JSON.stringify(ev.props || {})}</div>
                    </div>
                  ))}
                </div>
              </Section>
            </>
          );
        })()}

        {/* SETTINGS */}
        {section === 'Settings' && (() => {
          const c = consoleConfig || {};
          const log = auditLog?.entries || [];
          const merged = { ...c, ...consoleConfigEdits };
          const agentIds = merged.agents || {};
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Settings & Audit</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                    Agent infrastructure config, global controls, audit trail.
                  </div>
                </div>
                <div style={{ flex: 1 }} />
                <button onClick={loadSettings} style={{ background: 'transparent', border: '1px solid #333', color: '#888', borderRadius: 7, padding: '7px 14px', fontSize: 12, cursor: 'pointer' }}>
                  Refresh
                </button>
              </div>

              <Section title="Global Controls">
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 10, padding: '14px 18px' }}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Managed Agents</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: agentsPaused ? '#ef4444' : '#10b981' }}>
                        {agentsPaused ? 'PAUSED' : 'RUNNING'}
                      </span>
                      <button onClick={toggleAgentsPause} style={{
                        background: agentsPaused ? '#10b98122' : '#ef444422',
                        border: `1px solid ${agentsPaused ? '#10b98155' : '#ef444455'}`,
                        color: agentsPaused ? '#10b981' : '#ef4444',
                        borderRadius: 6, padding: '5px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                      }}>
                        {agentsPaused ? 'Resume' : 'Pause All'}
                      </button>
                    </div>
                  </div>
                </div>
              </Section>

              <Section title="Environment Status">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
                  {[
                    { name: 'ADMIN_SECRET', set: true, note: 'Gate for this dashboard' },
                    { name: 'SUPABASE_URL', set: true, note: 'DB endpoint' },
                    { name: 'ANTHROPIC_API_KEY', set: true, note: 'AI reports + agents' },
                    { name: 'RESEND_API_KEY', set: true, note: 'Email sending' },
                    { name: 'RESEND_WEBHOOK_SECRET', set: (eventsData?.emailTotal || 0) > 0, note: 'Email open/click tracking' },
                    { name: 'STRIPE_SECRET_KEY', set: true, note: 'Billing' },
                  ].map(ev => (
                    <div key={ev.name} style={{ background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: ev.set ? '#10b981' : '#ef4444' }} />
                        <span style={{ fontSize: 11, color: '#ccc', fontFamily: 'monospace' }}>{ev.name}</span>
                      </div>
                      <div style={{ fontSize: 10, color: '#555' }}>{ev.note}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 10, color: '#555', marginTop: 10 }}>
                  Env vars are managed in Railway dashboard. "Set" status is inferred — red means functionality is dark.
                </div>
              </Section>

              <Section title="Anthropic Console Config">
                <div style={{ fontSize: 10, color: '#555', marginBottom: 8 }}>
                  Environment ID + managed-agent IDs. Applied immediately on save.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <label style={{ fontSize: 11, color: '#888' }}>
                    environment_id
                    <input
                      value={merged.environment_id || ''}
                      onChange={e => setConsoleConfigEdits(prev => ({ ...prev, environment_id: e.target.value }))}
                      style={{ width: '100%', marginTop: 3, background: '#0d1117', border: '1px solid #222', color: '#ccc', borderRadius: 6, padding: '6px 10px', fontSize: 11, fontFamily: 'monospace', boxSizing: 'border-box' }}
                    />
                  </label>
                  {['reddit_monitor', 'mention_monitor', 'esports_research', 'org_outreach', 'seo_content', 'data_quality', 'scout_digest', 'onboarding', 'reengagement', 'team_report', 'blog_writer', 'weekly_digest', 'c2_content', 'anomaly_detector'].map(k => (
                    <label key={k} style={{ fontSize: 11, color: '#888' }}>
                      {k}
                      <input
                        value={(consoleConfigEdits.agents?.[k] ?? agentIds[k]) || ''}
                        onChange={e => setConsoleConfigEdits(prev => ({
                          ...prev,
                          agents: { ...(prev.agents || agentIds), [k]: e.target.value }
                        }))}
                        style={{ width: '100%', marginTop: 3, background: '#0d1117', border: '1px solid #222', color: '#ccc', borderRadius: 6, padding: '6px 10px', fontSize: 11, fontFamily: 'monospace', boxSizing: 'border-box' }}
                      />
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
                  <button onClick={saveConsoleConfig} disabled={!Object.keys(consoleConfigEdits).length} style={{
                    background: Object.keys(consoleConfigEdits).length ? '#00d4ff22' : '#111',
                    border: `1px solid ${Object.keys(consoleConfigEdits).length ? '#00d4ff55' : '#222'}`,
                    color: Object.keys(consoleConfigEdits).length ? '#00d4ff' : '#444',
                    borderRadius: 6, padding: '7px 18px', fontSize: 12, fontWeight: 700,
                    cursor: Object.keys(consoleConfigEdits).length ? 'pointer' : 'not-allowed',
                  }}>Save</button>
                  {settingsSaveMsg && <span style={{ fontSize: 11, color: settingsSaveMsg.includes('fail') ? '#ef4444' : '#10b981' }}>{settingsSaveMsg}</span>}
                </div>
              </Section>

              <Section title={`Admin Action Audit Log${log.length ? ` — last ${log.length}` : ''}`}>
                {!log.length ? (
                  <div style={{ fontSize: 12, color: '#555' }}>No actions logged yet.</div>
                ) : (
                  <div style={{ maxHeight: 360, overflowY: 'auto', background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#0d1117' }}>
                        <tr style={{ borderBottom: '1px solid #1a1a1a' }}>
                          {['When', 'Action', 'Details'].map(h => (
                            <th key={h} style={{ padding: '8px 12px', color: '#555', fontWeight: 600, textAlign: 'left' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {log.map((entry, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #111' }}>
                            <td style={{ padding: '7px 12px', color: '#555', whiteSpace: 'nowrap', fontSize: 10 }}>{new Date(entry.at).toLocaleString()}</td>
                            <td style={{ padding: '7px 12px', color: '#00d4ff', fontFamily: 'monospace' }}>{entry.action}</td>
                            <td style={{ padding: '7px 12px', color: '#888', fontFamily: 'monospace', fontSize: 10, wordBreak: 'break-all' }}>
                              {JSON.stringify(entry.details)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>
            </>
          );
        })()}

        {/* ORG OUTREACH */}
        {section === 'Orgs' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Org Outreach</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  Research esports orgs, generate intel, draft outreach emails for Team tier sales.
                </div>
              </div>
              <div style={{ flex: 1 }} />
              <button onClick={loadOrgs} disabled={orgsLoading} style={{
                background: 'transparent', border: '1px solid #333', color: '#888',
                borderRadius: 7, padding: '7px 14px', fontSize: 12, cursor: 'pointer',
              }}>
                {orgsLoading ? '…' : 'Reload'}
              </button>
            </div>

            <Section title="Research a New Org">
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={orgResearchName}
                  onChange={e => setOrgResearchName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !orgResearching) researchOrg(); }}
                  placeholder="e.g. Cloud9, Team Liquid, G2 Esports"
                  style={{ flex: 1, background: '#0d1117', border: '1px solid #222', borderRadius: 8, color: '#ccc', padding: '10px 14px', fontSize: 13, boxSizing: 'border-box' }}
                />
                <button
                  onClick={researchOrg}
                  disabled={orgResearching || !orgResearchName.trim()}
                  style={{ background: orgResearching ? '#a855f711' : '#a855f722', border: '1px solid #a855f755', color: '#a855f7', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: orgResearching || !orgResearchName.trim() ? 'not-allowed' : 'pointer' }}
                >
                  {orgResearching ? 'Researching…' : 'Research'}
                </button>
              </div>
              <div style={{ fontSize: 11, color: '#555', marginTop: 8 }}>
                Runs web-search intel gathering (Claude + web_search tool). Takes 15-30 seconds.
              </div>
            </Section>

            <Section title={`Researched Orgs${orgs.length ? ` — ${orgs.length}` : ''}`}>
              {!orgs.length ? (
                <div style={{ fontSize: 13, color: '#444', padding: '12px 0' }}>
                  {orgsLoading ? 'Loading…' : 'No orgs researched yet. Try a few names above.'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {orgs.map((o, i) => (
                    <div key={o.orgName || i} style={{ background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 10, padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{o.orgName}</div>
                          {o.researchedAt && <div style={{ fontSize: 10, color: '#555', marginTop: 2 }}>Researched {new Date(o.researchedAt).toLocaleDateString()}</div>}
                        </div>
                        <button onClick={() => draftOrgOutreach(o.orgName)} disabled={orgDrafting} style={{ background: '#a855f722', border: '1px solid #a855f755', color: '#a855f7', borderRadius: 6, padding: '6px 14px', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>
                          Draft Outreach
                        </button>
                      </div>
                      {o.summary && <div style={{ fontSize: 12, color: '#aaa', lineHeight: 1.6, marginBottom: 8 }}>{o.summary}</div>}
                      {Array.isArray(o.teams) && o.teams.length > 0 && (
                        <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
                          <span style={{ color: '#555' }}>Teams: </span>{o.teams.join(', ')}
                        </div>
                      )}
                      {Array.isArray(o.contacts) && o.contacts.length > 0 && (
                        <div style={{ fontSize: 11, color: '#888' }}>
                          <span style={{ color: '#555' }}>Contacts: </span>
                          {o.contacts.map((c, j) => (
                            <span key={j} style={{ color: '#00d4ff' }}>
                              {c.name || c.email}{c.role ? ` (${c.role})` : ''}{j < o.contacts.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </>
        )}

        </div>
      )}

      {/* ============ TOASTS ============ */}
      <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 11000, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 360 }}>
        {toasts.map(t => {
          const color = t.kind === 'success' ? '#10b981' : t.kind === 'error' ? '#ef4444' : t.kind === 'warning' ? '#f59e0b' : '#00d4ff';
          return (
            <div key={t.id} style={{ background: '#0d1117', border: `1px solid ${color}55`, borderLeft: `4px solid ${color}`, borderRadius: 8, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, animation: 'nr-toast-in 0.2s ease', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
              <div style={{ flex: 1, fontSize: 12, color: '#ccc', lineHeight: 1.4 }}>{t.message}</div>
              {t.action && (
                <button onClick={() => { t.action.onClick?.(); dismissToast(t.id); }} style={{ background: 'transparent', border: `1px solid ${color}55`, color, borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>
                  {t.action.label}
                </button>
              )}
              <button onClick={() => dismissToast(t.id)} style={{ background: 'transparent', border: 'none', color: '#555', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
            </div>
          );
        })}
      </div>
      <style>{`@keyframes nr-toast-in { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }`}</style>

      {/* ============ MODALS ============ */}

      {/* Global search modal */}
      {searchOpen && (
        <div onClick={() => setSearchOpen(false)} style={{ position: 'fixed', inset: 0, background: '#000b', zIndex: 10000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 80 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#0d1117', border: '1px solid #1a2a3a', borderRadius: 12, width: 'min(720px, 90%)', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #1a1a1a', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 16, color: '#00d4ff' }}>🔍</span>
              <input
                autoFocus
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                placeholder="Search users, profiles, sessions, events… (min 2 chars)"
                style={{ flex: 1, background: 'transparent', border: 'none', color: '#fff', fontSize: 14, outline: 'none' }}
              />
              <span style={{ fontSize: 10, color: '#555' }}>ESC</span>
            </div>
            <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1 }}>
              {/* Command palette when empty or starts with > */}
              {(() => {
                const showCommands = !searchQ || searchQ.startsWith('>');
                if (!showCommands) return null;
                const filter = searchQ.startsWith('>') ? searchQ.slice(1).trim().toLowerCase() : '';
                const COMMANDS = [
                  { label: 'Go to Overview', kind: 'nav', run: () => setSection('Overview') },
                  { label: 'Go to Events & Analytics', kind: 'nav', run: () => setSection('Events') },
                  { label: 'Go to Users', kind: 'nav', run: () => setSection('Users') },
                  { label: 'Go to CMO', kind: 'nav', run: () => setSection('CMO') },
                  { label: 'Go to Orgs', kind: 'nav', run: () => setSection('Orgs') },
                  { label: 'Go to Agents', kind: 'nav', run: () => setSection('Agents') },
                  { label: 'Go to Marketing', kind: 'nav', run: () => setSection('Marketing') },
                  { label: 'Go to Settings', kind: 'nav', run: () => setSection('Settings') },
                  { label: 'Go to Dashboard (home)', kind: 'nav', run: () => setSection(null) },
                  { label: 'Refresh data', kind: 'action', run: () => { load(); toast('Data refreshed', { kind: 'success' }); } },
                  { label: 'Pause / Resume Agents', kind: 'action', run: () => { toggleAgentsPause(); toast('Agents toggled', { kind: 'info' }); } },
                  { label: 'Run Reddit scan', kind: 'action', run: () => { runScan(); } },
                  { label: 'Scan mentions', kind: 'action', run: () => { runMentionMonitorAgent(); } },
                  { label: 'Load abandoned sessions', kind: 'action', run: () => loadAbandoned() },
                  { label: 'Load events analytics', kind: 'action', run: () => { loadEvents(); loadAnomalies(); } },
                  { label: 'Log out', kind: 'danger', run: () => { setAuthed(false); localStorage.removeItem('admin_secret'); } },
                ];
                const visible = filter ? COMMANDS.filter(c => c.label.toLowerCase().includes(filter)) : COMMANDS;
                return (
                  <div style={{ marginBottom: searchResults ? 18 : 0 }}>
                    <div style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
                      COMMANDS {searchQ ? '(filtered)' : '(type "> " to filter, or any text to search)'}
                    </div>
                    {visible.map((c, i) => (
                      <div key={i} onClick={() => { c.run(); setSearchOpen(false); setSearchQ(''); }}
                        style={{ padding: '8px 10px', background: '#060a0f', borderRadius: 6, marginBottom: 3, display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#0d1620'}
                        onMouseLeave={e => e.currentTarget.style.background = '#060a0f'}
                      >
                        <span style={{ fontSize: 10, color: c.kind === 'danger' ? '#ef4444' : c.kind === 'action' ? '#f59e0b' : '#00d4ff', width: 52, fontWeight: 700, letterSpacing: 0.5 }}>{c.kind.toUpperCase()}</span>
                        <span style={{ fontSize: 12, color: '#ccc', flex: 1 }}>{c.label}</span>
                        <span style={{ fontSize: 10, color: '#555' }}>↵</span>
                      </div>
                    ))}
                    {!visible.length && <div style={{ color: '#555', fontSize: 12 }}>No commands match.</div>}
                  </div>
                );
              })()}

              {searching && <div style={{ color: '#555', fontSize: 12 }}>Searching…</div>}
              {!searching && !searchResults && searchQ.length >= 2 && !searchQ.startsWith('>') && <div style={{ color: '#555', fontSize: 12 }}>No results.</div>}

              {searchResults && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  {searchResults.users?.length > 0 && (
                    <div>
                      <div style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>USERS ({searchResults.users.length})</div>
                      {searchResults.users.map(u => (
                        <div key={u.id} style={{ padding: '8px 10px', background: '#060a0f', borderRadius: 6, marginBottom: 4, display: 'flex', gap: 10, alignItems: 'center' }}>
                          <span style={{ fontSize: 12, color: '#ccc', flex: 1 }}>{u.email} <span style={{ color: '#555' }}>· {u.playerTag || '—'}</span></span>
                          {u.isAdmin && <span style={{ fontSize: 9, color: '#00d4ff', border: '1px solid #00d4ff55', borderRadius: 4, padding: '2px 6px' }}>ADMIN</span>}
                          <button onClick={() => { setSearchOpen(false); setSection('Users'); }} style={{ background: 'transparent', border: '1px solid #333', color: '#888', borderRadius: 4, padding: '3px 8px', fontSize: 10, cursor: 'pointer' }}>Open</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {searchResults.profiles?.length > 0 && (
                    <div>
                      <div style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>PROFILES ({searchResults.profiles.length})</div>
                      {searchResults.profiles.map(p => (
                        <div key={p.shareableId} style={{ padding: '8px 10px', background: '#060a0f', borderRadius: 6, marginBottom: 4, display: 'flex', gap: 10, alignItems: 'center' }}>
                          <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#00d4ff' }}>{p.shareableId?.slice(0, 10)}…</span>
                          <span style={{ fontSize: 12, color: '#ccc' }}>{p.playerTag}</span>
                          <span style={{ fontSize: 11, color: '#f59e0b', flex: 1 }}>{p.archetype}</span>
                          {p.flagged && <span style={{ fontSize: 9, color: '#ef4444', border: '1px solid #ef444455', borderRadius: 4, padding: '2px 6px' }}>FLAGGED</span>}
                          <a href={`/profile/${p.shareableId}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: '#00d4ff', textDecoration: 'none', border: '1px solid #00d4ff33', borderRadius: 4, padding: '3px 8px' }}>Open ↗</a>
                        </div>
                      ))}
                    </div>
                  )}
                  {searchResults.sessions?.length > 0 && (
                    <div>
                      <div style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>SESSIONS ({searchResults.sessions.length})</div>
                      {searchResults.sessions.map(s => (
                        <div key={s.sessionId} style={{ padding: '8px 10px', background: '#060a0f', borderRadius: 6, marginBottom: 4, display: 'flex', gap: 10, alignItems: 'center' }}>
                          <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#00d4ff' }}>{(s.sessionId || '').slice(0, 10)}…</span>
                          <span style={{ fontSize: 11, color: '#ccc' }}>{s.playerTag || '—'}</span>
                          <span style={{ fontSize: 11, color: '#a855f7' }}>{(s.genre || '').toUpperCase()}</span>
                          <span style={{ fontSize: 10, color: s.status === 'active' ? '#f59e0b' : '#10b981', flex: 1 }}>{s.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {searchResults.events?.length > 0 && (
                    <div>
                      <div style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>EVENTS ({searchResults.events.length})</div>
                      {searchResults.events.map((ev, i) => (
                        <div key={i} style={{ padding: '6px 10px', background: '#060a0f', borderRadius: 6, marginBottom: 3, display: 'flex', gap: 10, alignItems: 'center', fontSize: 10, fontFamily: 'monospace' }}>
                          <span style={{ color: '#555', width: 110 }}>{new Date(ev.at).toLocaleString()}</span>
                          <span style={{ color: '#00d4ff', width: 160 }}>{ev.name}</span>
                          <span style={{ color: '#888', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{JSON.stringify(ev.props || {})}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {searchResults.users?.length === 0 && searchResults.profiles?.length === 0 && searchResults.sessions?.length === 0 && searchResults.events?.length === 0 && (
                    <div style={{ color: '#555', fontSize: 12 }}>No results for "{searchQ}".</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Funnel drop-off drilldown */}
      {funnelModal && (
        <div onClick={() => setFunnelModal(null)} style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#0d1117', border: '1px solid #1a2a3a', borderRadius: 12, padding: 24, width: 'min(820px, 100%)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', flex: 1 }}>
                Dropped at: {funnelModal.module}
              </div>
              <button onClick={() => setFunnelModal(null)} style={{ background: 'transparent', border: '1px solid #333', color: '#888', borderRadius: 6, padding: '4px 12px', fontSize: 11, cursor: 'pointer' }}>Close</button>
            </div>

            {funnelModal.loading ? (
              <div style={{ color: '#888', fontSize: 13 }}>Loading…</div>
            ) : !funnelModal.sessions.length ? (
              <div style={{ color: '#444', fontSize: 13 }}>No sessions at this step.</div>
            ) : (
              <>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
                  {funnelModal.sessions.length} session{funnelModal.sessions.length !== 1 ? 's' : ''} ·{' '}
                  {funnelModal.sessions.filter(s => s.email).length} with email ·{' '}
                  Select rows to include in bulk email
                </div>
                <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #1a1a1a', borderRadius: 8, marginBottom: 16 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#0d1117' }}>
                      <tr style={{ borderBottom: '1px solid #1a1a1a' }}>
                        <th style={{ padding: '8px 10px', width: 30 }}>
                          <input type="checkbox"
                            checked={funnelModal.selected?.size === funnelModal.sessions.filter(s => s.userId).length && funnelModal.sessions.some(s => s.userId)}
                            onChange={e => {
                              const all = e.target.checked ? new Set(funnelModal.sessions.filter(s => s.userId).map(s => s.userId)) : new Set();
                              setFunnelModal(prev => ({ ...prev, selected: all }));
                            }}
                          />
                        </th>
                        {['Email', 'Genre', 'Age'].map(h => (
                          <th key={h} style={{ padding: '8px 10px', color: '#555', fontWeight: 600, textAlign: 'left' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {funnelModal.sessions.map(s => (
                        <tr key={s.sessionId} style={{ borderBottom: '1px solid #111', opacity: s.userId ? 1 : 0.5 }}>
                          <td style={{ padding: '6px 10px' }}>
                            {s.userId ? (
                              <input type="checkbox"
                                checked={funnelModal.selected?.has(s.userId) || false}
                                onChange={e => {
                                  setFunnelModal(prev => {
                                    const next = new Set(prev.selected || []);
                                    if (e.target.checked) next.add(s.userId); else next.delete(s.userId);
                                    return { ...prev, selected: next };
                                  });
                                }}
                              />
                            ) : null}
                          </td>
                          <td style={{ padding: '6px 10px', color: '#ccc' }}>{s.email || <span style={{ color: '#444' }}>anonymous</span>}</td>
                          <td style={{ padding: '6px 10px', color: '#a855f7' }}>{(s.genre || '').toUpperCase()}</td>
                          <td style={{ padding: '6px 10px', color: '#555' }}>{s.ageHours}h</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Subject</div>
                <input value={bulkEmailSubject} onChange={e => setBulkEmailSubject(e.target.value)}
                  style={{ width: '100%', background: '#060a0f', border: '1px solid #222', borderRadius: 6, color: '#ccc', padding: '8px 10px', fontSize: 12, boxSizing: 'border-box', marginBottom: 10 }}
                />
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Body</div>
                <textarea value={bulkEmailBody} onChange={e => setBulkEmailBody(e.target.value)} rows={5}
                  style={{ width: '100%', background: '#060a0f', border: '1px solid #222', borderRadius: 6, color: '#ccc', padding: '8px 10px', fontSize: 12, boxSizing: 'border-box', marginBottom: 14, resize: 'vertical' }}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => setFunnelModal(null)} style={{ background: 'transparent', border: '1px solid #333', color: '#888', borderRadius: 6, padding: '8px 16px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                  <button
                    onClick={sendFunnelEmail}
                    disabled={bulkSending || !(funnelModal.selected?.size)}
                    style={{ background: '#10b98122', border: '1px solid #10b98155', color: '#10b981', borderRadius: 6, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: bulkSending || !(funnelModal.selected?.size) ? 'not-allowed' : 'pointer' }}
                  >
                    {bulkSending ? 'Sending…' : `Send to ${funnelModal.selected?.size || 0}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Session Inspect */}
      {inspectModal && (
        <div onClick={() => setInspectModal(null)} style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#0d1117', border: '1px solid #1a2a3a', borderRadius: 12, padding: 24, width: 'min(600px, 100%)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', flex: 1 }}>
                Session {(inspectModal.sessionId || '').slice(0, 10)}…
              </div>
              <button onClick={() => setInspectModal(null)} style={{ background: 'transparent', border: '1px solid #333', color: '#888', borderRadius: 6, padding: '4px 12px', fontSize: 11, cursor: 'pointer' }}>Close</button>
            </div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 16, display: 'grid', gridTemplateColumns: '120px 1fr', gap: '6px 12px' }}>
              <div style={{ color: '#555' }}>User ID</div><div style={{ fontFamily: 'monospace', color: '#ccc' }}>{inspectModal.userId || '—'}</div>
              <div style={{ color: '#555' }}>Genre</div><div style={{ color: '#a855f7' }}>{(inspectModal.genre || '').toUpperCase()}</div>
              <div style={{ color: '#555' }}>Status</div><div style={{ color: '#f59e0b' }}>{inspectModal.status}</div>
              <div style={{ color: '#555' }}>Created</div><div style={{ color: '#888' }}>{inspectModal.createdAt ? new Date(inspectModal.createdAt).toLocaleString() : '—'}</div>
              {inspectModal.completedAt && <><div style={{ color: '#555' }}>Completed</div><div style={{ color: '#10b981' }}>{new Date(inspectModal.completedAt).toLocaleString()}</div></>}
            </div>
            <div style={{ fontSize: 11, color: '#555', marginBottom: 6, fontWeight: 700 }}>MODULES</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(inspectModal.modules || []).map(m => (
                <div key={m.module} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: '#060a0f', borderRadius: 6 }}>
                  <div style={{ fontSize: 11, color: m.completed ? '#10b981' : '#444', width: 16 }}>{m.completed ? '✓' : '○'}</div>
                  <div style={{ fontSize: 12, color: m.completed ? '#ccc' : '#555', flex: 1 }}>{m.module}</div>
                  <div style={{ fontSize: 10, color: '#555' }}>{m.trialCount ? `${m.trialCount} entries` : '—'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Org Outreach Draft */}
      {orgDraftModal && (
        <div onClick={() => setOrgDraftModal(null)} style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#0d1117', border: '1px solid #a855f744', borderRadius: 12, padding: 24, width: 'min(720px, 100%)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', flex: 1 }}>
                Outreach Draft — {orgDraftModal.orgName}
              </div>
              <button onClick={() => setOrgDraftModal(null)} style={{ background: 'transparent', border: '1px solid #333', color: '#888', borderRadius: 6, padding: '4px 12px', fontSize: 11, cursor: 'pointer' }}>Close</button>
            </div>
            {orgDrafting && !orgDraftModal.draft ? (
              <div style={{ color: '#a855f7', fontSize: 13 }}>Generating draft…</div>
            ) : orgDraftModal.error ? (
              <div style={{ color: '#ef4444', fontSize: 13 }}>Error: {orgDraftModal.error}</div>
            ) : orgDraftModal.draft ? (
              <>
                {orgDraftModal.draft.subject && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 10, color: '#555', marginBottom: 4, fontWeight: 700 }}>SUBJECT</div>
                    <div style={{ fontSize: 14, color: '#fff', padding: '8px 12px', background: '#060a0f', borderRadius: 6 }}>{orgDraftModal.draft.subject}</div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 10, color: '#555', marginBottom: 4, fontWeight: 700 }}>BODY</div>
                  <div style={{ fontSize: 13, color: '#ccc', lineHeight: 1.6, whiteSpace: 'pre-wrap', padding: '12px 14px', background: '#060a0f', borderRadius: 6 }}>
                    {orgDraftModal.draft.body || orgDraftModal.draft}
                  </div>
                </div>
                <div style={{ marginTop: 12 }}>
                  <button onClick={() => navigator.clipboard.writeText(typeof orgDraftModal.draft === 'string' ? orgDraftModal.draft : `${orgDraftModal.draft.subject || ''}\n\n${orgDraftModal.draft.body || ''}`)} style={{ background: 'transparent', border: '1px solid #333', color: '#888', borderRadius: 6, padding: '6px 14px', fontSize: 11, cursor: 'pointer' }}>Copy</button>
                </div>
              </>
            ) : (
              <div style={{ color: '#888', fontSize: 13 }}>No draft.</div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

// ── Data Quality Panel ────────────────────────────────────────────────────────
function DataQualityPanel({ api, secret }) {
  const [report, setReport]   = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    fetch(`${api}/api/admin/agents/data-quality`, { headers: { 'x-admin-secret': secret } })
      .then(r => r.json()).then(d => d && setReport(d)).catch(() => {});
  }, [api, secret]); // eslint-disable-line

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await fetch(`${api}/api/admin/agents/data-quality`, {
        method: 'POST', headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
      });
      const d = await r.json();
      if (d.ok) {
        // Reload full report
        const r2 = await fetch(`${api}/api/admin/agents/data-quality`, { headers: { 'x-admin-secret': secret } });
        setReport(await r2.json());
      }
    } catch {}
    setRunning(false);
  };

  const SEVERITY_COLOR = { high: '#ef4444', medium: '#f59e0b', low: '#555' };

  return (
    <Section title="Data Quality Monitor">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1, fontSize: 12, color: '#555' }}>
          Scans all profiles + sessions for impossible scores, bot patterns, and scoring bugs. Scheduled Sunday 9am UTC.
          {report && <span style={{ marginLeft: 8, color: report.flagCount > 0 ? '#ef4444' : '#10b981' }}>
            {report.flagCount > 0 ? `${report.flagCount} flags` : 'Clean'} · {report.profilesScanned} profiles · {new Date(report.generatedAt).toLocaleDateString()}
          </span>}
        </div>
        <button onClick={runNow} disabled={running} style={{
          background: running ? '#ef444411' : '#ef444422', border: '1px solid #ef444455',
          color: '#ef4444', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>{running ? 'Scanning…' : 'Scan Now'}</button>
      </div>

      {report?.summary && (
        <div style={{ fontSize: 12, color: '#888', background: '#0d1117', borderRadius: 8, padding: '10px 14px', marginBottom: 12, lineHeight: 1.6 }}>
          {report.summary}
        </div>
      )}

      {report?.flags?.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {report.flags.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: '#0d1117', borderRadius: 7, padding: '9px 12px', borderLeft: `3px solid ${SEVERITY_COLOR[f.severity] || '#555'}` }}>
              <div style={{ flex: 1, fontSize: 11, color: '#888', lineHeight: 1.5 }}>
                <span style={{ color: SEVERITY_COLOR[f.severity], fontWeight: 700, textTransform: 'uppercase', marginRight: 8, fontSize: 10 }}>{f.severity}</span>
                <span style={{ color: '#555', marginRight: 8 }}>{f.type}</span>
                {f.playerTag && <span style={{ color: '#aaa', marginRight: 8 }}>{f.playerTag}</span>}
                {f.detail}
              </div>
              {f.shareableId && (
                <a href={`/profile/${f.shareableId}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: '#00d4ff', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {f.shareableId} →
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {report && !report.flags?.length && (
        <div style={{ fontSize: 13, color: '#10b981', padding: '8px 0' }}>No issues found in last scan.</div>
      )}
    </Section>
  );
}

// ── SEO Content Agent Panel ───────────────────────────────────────────────────
function SeoAgentPanel({ api, secret }) {
  const [generating, setGenerating] = useState(false);
  const [status, setStatus] = useState('');
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    fetch(`${api}/api/admin/agents/seo-posts`, { headers: { 'x-admin-secret': secret } })
      .then(r => r.json())
      .then(d => setPosts(d.posts || []))
      .catch(() => {});
  }, [api, secret]); // eslint-disable-line

  const generate = async () => {
    setGenerating(true);
    setStatus('Generating…');
    try {
      const r = await fetch(`${api}/api/admin/agents/seo-generate`, {
        method: 'POST', headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
      });
      const d = await r.json();
      if (d.ok && d.post) {
        setStatus(`Written: "${d.post.title}"`);
        setPosts(prev => [{ ...d.post, wordCount: '~' }, ...(prev || [])]);
      } else {
        setStatus(d.note || d.error || 'Done');
      }
    } catch (e) { setStatus(`Error: ${e.message}`); }
    setGenerating(false);
  };

  return (
    <Section title="SEO Content Agent">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1, fontSize: 12, color: '#555' }}>
          Generates one 700-900 word blog post per run. Scheduled Mon/Wed/Fri 7am UTC.
          {posts !== null && <span style={{ color: '#818cf8', marginLeft: 8 }}>{posts.length} posts written</span>}
        </div>
        <button onClick={generate} disabled={generating} style={{
          background: generating ? '#818cf811' : '#818cf822', border: '1px solid #818cf855',
          color: '#818cf8', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>{generating ? 'Generating…' : 'Generate Now'}</button>
      </div>
      {status && <div style={{ fontSize: 12, color: '#aaa', marginBottom: 12, fontStyle: 'italic' }}>{status}</div>}
      {posts?.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {posts.slice(0, 8).map(p => (
            <div key={p.slug} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, background: '#0d1117', borderRadius: 7, padding: '8px 12px' }}>
              <div style={{ flex: 1, color: '#ccc' }}>{p.title}</div>
              <div style={{ color: '#555', whiteSpace: 'nowrap' }}>{p.wordCount}w</div>
              <div style={{ color: '#444', whiteSpace: 'nowrap' }}>{p.date}</div>
              <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" style={{ color: '#818cf8', textDecoration: 'none', fontSize: 11 }}>View →</a>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ── Scout Digest Panel ────────────────────────────────────────────────────────
function ScoutDigestPanel({ api, secret }) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const runDryRun = async () => {
    setSending(true);
    setResult(null);
    try {
      const r = await fetch(`${api}/api/admin/agents/scout-digest`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun: true }),
      });
      const d = await r.json();
      setResult({ ...d, mode: 'dry run' });
    } catch (e) { setResult({ error: e.message }); }
    setSending(false);
  };

  const runLive = async () => {
    setSending(true);
    setResult(null);
    try {
      const r = await fetch(`${api}/api/admin/agents/scout-digest`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun: false }),
      });
      const d = await r.json();
      setResult({ ...d, mode: 'live send' });
    } catch (e) { setResult({ error: e.message }); }
    setSending(false);
  };

  return (
    <Section title="Scout Digest">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: result ? 12 : 0 }}>
        <div style={{ flex: 1, fontSize: 12, color: '#555' }}>
          Weekly email to Scout Pro subscribers. Top 5 new profiles, personalised by genre. Scheduled Sunday 10am UTC.
        </div>
        <button onClick={runDryRun} disabled={sending} style={{
          background: 'transparent', border: '1px solid #333', color: '#555',
          borderRadius: 7, padding: '7px 14px', fontSize: 12, cursor: 'pointer',
        }}>{sending ? '…' : 'Dry Run'}</button>
        <button onClick={runLive} disabled={sending} style={{
          background: sending ? '#f59e0b11' : '#f59e0b22', border: '1px solid #f59e0b55',
          color: '#f59e0b', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>{sending ? 'Sending…' : 'Send Now'}</button>
      </div>
      {result && (
        <div style={{ background: '#0d1117', borderRadius: 8, padding: '12px 14px', fontSize: 12 }}>
          {result.error
            ? <span style={{ color: '#ef4444' }}>{result.error}</span>
            : <span style={{ color: '#aaa' }}>{result.mode} — {result.scouts} scouts, {result.profiles} new profiles, {result.sent} emails sent</span>
          }
        </div>
      )}
    </Section>
  );
}

// ── Onboarding Panel ──────────────────────────────────────────────────────────
function OnboardingPanel({ api, secret }) {
  const [stats,   setStats]   = useState(null);
  const [running, setRunning] = useState(false);
  const [result,  setResult]  = useState(null);

  useEffect(() => {
    fetch(`${api}/api/admin/agents/onboarding`, { headers: { 'x-admin-secret': secret } })
      .then(r => r.json()).then(d => d && setStats(d)).catch(() => {});
  }, [api, secret]); // eslint-disable-line

  const runNow = async () => {
    setRunning(true);
    setResult(null);
    try {
      const r = await fetch(`${api}/api/admin/agents/onboarding`, {
        method: 'POST', headers: { 'x-admin-secret': secret },
      });
      const d = await r.json();
      setResult(d);
    } catch (e) { setResult({ error: e.message }); }
    setRunning(false);
  };

  return (
    <Section title="Onboarding Drip">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: result ? 12 : 0 }}>
        <div style={{ flex: 1, fontSize: 12, color: '#555' }}>
          3-day email drip for new users. Day 0 fires on registration. Days 1+2 via daily cron (10am UTC).
          {stats && <span style={{ color: '#888', marginLeft: 8 }}>Queue: {stats.queueSize} · Day 1 pending: {stats.day1Pending} · Day 2 pending: {stats.day2Pending}</span>}
        </div>
        <button onClick={runNow} disabled={running} style={{
          background: running ? '#34d39911' : '#34d39922', border: '1px solid #34d39955',
          color: '#34d399', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>{running ? 'Running…' : 'Run Now'}</button>
      </div>
      {result && (
        <div style={{ background: '#0d1117', borderRadius: 8, padding: '12px 14px', fontSize: 12 }}>
          {result.error
            ? <span style={{ color: '#ef4444' }}>{result.error}</span>
            : <span style={{ color: '#aaa' }}>Day 1 sent: {result.day1Sent} · Day 2 sent: {result.day2Sent}</span>
          }
        </div>
      )}
    </Section>
  );
}

// ── Team Report Panel ─────────────────────────────────────────────────────────
function TeamReportPanel({ api, secret }) {
  const [sending, setSending] = useState(false);
  const [result, setResult]   = useState(null);

  const run = async (dryRun) => {
    setSending(true);
    setResult(null);
    try {
      const r = await fetch(`${api}/api/admin/agents/team-report`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun }),
      });
      const d = await r.json();
      setResult({ ...d, mode: dryRun ? 'dry run' : 'live send' });
    } catch (e) { setResult({ error: e.message }); }
    setSending(false);
  };

  return (
    <Section title="Team Report">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: result ? 12 : 0 }}>
        <div style={{ flex: 1, fontSize: 12, color: '#555' }}>
          Weekly cognitive analysis for Team tier subscribers. Roster stats, AI intelligence report, top performers. Scheduled Monday 8am UTC.
        </div>
        <button onClick={() => run(true)} disabled={sending} style={{
          background: 'transparent', border: '1px solid #333', color: '#555',
          borderRadius: 7, padding: '7px 14px', fontSize: 12, cursor: 'pointer',
        }}>{sending ? '…' : 'Dry Run'}</button>
        <button onClick={() => run(false)} disabled={sending} style={{
          background: sending ? '#06b6d411' : '#06b6d422', border: '1px solid #06b6d455',
          color: '#06b6d4', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>{sending ? 'Sending…' : 'Send Now'}</button>
      </div>
      {result && (
        <div style={{ background: '#0d1117', borderRadius: 8, padding: '12px 14px', fontSize: 12 }}>
          {result.error
            ? <span style={{ color: '#ef4444' }}>{result.error}</span>
            : <span style={{ color: '#aaa' }}>{result.mode} — {result.total} team accounts, {result.sent} reports sent{result.results?.filter(r => r.status === 'skipped').length ? `, ${result.results.filter(r => r.status === 'skipped').length} skipped (no roster)` : ''}</span>
          }
        </div>
      )}
    </Section>
  );
}

// ── Re-engagement Panel ───────────────────────────────────────────────────────
function ReengagementPanel({ api, secret }) {
  const [sending, setSending] = useState(false);
  const [result, setResult]   = useState(null);

  const run = async (dryRun) => {
    setSending(true);
    setResult(null);
    try {
      const r = await fetch(`${api}/api/admin/agents/reengagement`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun }),
      });
      const d = await r.json();
      setResult({ ...d, mode: dryRun ? 'dry run' : 'live send' });
    } catch (e) { setResult({ error: e.message }); }
    setSending(false);
  };

  return (
    <Section title="Re-engagement">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: result ? 12 : 0 }}>
        <div style={{ flex: 1, fontSize: 12, color: '#555' }}>
          Personalised re-engagement emails to lapsed claimed players (14+ days since combine, 21-day dedup). Claude Haiku hook referencing their archetype + weakest dimension. Scheduled Wed + Sat noon UTC.
        </div>
        <button onClick={() => run(true)} disabled={sending} style={{
          background: 'transparent', border: '1px solid #333', color: '#555',
          borderRadius: 7, padding: '7px 14px', fontSize: 12, cursor: 'pointer',
        }}>{sending ? '…' : 'Dry Run'}</button>
        <button onClick={() => run(false)} disabled={sending} style={{
          background: sending ? '#fb923c11' : '#fb923c22', border: '1px solid #fb923c55',
          color: '#fb923c', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>{sending ? 'Sending…' : 'Send Now'}</button>
      </div>
      {result && (
        <div style={{ background: '#0d1117', borderRadius: 8, padding: '12px 14px', fontSize: 12 }}>
          {result.error
            ? <span style={{ color: '#ef4444' }}>{result.error}</span>
            : <>
                <span style={{ color: '#aaa' }}>{result.mode} — {result.total} eligible, {result.sent} emails sent</span>
                {result.results?.slice(0, 3).map((r, i) => r.hook && (
                  <div key={i} style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #1a1a2e' }}>
                    <span style={{ color: '#fb923c', fontSize: 11 }}>{r.playerTag}</span>
                    <span style={{ color: '#555', fontSize: 11 }}> ({r.archetype}, {r.overallScore}/100) — </span>
                    <span style={{ color: '#888', fontSize: 11 }}>{r.hook}</span>
                  </div>
                ))}
              </>
          }
        </div>
      )}
    </Section>
  );
}

// ── Agent Config Panel ────────────────────────────────────────────────────────
function AgentConfigPanel({ api, secret }) {
  const CONSOLE_AGENTS = [
    { key: 'coach_free',       label: 'Coach (Free)',        hint: 'Haiku, short coaching for free-tier players' },
    { key: 'coach_pro',        label: 'Coach (Pro)',          hint: 'Sonnet, full breakdown for Player Pro' },
    { key: 'esports_research', label: 'Esports Research',    hint: 'Sonnet + web_search, org intelligence' },
    { key: 'org_outreach',     label: 'Org Outreach',        hint: 'Haiku, draft outreach emails' },
    { key: 'watchlist',        label: 'Watchlist',           hint: 'Haiku, scout retest notifications' },
    { key: 'reddit_monitor',   label: 'Reddit Monitor',      hint: 'Sonnet + web_fetch, cron 3x daily' },
    { key: 'mention_monitor',  label: 'Mention Monitor',     hint: 'Haiku + web_search, cron 2x daily' },
    { key: 'c2_content',       label: 'C2 Content Generator',hint: 'Sonnet + web_search, daily content' },
    { key: 'coaching_session', label: 'Coaching Session',    hint: 'Sonnet, interactive player chat' },
  ];

  const [config, setConfig]   = useState(null);
  const [editing, setEditing] = useState({});
  const [envId, setEnvId]     = useState('');
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);

  useEffect(() => {
    fetch(`${api}/api/admin/agents/console-config`, { headers: { 'x-admin-secret': secret } })
      .then(r => r.json())
      .then(d => {
        setConfig(d);
        setEditing(d.agents || {});
        setEnvId(d.environment_id || '');
      })
      .catch(() => {});
  }, [api, secret]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const r = await fetch(`${api}/api/admin/agents/console-config`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ agents: editing, environment_id: envId }),
      });
      const d = await r.json();
      if (d.ok) { setConfig(d); setSaved(true); setTimeout(() => setSaved(false), 3000); }
    } catch {}
    setSaving(false);
  };

  const inputStyle = {
    background: '#060a0f', border: '1px solid #1a2a3a', borderRadius: 6,
    padding: '7px 10px', color: '#fff', fontSize: 11, fontFamily: 'monospace',
    width: '100%', boxSizing: 'border-box', outline: 'none',
  };

  return (
    <Section title="Console Agent IDs">
      <div style={{ fontSize: 12, color: '#555', marginBottom: 16 }}>
        Paste agent IDs from the{' '}
        <a href="https://console.anthropic.com" target="_blank" rel="noreferrer" style={{ color: '#00d4ff', textDecoration: 'none' }}>
          Anthropic Console
        </a>
        {' '}→ Agents. IDs take effect immediately — no redeploy needed.
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Environment ID <span style={{ color: '#555', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(shared across all agents)</span>
        </div>
        <input
          style={inputStyle}
          value={envId}
          onChange={e => setEnvId(e.target.value)}
          placeholder="env_…"
          spellCheck={false}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {CONSOLE_AGENTS.map(({ key, label, hint }) => {
          const val     = editing[key] || '';
          const isSet   = val.startsWith('ag_');
          return (
            <div key={key}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: isSet ? '#10b981' : '#333', flexShrink: 0 }} />
                <div style={{ fontSize: 12, fontWeight: 600, color: isSet ? '#fff' : '#777', flex: 1 }}>{label}</div>
                <div style={{ fontSize: 10, color: '#444' }}>{hint}</div>
              </div>
              <input
                style={{ ...inputStyle, borderColor: isSet ? '#1a3a2a' : '#1a2a3a' }}
                value={val}
                onChange={e => setEditing(prev => ({ ...prev, [key]: e.target.value }))}
                placeholder={`ag_${key}_…`}
                spellCheck={false}
              />
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={save} disabled={saving} style={{
          background: saving ? '#00d4ff11' : '#00d4ff22', border: '1px solid #00d4ff55',
          color: '#00d4ff', borderRadius: 7, padding: '8px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>{saving ? 'Saving…' : 'Save IDs'}</button>
        {saved && <span style={{ fontSize: 12, color: '#10b981' }}>Saved — agents will use new IDs immediately</span>}
        {config && (
          <span style={{ fontSize: 11, color: '#444' }}>
            {Object.values(editing).filter(v => v?.startsWith('ag_')).length} / {CONSOLE_AGENTS.length} configured
          </span>
        )}
      </div>
    </Section>
  );
}

// ── Blog Writer Panel ─────────────────────────────────────────────────────────
function BlogWriterPanel({ api, secret }) {
  const [running, setRunning] = useState(false);
  const [result, setResult]   = useState(null);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const r = await fetch(`${api}/api/admin/agents/blog-writer`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
      });
      const d = await r.json();
      setResult(d);
    } catch (e) { setResult({ error: e.message }); }
    setRunning(false);
  };

  return (
    <Section title="Blog Writer">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: result ? 12 : 0 }}>
        <div style={{ flex: 1, fontSize: 12, color: '#555' }}>
          Data-driven weekly post from live platform stats (trending archetype, genre breakdown, top scores). Saves to blog alongside evergreen SEO posts. Scheduled Friday 7am UTC.
        </div>
        <button onClick={run} disabled={running} style={{
          background: running ? '#6366f111' : '#6366f122', border: '1px solid #6366f155',
          color: '#6366f1', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>{running ? 'Writing…' : 'Run Now'}</button>
      </div>
      {result && (
        <div style={{ background: '#0d1117', borderRadius: 8, padding: '12px 14px', fontSize: 12 }}>
          {result.error
            ? <span style={{ color: '#ef4444' }}>{result.error}</span>
            : result.note
              ? <span style={{ color: '#555' }}>{result.note}</span>
              : <span style={{ color: '#aaa' }}>
                  Wrote <span style={{ color: '#6366f1', fontWeight: 600 }}>{result.post?.title}</span>
                  {result.post?.dataStats && ` — ${result.post.dataStats.weekCount} profiles this week`}
                </span>
          }
        </div>
      )}
    </Section>
  );
}

// ── Weekly Digest Panel ───────────────────────────────────────────────────────
function WeeklyDigestPanel({ api, secret }) {
  const [sending, setSending] = useState(false);
  const [result, setResult]   = useState(null);

  const run = async (dryRun) => {
    setSending(true);
    setResult(null);
    try {
      const r = await fetch(`${api}/api/admin/agents/weekly-digest`, {
        method: 'POST',
        headers: { 'x-admin-secret': secret, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun }),
      });
      const d = await r.json();
      setResult({ ...d, mode: dryRun ? 'dry run' : 'live send' });
    } catch (e) { setResult({ error: e.message }); }
    setSending(false);
  };

  return (
    <Section title="Weekly Digest">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: result ? 12 : 0 }}>
        <div style={{ flex: 1, fontSize: 12, color: '#555' }}>
          Platform weekly summary to all users — new profiles, top scores, trending archetype, genre split. Personalised cohort rank for users with profiles. Scheduled Monday 9am UTC.
        </div>
        <button onClick={() => run(true)} disabled={sending} style={{
          background: 'transparent', border: '1px solid #333', color: '#555',
          borderRadius: 7, padding: '7px 14px', fontSize: 12, cursor: 'pointer',
        }}>{sending ? '…' : 'Dry Run'}</button>
        <button onClick={() => run(false)} disabled={sending} style={{
          background: sending ? '#84cc1611' : '#84cc1622', border: '1px solid #84cc1655',
          color: '#84cc16', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>{sending ? 'Sending…' : 'Send Now'}</button>
      </div>
      {result && (
        <div style={{ background: '#0d1117', borderRadius: 8, padding: '12px 14px', fontSize: 12 }}>
          {result.error
            ? <span style={{ color: '#ef4444' }}>{result.error}</span>
            : <>
                <span style={{ color: '#aaa' }}>{result.mode} — {result.total} eligible users, {result.sent} sent</span>
                {result.headline && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #1a1a2e', color: '#84cc16', fontSize: 11 }}>
                    "{result.headline}"
                  </div>
                )}
              </>
          }
        </div>
      )}
    </Section>
  );
}

// ── Daily Checklist ───────────────────────────────────────────────────────────

const CHECKLIST = [
  {
    group: 'Morning — Automated',
    color: '#10b981',
    items: [
      { id: 'auto_content', label: 'Content generation ran (9am Task Scheduler)', note: 'Check output/YYYY-MM-DD/ folder exists' },
      { id: 'auto_buffer', label: 'Twitter posts queued in Buffer', note: 'Auto-queued from daily output' },
    ],
  },
  {
    group: 'Review & Approve',
    color: '#00d4ff',
    items: [
      { id: 'review_twitter', label: 'Reviewed twitter-posts.txt — picked best 2-3' },
      { id: 'review_reddit', label: 'Reviewed reddit-posts.txt' },
      { id: 'review_discord', label: 'Reviewed discord-messages.txt' },
      { id: 'review_tiktok', label: 'Reviewed tiktok-ideas.txt' },
    ],
  },
  {
    group: 'Post Manually',
    color: '#f59e0b',
    items: [
      { id: 'post_reddit', label: 'Reddit post published to r/neurorank', note: '1 post per day — own community only' },
      { id: 'post_discord', label: 'Discord messages sent to 2-3 servers', note: 'Run: npm run post:discord' },
    ],
  },
  {
    group: 'Video Pipeline',
    color: '#a855f7',
    items: [
      { id: 'vid_clips', label: 'Game clips refreshed', note: 'npm run clips' },
      { id: 'vid_record', label: 'Site screenshots recorded', note: 'npm run record' },
      { id: 'vid_render', label: 'Videos rendered', note: 'npm run render:force' },
      { id: 'vid_tiktok', label: 'Videos uploaded to TikTok' },
      { id: 'vid_shorts', label: 'Videos uploaded to YouTube Shorts' },
      { id: 'vid_reels', label: 'Videos uploaded to Instagram Reels' },
    ],
  },
  {
    group: 'Weekly (Fridays)',
    color: '#ef4444',
    weekly: true,
    items: [
      { id: 'weekly_newsletter', label: 'Newsletter generated & sent', note: 'npm run daily -- --platform=newsletter' },
      { id: 'weekly_youtube', label: 'YouTube long-form script reviewed', note: 'npm run daily -- --platform=youtube' },
      { id: 'weekly_kpis', label: 'Growth tracker updated with this week\'s numbers' },
    ],
  },
];

function DailyChecklist() {
  const todayKey = new Date().toISOString().split('T')[0];
  const weekKey = `week_${getWeekNumber()}`;

  const [checks, setChecks] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('nr_checklist') || '{}');
      // daily items reset if date doesn't match, weekly items reset if week doesn't match
      return stored;
    } catch { return {}; }
  });

  function getWeekNumber() {
    const d = new Date();
    const jan1 = new Date(d.getFullYear(), 0, 1);
    return Math.ceil(((d - jan1) / 86400000 + jan1.getDay() + 1) / 7);
  }

  function toggle(id, weekly) {
    const key = weekly ? `${weekKey}_${id}` : `${todayKey}_${id}`;
    const next = { ...checks, [key]: !checks[key] };
    setChecks(next);
    localStorage.setItem('nr_checklist', JSON.stringify(next));
  }

  function isChecked(id, weekly) {
    const key = weekly ? `${weekKey}_${id}` : `${todayKey}_${id}`;
    return !!checks[key];
  }

  const allItems = CHECKLIST.flatMap(g => g.items);
  const doneCount = allItems.filter(i => isChecked(i.id, CHECKLIST.find(g => g.items.includes(i))?.weekly)).length;
  const pct = Math.round((doneCount / allItems.length) * 100);

  return (
    <>
      {/* Progress bar */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div style={{ fontSize: 13, color: '#aaa' }}>
            {doneCount}/{allItems.length} tasks complete
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: pct === 100 ? '#10b981' : '#00d4ff', fontFamily: 'monospace' }}>
            {pct}%
          </div>
        </div>
        <div style={{ height: 6, background: '#1a1a1a', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 3,
            background: pct === 100 ? '#10b981' : '#00d4ff',
            width: `${pct}%`, transition: 'width 0.3s ease',
          }} />
        </div>
        <div style={{ fontSize: 11, color: '#444', marginTop: 6 }}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          {' · '}Daily items reset at midnight · Weekly items reset Monday
        </div>
      </div>

      {CHECKLIST.map(group => {
        const groupDone = group.items.filter(i => isChecked(i.id, group.weekly)).length;
        return (
          <Section key={group.group} title={`${group.group} — ${groupDone}/${group.items.length}`}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {group.items.map(item => {
                const checked = isChecked(item.id, group.weekly);
                return (
                  <div
                    key={item.id}
                    onClick={() => toggle(item.id, group.weekly)}
                    style={{
                      background: checked ? `${group.color}11` : '#0d1117',
                      border: `1px solid ${checked ? group.color + '44' : '#1a1a1a'}`,
                      borderRadius: 8, padding: '12px 16px',
                      display: 'flex', alignItems: 'center', gap: 12,
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                  >
                    {/* Checkbox */}
                    <div style={{
                      width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                      border: `2px solid ${checked ? group.color : '#333'}`,
                      background: checked ? group.color : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.15s',
                    }}>
                      {checked && <span style={{ color: '#060a0f', fontSize: 11, fontWeight: 900, lineHeight: 1 }}>✓</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: 13, color: checked ? '#555' : '#ccc',
                        textDecoration: checked ? 'line-through' : 'none',
                        transition: 'all 0.15s',
                      }}>
                        {item.label}
                      </div>
                      {item.note && (
                        <div style={{ fontSize: 11, color: '#444', marginTop: 2 }}>{item.note}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        );
      })}
    </>
  );
}

// ── Growth Tracker ────────────────────────────────────────────────────────────

const KPI_TARGETS = [
  { label: 'Total Profiles Completed',    key: 'completedSessions', p1: 500,   p2: 2500,  p3: 10000, color: '#00d4ff' },
  { label: 'New Profiles This Week',       key: 'thisWeek',          p1: 60,    p2: 200,   p3: 600,   color: '#10b981' },
  { label: 'Weekly Active Users',          key: null,                 p1: 100,   p2: 400,   p3: 1500,  color: '#a855f7' },
  { label: 'Total Registered Users',       key: 'totalUsers',         p1: 200,   p2: 1000,  p3: 5000,  color: '#00d4ff' },
  { label: 'Discord Members',              key: 'discord',            p1: 200,   p2: 600,   p3: 1000,  color: '#5865f2', manual: true },
  { label: 'TikTok Followers',             key: 'tiktok',             p1: 500,   p2: 3000,  p3: 10000, color: '#ff0050', manual: true },
  { label: 'Twitter/X Followers',          key: 'twitter',            p1: 300,   p2: 2000,  p3: 5000,  color: '#1da1f2', manual: true },
  { label: 'Scout Pro Subscribers',        key: 'scout_pro',          p1: 5,     p2: 50,    p3: 250,   color: '#f59e0b', manual: true },
  { label: 'Monthly Revenue ($)',          key: 'revenue',            p1: 0,     p2: 500,   p3: 5000,  color: '#10b981', manual: true },
  { label: 'Creator Integrations Live',    key: 'creators',           p1: 5,     p2: 30,    p3: 50,    color: '#a855f7', manual: true },
  { label: 'Press Mentions (cumulative)',  key: 'press',              p1: 0,     p2: 5,     p3: 15,    color: '#f59e0b', manual: true },
];

const CHANNELS = ['Reddit', 'TikTok', 'Twitter/X', 'Discord', 'Product Hunt', 'Influencer/Creator', 'Direct/SEO'];

function GrowthTracker({ stats }) {
  const p = stats?.platform || {};
  const u = stats?.users || {};

  const [manual, setManual] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nr_growth') || '{}'); }
    catch { return {}; }
  });

  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState('');

  function saveManual(key, val) {
    const next = { ...manual, [key]: Number(val) || 0 };
    setManual(next);
    localStorage.setItem('nr_growth', JSON.stringify(next));
    setEditing(null);
  }

  function getLive(kpi) {
    if (kpi.manual) return manual[kpi.key] ?? 0;
    if (kpi.key === 'completedSessions') return p.completedSessions ?? 0;
    if (kpi.key === 'thisWeek') return p.thisWeek ?? 0;
    if (kpi.key === 'totalUsers') return u.total ?? 0;
    return 0;
  }

  function getPhase(val, p1, p2, p3) {
    if (val >= p3) return 3;
    if (val >= p2) return 2;
    if (val >= p1) return 1;
    return 0;
  }

  function phasePct(val, p1, p2) {
    if (val >= p2) return 100;
    if (val <= 0) return 0;
    return Math.min(Math.round((val / p1) * 100), 100);
  }

  const phase1Complete = KPI_TARGETS.filter(k => getLive(k) >= k.p1).length;

  return (
    <>
      {/* Phase summary */}
      <Section title="Growth Phase Progress">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
          {[
            { label: 'Phase 1 — Seed', pct: Math.round((phase1Complete / KPI_TARGETS.length) * 100), color: '#00d4ff', target: 'All KPIs hit P1' },
            { label: 'Phase 2 — Grow', pct: Math.round((KPI_TARGETS.filter(k => getLive(k) >= k.p2).length / KPI_TARGETS.length) * 100), color: '#10b981', target: 'All KPIs hit P2' },
            { label: 'Phase 3 — Scale', pct: Math.round((KPI_TARGETS.filter(k => getLive(k) >= k.p3).length / KPI_TARGETS.length) * 100), color: '#f59e0b', target: 'All KPIs hit P3' },
          ].map(ph => (
            <div key={ph.label} style={{
              background: '#0d1117', border: `1px solid ${ph.color}33`,
              borderRadius: 10, padding: '16px 20px', minWidth: 180, flex: 1,
            }}>
              <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>{ph.label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: ph.color, fontFamily: 'monospace' }}>{ph.pct}%</div>
              <div style={{ height: 4, background: '#1a1a1a', borderRadius: 2, marginTop: 8, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: ph.color, width: `${ph.pct}%`, borderRadius: 2, transition: 'width 0.3s' }} />
              </div>
              <div style={{ fontSize: 10, color: '#444', marginTop: 6 }}>{ph.target}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, color: '#444' }}>
          Manual fields (social followers, revenue) are stored locally — update them weekly. Live fields pull from the database automatically.
        </div>
      </Section>

      {/* KPI table */}
      <Section title="KPI Targets (from Growth Tracker)">
        <div style={{ background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1a1a1a' }}>
                {['Metric', 'Current', 'P1 Target', 'P2 Target', 'P3 Target', 'Progress'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', color: '#555', fontWeight: 600, textAlign: h === 'Metric' ? 'left' : 'center' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {KPI_TARGETS.map((kpi, i) => {
                const live = getLive(kpi);
                const phase = getPhase(live, kpi.p1, kpi.p2, kpi.p3);
                const pct = phasePct(live, kpi.p1, kpi.p2);
                const isEditing = editing === kpi.key;
                return (
                  <tr key={kpi.label} style={{ borderBottom: '1px solid #111', background: i % 2 === 0 ? 'transparent' : '#0a0e14' }}>
                    <td style={{ padding: '10px 14px', color: '#ccc' }}>
                      {kpi.label}
                      {kpi.manual && <span style={{ fontSize: 10, color: '#555', marginLeft: 6 }}>manual</span>}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      {kpi.manual ? (
                        isEditing ? (
                          <input
                            autoFocus
                            defaultValue={live}
                            onBlur={e => saveManual(kpi.key, e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveManual(kpi.key, e.target.value); if (e.key === 'Escape') setEditing(null); }}
                            style={{
                              width: 70, background: '#1a2a3a', border: '1px solid #00d4ff',
                              color: '#fff', borderRadius: 4, padding: '3px 8px',
                              fontSize: 12, textAlign: 'center', outline: 'none',
                            }}
                          />
                        ) : (
                          <span
                            onClick={() => { setEditing(kpi.key); setEditVal(String(live)); }}
                            style={{ color: kpi.color, fontWeight: 700, fontFamily: 'monospace', cursor: 'text', borderBottom: '1px dashed #333' }}
                            title="Click to edit"
                          >
                            {live.toLocaleString()}
                          </span>
                        )
                      ) : (
                        <span style={{ color: kpi.color, fontWeight: 700, fontFamily: 'monospace' }}>
                          {live.toLocaleString()}
                        </span>
                      )}
                    </td>
                    {[kpi.p1, kpi.p2, kpi.p3].map((t, ti) => (
                      <td key={ti} style={{ padding: '10px 14px', textAlign: 'center', color: ti === 0 ? '#00d4ff' : ti === 1 ? '#10b981' : '#f59e0b', fontFamily: 'monospace' }}>
                        {typeof t === 'number' ? t.toLocaleString() : t}
                      </td>
                    ))}
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 5, background: '#1a1a1a', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', borderRadius: 3,
                            background: phase >= 1 ? kpi.color : '#333',
                            width: `${pct}%`, transition: 'width 0.3s',
                          }} />
                        </div>
                        <span style={{ fontSize: 10, color: phase >= 1 ? kpi.color : '#444', width: 28, textAlign: 'right', fontFamily: 'monospace' }}>
                          {phase >= 1 ? `P${phase}` : `${pct}%`}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Channel health */}
      <Section title="Channel Health — Manual Tracking">
        <div style={{ background: '#0d1117', border: '1px solid #1a1a1a', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1a1a1a' }}>
                {['Channel', 'Visits', 'Sign-ups', 'Conv. %', 'Status'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', color: '#555', fontWeight: 600, textAlign: h === 'Channel' ? 'left' : 'center' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CHANNELS.map((ch, i) => {
                const visits = manual[`ch_visits_${ch}`] ?? 0;
                const signups = manual[`ch_signups_${ch}`] ?? 0;
                const conv = visits > 0 ? ((signups / visits) * 100).toFixed(1) : '—';
                return (
                  <tr key={ch} style={{ borderBottom: '1px solid #111', background: i % 2 === 0 ? 'transparent' : '#0a0e14' }}>
                    <td style={{ padding: '10px 14px', color: '#ccc', fontWeight: 600 }}>{ch}</td>
                    {['visits', 'signups'].map(field => {
                      const mkey = `ch_${field}_${ch}`;
                      return (
                        <td key={field} style={{ padding: '10px 14px', textAlign: 'center' }}>
                          {editing === mkey ? (
                            <input
                              autoFocus
                              defaultValue={manual[mkey] ?? 0}
                              onBlur={e => saveManual(mkey, e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') saveManual(mkey, e.target.value); if (e.key === 'Escape') setEditing(null); }}
                              style={{
                                width: 60, background: '#1a2a3a', border: '1px solid #00d4ff',
                                color: '#fff', borderRadius: 4, padding: '3px 8px',
                                fontSize: 12, textAlign: 'center', outline: 'none',
                              }}
                            />
                          ) : (
                            <span
                              onClick={() => setEditing(mkey)}
                              style={{ color: '#aaa', cursor: 'text', borderBottom: '1px dashed #333', fontFamily: 'monospace' }}
                            >
                              {(manual[mkey] ?? 0).toLocaleString()}
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td style={{ padding: '10px 14px', textAlign: 'center', color: '#f59e0b', fontFamily: 'monospace' }}>{conv}{conv !== '—' ? '%' : ''}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, background: '#10b98122', color: '#10b981', border: '1px solid #10b98133' }}>Active</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11, color: '#444', marginTop: 8 }}>Click any number to edit. Updates saved locally.</div>
      </Section>
    </>
  );
}

